/**
 * Cycles in Harmony Style Card - Centralized Visual Tokens
 * 
 * PROPRIETARY NOTICE: THE ONESCENT DIAL
 * © Optimal Mastery LLC and the Onesce family of applied wellness brands.
 * 
 * PURPOSE:
 * Single source of truth for all visual styling tokens.
 * Used across UI components AND exports (PDF, PNG) for consistency.
 * 
 * USAGE:
 * import { COLORS, FONTS, PDF_STYLES, BRAND_LINKS } from '../utils/styleCard';
 */

// =============================================================================
// BRAND LINKS & ENTITY META (Source of Truth)
// =============================================================================

export const BRAND_LINKS = {
  // Official CiH governing entity
  optimalMastery: {
    name: 'Optimal Mastery LLC',
    url: 'https://optimalmastery.life',
    description: 'Official CiH governing entity',
    qualifiers: ['Optimal Mastery', 'Optimal Mastery LLC', 'governing entity', 'CiH entity']
  },
  
  // Official creator/owner (all lowercase, italicized)
  mayryanna: {
    name: 'mayryanna',
    url: 'https://mayryanna.link',
    description: 'Official creator and owner',
    qualifiers: ['mayryanna', 'creator', 'owner', 'DBA'],
    displayStyle: 'italic lowercase' // Special styling requirement
  },
  
  // Official CiH resources publisher
  onescePress: {
    name: 'Onesce Press',
    url: 'https://onescepress.com',
    description: 'Official CiH resources',
    qualifiers: ['Onesce Press', 'resources', 'CiH resources', 'press']
  },
  
  // Official free online periodical
  onescence: {
    name: 'Onescence',
    url: 'https://onescence.com',
    description: 'Official free online periodical',
    qualifiers: ['Onescence', 'periodical', 'online periodical', 'our periodical']
  }
};

// Helper to get brand link by qualifier
export const getBrandLink = (text) => {
  const lowerText = text.toLowerCase();
  for (const [key, brand] of Object.entries(BRAND_LINKS)) {
    if (brand.qualifiers.some(q => lowerText.includes(q.toLowerCase()))) {
      return brand;
    }
  }
  return null;
};

// Helper to render brand name with correct styling
export const formatBrandName = (brandKey) => {
  const brand = BRAND_LINKS[brandKey];
  if (!brand) return null;
  
  if (brand.displayStyle === 'italic lowercase') {
    return { text: brand.name.toLowerCase(), style: 'italic' };
  }
  return { text: brand.name, style: 'normal' };
};

// =============================================================================
// COLOR PALETTE
// =============================================================================

export const COLORS = {
  // Primary Brand Colors
  brass: '#C5A059',
  gold: '#FFD700',
  text: '#E6C288',
  muted: '#94A3B8',
  
  // Dark Background Scale
  dark: {
    bg: '#0B0F19',
    darker: '#050814',
    deepest: '#020204'
  },
  
  // Phase Colors (CiH Core)
  phases: {
    NOURISH: '#4A90E2',
    ACTIVATE: '#7ED321',
    FULFILL: '#F5A623',
    INTEGRATE: '#9B59B6'
  },
  
  // Accent Colors
  accents: {
    cyan: '#00F0FF',
    purple: '#9370DB',
    lavender: '#DDA0DD',
    green: '#7ED321',
    orange: '#F59E0B',
    blue: '#4682B4',
    red: '#FF6B6B'
  },
  
  // Status Colors
  status: {
    applying: '#7ED321',
    exact: '#FFD700',
    separating: '#94A3B8',
    retrograde: '#FF6B6B',
    station: '#FFD700'
  },
  
  // Regional Season Colors - Theme-aware
  seasons: {
    // Standard temperate seasons
    Spring: '#90EE90',
    Summer: '#FFD700',
    Autumn: '#CD853F',
    Winter: '#87CEEB',
    
    // Equatorial/Tropical seasons
    Wet: '#4682B4',
    Dry: '#DAA520',
    'Wet (Primary)': '#2E86AB',
    'Wet (Secondary)': '#5DADE2',
    'Dry (Primary)': '#D4A017',
    'Dry (Secondary)': '#E6BE44',
    
    // Polar seasons - nuanced states
    'Polar Day': '#FFD700',
    'Polar Day (Peak)': '#FFD700',
    'Polar Day (Rising)': '#FFAA33',
    'Polar Day (Emerging)': '#FF8844',
    'Polar Day (Fading)': '#FFB347',
    'Polar Night': '#4169E1',
    'Polar Night (Deep)': '#2E3B8C',
    'Polar Night (Approaching)': '#5C6BC0',
    'Polar Twilight': '#6A5ACD',
    'Polar Twilight (Emerging)': '#9370DB',
    'Polar Twilight (Fading)': '#7B68EE',
    'Midnight Sun': '#FFB347'
  },
  
  // High contrast versions
  seasonsHighContrast: {
    Spring: '#00FF00',
    Summer: '#FFFF00',
    Autumn: '#FF8C00',
    Winter: '#00BFFF',
    Wet: '#00FFFF',
    Dry: '#FFD700',
    'Polar Day': '#FFFF00',
    'Polar Day (Peak)': '#FFFF00',
    'Polar Day (Rising)': '#FFCC00',
    'Polar Day (Emerging)': '#FF9900',
    'Polar Day (Fading)': '#FFAA00',
    'Polar Night': '#0000FF',
    'Polar Night (Deep)': '#000080',
    'Polar Night (Approaching)': '#4444FF',
    'Polar Twilight': '#9900FF',
    'Polar Twilight (Emerging)': '#CC66FF',
    'Polar Twilight (Fading)': '#AA44FF'
  }
};

// =============================================================================
// REGIONAL SEASON COLORS - Theme-aware getter function
// =============================================================================
// Returns season colors based on current theme (standard, light, high-contrast)

export const getSeasonColor = (seasonName, isHighContrast = false) => {
  if (isHighContrast) {
    return COLORS.seasonsHighContrast[seasonName] || COLORS.seasons[seasonName] || '#C5A059';
  }
  return COLORS.seasons[seasonName] || '#C5A059';
};

// =============================================================================
// THEME VARIANTS - Dark/Light Mode Support
// =============================================================================

export const THEME_VARIANTS = {
  // Theme configuration
  config: {
    default: 'dark',
    storageKey: 'tod_theme_preference',
    transitionDuration: '300ms',
    availableThemes: ['dark', 'light', 'high-contrast']
  },
  
  // Dark Theme (Default) - The original CiH aesthetic
  dark: {
    id: 'dark',
    name: 'Celestial Dark',
    description: 'The original cosmic aesthetic',
    
    // Core colors
    colors: {
      // Backgrounds
      bgPrimary: '#0B0F19',
      bgSecondary: '#050814',
      bgTertiary: '#020204',
      bgOverlay: '#050814',  // OPAQUE for readability - was rgba(5, 8, 20, 0.95)
      bgCard: 'rgba(11, 15, 25, 0.7)',
      bgInput: '#050814',
      
      // Text
      textPrimary: '#E6C288',
      textSecondary: '#94A3B8',
      textMuted: '#64748B',
      textInverse: '#0B0F19',
      
      // Brand
      brass: '#C5A059',
      gold: '#FFD700',
      accent: '#00F0FF',
      
      // Accent colors
      accentPurple: '#9370DB',
      accentCyan: '#00F0FF',
      accentBlue: '#4A90D9',
      accentOrange: '#F59E0B',
      lavender: '#DDA0DD',
      
      // Borders
      borderPrimary: 'rgba(197, 160, 89, 0.4)',
      borderSecondary: 'rgba(197, 160, 89, 0.2)',
      borderFocus: '#C5A059',
      
      // Status
      success: '#7ED321',
      warning: '#F5A623',
      error: '#FF6B6B',
      info: '#4A90E2',
      
      // Shadows
      shadowColor: 'rgba(0, 0, 0, 0.5)',
      glowColor: 'rgba(197, 160, 89, 0.3)'
    },
    
    // Phase colors (unchanged)
    phases: {
      NOURISH: '#4A90E2',
      ACTIVATE: '#7ED321',
      FULFILL: '#F5A623',
      INTEGRATE: '#9B59B6'
    },
    
    // Accessibility settings
    a11y: {
      focusOutlineWidth: '2px',
      focusOutlineOffset: '2px',
      minBorderWidth: '1px'
    }
  },
  
  // Light Theme - Warm, scholarly aesthetic
  light: {
    id: 'light',
    name: 'Parchment Light',
    description: 'A warm, scholarly aesthetic',
    
    // Core colors
    colors: {
      // Backgrounds
      bgPrimary: '#FAF7F2',
      bgSecondary: '#F5F0E8',
      bgTertiary: '#EDE6DA',
      bgOverlay: '#FAF7F2',  // OPAQUE for readability - was rgba(250, 247, 242, 0.98)
      bgCard: 'rgba(245, 240, 232, 0.9)',
      bgInput: '#FFFFFF',
      
      // Text
      textPrimary: '#3D3425',
      textSecondary: '#6B5D4D',
      textMuted: '#9B8B7A',
      textInverse: '#FAF7F2',
      
      // Brand (warmer brass for light mode)
      brass: '#8B6914',
      gold: '#B8860B',
      accent: '#1E6B7B',
      
      // Accent colors (adjusted for light mode visibility)
      accentPurple: '#6B4BA3',
      accentCyan: '#0E7B8B',
      accentBlue: '#2E6B99',
      accentOrange: '#C77B10',
      lavender: '#8B6BA3',
      
      // Borders
      borderPrimary: 'rgba(139, 105, 20, 0.3)',
      borderSecondary: 'rgba(139, 105, 20, 0.15)',
      borderFocus: '#8B6914',
      
      // Status (slightly muted for light mode)
      success: '#4A8B2C',
      warning: '#C77B10',
      error: '#C94A4A',
      info: '#2E6B99',
      
      // Shadows
      shadowColor: 'rgba(61, 52, 37, 0.15)',
      glowColor: 'rgba(139, 105, 20, 0.2)'
    },
    
    // Phase colors (adjusted for light mode visibility)
    phases: {
      NOURISH: '#2E6B99',
      ACTIVATE: '#4A8B2C',
      FULFILL: '#C77B10',
      INTEGRATE: '#7B5BA3'
    },
    
    // Accessibility settings
    a11y: {
      focusOutlineWidth: '2px',
      focusOutlineOffset: '2px',
      minBorderWidth: '1px'
    }
  },
  
  // High Contrast Theme - Maximum accessibility
  'high-contrast': {
    id: 'high-contrast',
    name: 'High Contrast',
    description: 'Maximum visibility for accessibility',
    
    // Core colors - Pure blacks and whites with saturated accents
    colors: {
      // Backgrounds - Pure black
      bgPrimary: '#000000',
      bgSecondary: '#0A0A0A',
      bgTertiary: '#141414',
      bgOverlay: '#000000',
      bgCard: '#0A0A0A',
      bgInput: '#000000',
      
      // Text - Pure white for maximum contrast
      textPrimary: '#FFFFFF',
      textSecondary: '#E0E0E0',
      textMuted: '#B0B0B0',
      textInverse: '#000000',
      
      // Brand - Bright gold for visibility
      brass: '#FFD700',
      gold: '#FFEA00',
      accent: '#00FFFF',
      
      // Accent colors - High contrast versions
      accentPurple: '#FF00FF',
      accentCyan: '#00FFFF',
      accentBlue: '#00BFFF',
      accentOrange: '#FFA500',
      lavender: '#FF80FF',
      
      // Borders - Thick, visible
      borderPrimary: '#FFD700',
      borderSecondary: '#808080',
      borderFocus: '#00FFFF',
      
      // Status - Saturated colors
      success: '#00FF00',
      warning: '#FFFF00',
      error: '#FF0000',
      info: '#00BFFF',
      
      // Shadows - None for clarity
      shadowColor: 'transparent',
      glowColor: 'transparent'
    },
    
    // Phase colors - High saturation
    phases: {
      NOURISH: '#00BFFF',
      ACTIVATE: '#00FF00',
      FULFILL: '#FFFF00',
      INTEGRATE: '#FF00FF'
    },
    
    // Accessibility settings - Enhanced
    a11y: {
      focusOutlineWidth: '3px',
      focusOutlineOffset: '3px',
      minBorderWidth: '2px',
      noTransparency: true,
      noBlur: true,
      boldText: true
    }
  }
};

// =============================================================================
// META STANDARDS - System-Wide Design Requirements
// =============================================================================
// These standards MUST be followed by all components, panels, overlays, and tools
export const META_STANDARDS = {
  // OPAQUE BACKGROUNDS - Critical for readability
  backgrounds: {
    // All panels, overlays, and modals MUST use opaque backgrounds
    requirement: 'OPAQUE',
    // Use these patterns for all panel backgrounds:
    panelBgDark: '#050814',      // Primary panel bg (dark)
    panelBgLight: '#FAF7F2',     // Primary panel bg (light)
    overlayBgDark: '#0B0F19',    // Overlay bg (dark)
    overlayBgLight: '#FFFEF9',   // Overlay bg (light)
    // NO semi-transparent backgrounds on panels (inhibits readability)
    // backdrop-blur is allowed but underlying bg must be opaque (95%+ opacity minimum)
    minOpacity: 0.95,
    // Helper to get opaque panel background
    getPanelBg: (isDark) => isDark ? '#050814' : '#FAF7F2',
    getOverlayBg: (isDark) => isDark ? '#0B0F19' : '#FFFEF9',
    getCardBg: (isDark) => isDark ? 'rgba(11, 15, 25, 0.95)' : 'rgba(250, 247, 242, 0.95)'
  },
  
  // PANEL SIZING STANDARDS - Critical for responsive consistency
  panelSizing: {
    requirement: 'CONSISTENT_RESPONSIVE_SIZING',
    
    // Overlay panels (side panels, glossary, profile, etc.)
    overlay: {
      mobile: {
        width: 'calc(100vw - 16px)',
        maxWidth: '400px',
        maxHeight: 'calc(100vh - 32px)',
        margin: '8px'
      },
      tablet: {
        width: '420px',
        maxWidth: '90vw',
        maxHeight: 'calc(100vh - 40px)'
      },
      desktop: {
        width: '440px',
        maxWidth: '40vw',
        maxHeight: 'calc(100vh - 48px)'
      },
      // Classes for responsive sizing
      classes: 'w-[calc(100vw-16px)] sm:w-[420px] lg:w-[440px] max-w-[90vw] lg:max-w-[40vw]'
    },
    
    // Compact panels (quick status, storage preview)
    compact: {
      mobile: {
        width: 'calc(100vw - 24px)',
        maxWidth: '320px'
      },
      tablet: {
        width: '340px',
        maxWidth: '85vw'
      },
      desktop: {
        width: '360px',
        maxWidth: '35vw'
      },
      // Classes for responsive sizing
      classes: 'w-[calc(100vw-24px)] sm:w-[340px] lg:w-[360px] max-w-[320px] sm:max-w-[85vw] lg:max-w-[35vw]'
    },
    
    // Full modals (date picker, export, etc.)
    modal: {
      mobile: {
        width: 'calc(100vw - 24px)',
        maxWidth: '500px',
        maxHeight: 'calc(100vh - 48px)'
      },
      tablet: {
        width: '520px',
        maxWidth: '90vw',
        maxHeight: 'calc(100vh - 64px)'
      },
      desktop: {
        width: '560px',
        maxWidth: '60vw',
        maxHeight: 'calc(100vh - 80px)'
      },
      // Classes for responsive sizing
      classes: 'w-[calc(100vw-24px)] sm:w-[520px] lg:w-[560px] max-w-[90vw] lg:max-w-[60vw]'
    }
  },
  
  // THEME AWARENESS - All components must support light/dark themes
  theme: {
    requirement: 'ALL_COMPONENTS_THEME_AWARE',
    // Every component MUST import and use useTheme
    implementation: `
      import { useTheme } from '../contexts/ThemeContext';
      const { isDark } = useTheme();
    `,
    // Use THEME_VARIANTS or CSS custom properties for colors
    // NO hardcoded color values (except in meta sources)
  },
  
  // PANEL ANIMATIONS - Prevent double animations with Suspense
  animations: {
    // Lazy-loaded panels should NOT animate if skeleton already animated
    // Use layout animations, not entrance animations, for Suspense children
    suspenseStrategy: 'SKELETON_ONLY_ANIMATES',
    // Standard panel entrance: slide from right
    panelEntrance: { initial: { x: '100%' }, animate: { x: 0 } },
    // Lazy panels: no entrance animation (skeleton handles it)
    lazyPanelEntrance: { initial: { opacity: 1 }, animate: { opacity: 1 } }
  },
  
  // CIH SYNTHESIS - All synthesis must use meta templates
  synthesis: {
    requirement: 'USE_CIH_SYNTHESIS_FUNCTIONS',
    // All synthesis text MUST come from cihSynthesis.js or cihCore.js
    // NO inline synthesis generation
    functions: [
      'generateTemporalLayerSynthesis',
      'generateModulationSynthesis',
      'generateAspectSynthesis',
      'generateElementSynthesis',
      'generateStelliumSynthesis',
      'generatePlanetSynthesis',
      'generateEclipseSynthesis',
      'generateNodeAxisSynthesis',
      'generateSpaceWeatherSynthesis',
      'generateComparativeSynthesis'
    ]
  },
  
  // PHASE COLORS - Single source of truth
  phases: {
    requirement: 'USE_PHASE_COLORS_FROM_CIH_CONSTANTS',
    source: 'cihConstants.js or cihCore.js',
    // NEVER define phase colors inline
  },
  
  // BREADCRUMB NAVIGATION - Standard for all panels and overlays
  breadcrumbs: {
    requirement: 'ALL_PANELS_SUPPORT_BREADCRUMB_NAVIGATION',
    implementation: `
      // Every panel should track navigation path and provide back/edit options
      // Props: onNavigateBack, onEdit, navigationPath[]
      // UI: Back arrow with context label, Edit button for OA parameters
    `,
    // Breadcrumb pattern for nested panels
    pattern: {
      showBackButton: true,        // Always show back/close option
      showEditButton: true,        // Show edit for OA configurations
      showNavigationPath: true,    // Show where user came from
      maxPathDepth: 3              // Truncate after 3 levels
    },
    // Standard breadcrumb component props
    props: ['onNavigateBack', 'onEdit', 'navigationPath', 'currentPanelLabel']
  }
};

// =============================================================================
// BREADCRUMB NAVIGATION HELPERS
// =============================================================================
export const getBreadcrumbStyle = (isDark) => ({
  container: `flex items-center gap-2 text-xs`,
  backButton: `flex items-center gap-1 px-2 py-1 rounded hover:bg-tod-brass/10 transition-colors`,
  editButton: `flex items-center gap-1 px-2 py-1 rounded bg-tod-brass/10 hover:bg-tod-brass/20 text-tod-brass transition-colors`,
  pathSeparator: `text-tod-text-muted`,
  pathItem: `text-tod-text-secondary hover:text-tod-brass cursor-pointer`,
  currentItem: `text-tod-text-primary font-medium`
});

// =============================================================================
// Theme-aware helper functions for META_STANDARDS compliance
// =============================================================================
export const getThemeAwarePanelStyles = (isDark) => ({
  backgroundColor: META_STANDARDS.backgrounds.getPanelBg(isDark),
  overlayBg: META_STANDARDS.backgrounds.getOverlayBg(isDark),
  cardBg: META_STANDARDS.backgrounds.getCardBg(isDark),
  textPrimary: isDark ? THEME_VARIANTS.dark.colors.textPrimary : THEME_VARIANTS.light.colors.textPrimary,
  textSecondary: isDark ? THEME_VARIANTS.dark.colors.textSecondary : THEME_VARIANTS.light.colors.textSecondary,
  borderColor: isDark ? THEME_VARIANTS.dark.colors.borderPrimary : THEME_VARIANTS.light.colors.borderPrimary,
  brass: isDark ? THEME_VARIANTS.dark.colors.brass : THEME_VARIANTS.light.colors.brass,
  gold: isDark ? THEME_VARIANTS.dark.colors.gold : THEME_VARIANTS.light.colors.gold
});

// CSS Custom Properties Generator
export const generateThemeCSS = (themeId = 'dark') => {
  const theme = THEME_VARIANTS[themeId] || THEME_VARIANTS.dark;
  const { colors, phases } = theme;
  
  return `
    :root {
      /* Background */
      --tod-bg-primary: ${colors.bgPrimary};
      --tod-bg-secondary: ${colors.bgSecondary};
      --tod-bg-tertiary: ${colors.bgTertiary};
      --tod-bg-overlay: ${colors.bgOverlay};
      --tod-bg-card: ${colors.bgCard};
      --tod-bg-input: ${colors.bgInput};
      --tod-bg-light: ${colors.bgInput};
      
      /* Text */
      --tod-text-primary: ${colors.textPrimary};
      --tod-text-secondary: ${colors.textSecondary};
      --tod-text-muted: ${colors.textMuted};
      --tod-text-inverse: ${colors.textInverse};
      
      /* Brand */
      --tod-brass: ${colors.brass};
      --tod-gold: ${colors.gold};
      --tod-accent: ${colors.accent};
      
      /* Accent Colors */
      --tod-accent-purple: ${colors.accentPurple};
      --tod-accent-cyan: ${colors.accentCyan};
      --tod-accent-blue: ${colors.accentBlue};
      --tod-accent-blue-light: ${colors.accentBlue}80;
      --tod-accent-orange: ${colors.accentOrange};
      --tod-accent-pink: ${colors.lavender};
      --tod-lavender: ${colors.lavender};
      
      /* Borders */
      --tod-border-primary: ${colors.borderPrimary};
      --tod-border-secondary: ${colors.borderSecondary};
      --tod-border-focus: ${colors.borderFocus};
      
      /* Status */
      --tod-success: ${colors.success};
      --tod-warning: ${colors.warning};
      --tod-error: ${colors.error};
      --tod-info: ${colors.info};
      
      /* Effects */
      --tod-shadow: ${colors.shadowColor};
      --tod-glow: ${colors.glowColor};
      
      /* Phases */
      --tod-phase-nourish: ${phases.NOURISH};
      --tod-phase-activate: ${phases.ACTIVATE};
      --tod-phase-fulfill: ${phases.FULFILL};
      --tod-phase-integrate: ${phases.INTEGRATE};
      
      /* Transitions */
      --tod-transition: ${THEME_VARIANTS.config.transitionDuration};
    }
  `;
};

// RGB versions for PDF (jsPDF requires RGB arrays)
export const COLORS_RGB = {
  brass: [197, 160, 89],
  gold: [255, 215, 0],
  text: [230, 194, 136],
  muted: [148, 163, 184],
  lightText: [230, 194, 136],
  grayText: [148, 163, 184],
  
  dark: {
    bg: [11, 15, 25],
    darker: [5, 8, 20]
  },
  
  phases: {
    NOURISH: [74, 144, 226],
    ACTIVATE: [126, 211, 33],
    FULFILL: [245, 166, 35],
    INTEGRATE: [147, 112, 219]
  },
  
  accents: {
    cyan: [0, 240, 255],
    purple: [147, 112, 219],
    blue: [70, 130, 180],
    orange: [255, 165, 0]
  }
};

// =============================================================================
// ASTRONOMICAL SYMBOLS - Single Source of Truth
// Used across dial, panels, AND exports for consistency
// =============================================================================

export const SYMBOLS = {
  // Planet symbols with names and colors
  planets: {
    sun: { symbol: '☉', name: 'Sun', color: '#FFD700' },
    moon: { symbol: '☽', name: 'Moon', color: '#E8E8E8' },
    mercury: { symbol: '☿', name: 'Mercury', color: '#A0522D' },
    venus: { symbol: '♀', name: 'Venus', color: '#DEB887' },
    mars: { symbol: '♂', name: 'Mars', color: '#CD5C5C' },
    jupiter: { symbol: '♃', name: 'Jupiter', color: '#DAA520' },
    saturn: { symbol: '♄', name: 'Saturn', color: '#D2B48C' },
    uranus: { symbol: '♅', name: 'Uranus', color: '#87CEEB' },
    neptune: { symbol: '♆', name: 'Neptune', color: '#4169E1' },
    pluto: { symbol: '♇', name: 'Pluto', color: '#8B4513' },
    northNode: { symbol: '☊', name: 'North Node', color: '#9370DB' },
    southNode: { symbol: '☋', name: 'South Node', color: '#9370DB' }
  },
  
  // Aspect symbols
  aspects: {
    conjunction: { symbol: '☌', name: 'Conjunction', abbrev: 'conj' },
    opposition: { symbol: '☍', name: 'Opposition', abbrev: 'opp' },
    trine: { symbol: '△', name: 'Trine', abbrev: 'tri' },
    square: { symbol: '□', name: 'Square', abbrev: 'sqr' },
    sextile: { symbol: '✶', name: 'Sextile', abbrev: 'sxt' }  // Using ✶ (six-pointed star) as ⚹ not in DejaVu Sans
  },
  
  // Zodiac signs
  zodiac: {
    aries: { symbol: '♈', name: 'Aries' },
    taurus: { symbol: '♉', name: 'Taurus' },
    gemini: { symbol: '♊', name: 'Gemini' },
    cancer: { symbol: '♋', name: 'Cancer' },
    leo: { symbol: '♌', name: 'Leo' },
    virgo: { symbol: '♍', name: 'Virgo' },
    libra: { symbol: '♎', name: 'Libra' },
    scorpio: { symbol: '♏', name: 'Scorpio' },
    sagittarius: { symbol: '♐', name: 'Sagittarius' },
    capricorn: { symbol: '♑', name: 'Capricorn' },
    aquarius: { symbol: '♒', name: 'Aquarius' },
    pisces: { symbol: '♓', name: 'Pisces' }
  },
  
  // Status indicators
  status: {
    applying: { symbol: '→', name: 'Applying' },
    exact: { symbol: '●', name: 'Exact' },
    separating: { symbol: '←', name: 'Separating' },
    retrograde: { symbol: '℞', name: 'Retrograde' }
  },
  
  // Miscellaneous
  misc: {
    degree: '°',
    star: '★',
    sparkle: '✧'
  }
};

// Helper functions for symbol access
export const getPlanetSymbol = (planet) => SYMBOLS.planets[planet?.toLowerCase()]?.symbol || '•';
export const getPlanetName = (planet) => SYMBOLS.planets[planet?.toLowerCase()]?.name || formatPlanetName(planet);
export const getAspectSymbol = (aspect) => SYMBOLS.aspects[aspect?.toLowerCase()]?.symbol || '•';
export const getAspectAbbrev = (aspect) => SYMBOLS.aspects[aspect?.toLowerCase()]?.abbrev || aspect?.slice(0,3) || '';
export const getZodiacSymbol = (sign) => SYMBOLS.zodiac[sign?.toLowerCase()]?.symbol || '';
export const getStatusSymbol = (status) => SYMBOLS.status[status?.toLowerCase()]?.symbol || '';

// =============================================================================
// SYNTHESIS GRAVITY WEIGHTS
// =============================================================================
// Based on relative gravitational influence on Earth experience
// These weights determine the synthesis emphasis for each celestial factor
// Higher weight = more influence on final synthesis interpretation

export const GRAVITY_WEIGHTS = {
  // Earth-level influences (highest weight - climate, seasons, sabbats)
  earth: {
    weight: 100,
    influences: ['climate', 'atmospheric', 'season', 'sabbat', 'daylight', 'hemisphere'],
    description: 'Terrestrial conditions form the primary context for all experience'
  },
  
  // Personal planets (significant individual influence)
  moon: {
    weight: 90,
    influences: ['lunar_phase', 'moon_sign', 'emotional_rhythm'],
    description: 'Lunar cycles shape daily emotional and energetic tides'
  },
  venus: {
    weight: 80,
    influences: ['relating', 'harmony', 'values'],
    description: 'Venusian energy governs connection and aesthetic appreciation'
  },
  mercury: {
    weight: 70,
    influences: ['communication', 'perception', 'mental_process'],
    description: 'Mercurial patterns inform thought and exchange'
  },
  
  // Solar influence (zodiac, space weather)
  sun: {
    weight: 60,
    influences: ['zodiac_sign', 'space_weather', 'solar_activity', 'identity'],
    description: 'Solar position defines the zodiacal backdrop and geomagnetic context'
  },
  
  // Social planets (collective/generational influence)
  mars: {
    weight: 50,
    influences: ['action', 'drive', 'assertion'],
    description: 'Martian energy shapes initiative and physical engagement'
  },
  jupiter: {
    weight: 40,
    influences: ['expansion', 'growth', 'opportunity'],
    description: 'Jovian cycles mark periods of expansion and integration'
  },
  saturn: {
    weight: 30,
    influences: ['structure', 'discipline', 'mastery'],
    description: 'Saturnian energy defines boundaries and maturation'
  },
  
  // Outer planets (transpersonal/transformative)
  uranus: {
    weight: 20,
    influences: ['innovation', 'awakening', 'disruption'],
    description: 'Uranian influence marks breakthrough and change'
  },
  neptune: {
    weight: 10,
    influences: ['transcendence', 'imagination', 'dissolution'],
    description: 'Neptunian energy governs subtle, transpersonal dimensions'
  },
  pluto: {
    weight: 1,
    influences: ['transformation', 'depth', 'regeneration'],
    description: 'Plutonian influence operates at the deepest transformative level'
  }
};

// Helper to get gravity weight for a planet
export const getGravityWeight = (planet) => {
  const key = planet?.toLowerCase();
  return GRAVITY_WEIGHTS[key]?.weight || 1;
};

// Calculate weighted synthesis score
export const calculateWeightedScore = (factors) => {
  // factors: { planet: score, ... } or { category: score, ... }
  let totalWeight = 0;
  let weightedSum = 0;
  
  Object.entries(factors).forEach(([key, score]) => {
    const weight = getGravityWeight(key);
    totalWeight += weight;
    weightedSum += score * weight;
  });
  
  return totalWeight > 0 ? weightedSum / totalWeight : 0;
};

// =============================================================================
// CONTEXTUAL ORIENTATION DISPLAY META
// =============================================================================
// Rules for displaying real-time and real-place data in the CO panel

export const CO_DISPLAY_META = {
  // Subheading format: combines date + location
  subheading: {
    format: '{date} • {city}, {country}',
    fallback: '{date} • Set location for place-specific synthesis',
    dateFormat: { month: 'short', day: 'numeric', year: 'numeric' },
    // When to show location
    showLocation: true,
    // Additional context
    suffix: ' • CiH Framework'
  },
  
  // Real-time context badges
  realTimeContext: {
    showPlanetBadges: true,
    showPhaseEmphasis: true,
    showDaylightHours: true
  },
  
  // Place-specific synthesis
  placeContext: {
    showHemisphere: true,
    showClimateZone: true,
    showDaylightRatio: true
  }
};

// =============================================================================
// TYPOGRAPHY
// =============================================================================

export const FONTS = {
  // Primary Fonts with alternatives that match brand aesthetic
  title: 'Cinzel Decorative',
  titleFallback: 'Cinzel Decorative, Cinzel, Trajan Pro, Times New Roman, serif',
  titleAlternatives: ['Cinzel Decorative', 'Cinzel', 'Trajan Pro', 'Playfair Display', 'Cormorant Garamond'],
  
  body: 'Rajdhani',
  bodyFallback: 'Rajdhani, Exo 2, Orbitron, -apple-system, BlinkMacSystemFont, sans-serif',
  bodyAlternatives: ['Rajdhani', 'Exo 2', 'Barlow', 'Inter', 'Roboto'],
  
  mono: 'Space Mono',
  monoFallback: 'Space Mono, JetBrains Mono, Fira Code, Monaco, Consolas, monospace',
  monoAlternatives: ['Space Mono', 'JetBrains Mono', 'Fira Code', 'Source Code Pro'],
  
  // PDF-safe alternatives (system fonts that work in jsPDF)
  pdf: {
    title: 'helvetica',  // jsPDF built-in, style: bold
    body: 'helvetica',   // jsPDF built-in, style: normal
    mono: 'courier'      // jsPDF built-in for data
  },
  
  // Font Weights
  weights: {
    light: 300,
    regular: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
    black: 900
  },
  
  // Font Sizes (rem)
  sizes: {
    xs: '0.65rem',
    sm: '0.75rem',
    base: '0.875rem',
    md: '1rem',
    lg: '1.125rem',
    xl: '1.25rem',
    '2xl': '1.5rem',
    '3xl': '2rem',
    '4xl': '2.5rem'
  }
};

// =============================================================================
// SPACING & LAYOUT
// =============================================================================

export const SPACING = {
  // Base spacing units (px)
  xs: 4,
  sm: 8,
  md: 12,
  lg: 16,
  xl: 24,
  '2xl': 32,
  '3xl': 48,
  
  // Component-specific
  panelPadding: 16,
  cardPadding: 12,
  buttonPadding: { x: 16, y: 8 },
  
  // PDF-specific (mm for jsPDF)
  pdf: {
    margin: 15,
    headerHeight: 50,
    footerHeight: 45,
    sectionGap: 6,
    lineHeight: 3.5
  }
};

// =============================================================================
// CAPITALIZATION & TEXT FORMATTING RULES
// =============================================================================

export const TEXT_FORMATTING = {
  // Capitalization rules by context
  capitalization: {
    // Product/Brand names - exact casing
    brandNames: {
      product: 'THE ONESCENT DIAL',        // Always uppercase
      framework: 'Cycles in Harmony',       // Title case
      frameworkAbbrev: 'CiH',              // Exact casing
      company: 'Optimal Mastery LLC',       // Title case
      spatioTemporal: 'Spacio-Temporal Analysis', // Title case with trademark
    },
    
    // Section headers - ALL CAPS
    headers: 'uppercase',
    headerExamples: [
      'COSMIC CLIMATE',
      'DOMINANT PHASE', 
      'CELESTIAL POSITIONS',
      'ACTIVE ASPECTS',
      'ASPECT DYNAMICS',
      'RETROGRADE STATUS',
      'ORIENTATION NOTE',
      'FIELD FLAGS'
    ],
    
    // Phase names - ALL CAPS
    phases: 'uppercase',
    phaseExamples: ['NOURISH', 'ACTIVATE', 'FULFILL', 'INTEGRATE'],
    
    // Planet names - Title Case
    planets: 'titleCase',
    planetExamples: ['Sun', 'Moon', 'Mercury', 'Venus', 'Mars', 'Jupiter', 'Saturn'],
    
    // Zodiac signs - Title Case  
    zodiac: 'titleCase',
    zodiacExamples: ['Aries', 'Taurus', 'Gemini', 'Cancer', 'Leo', 'Virgo'],
    
    // Status labels - Title Case
    status: 'titleCase',
    statusExamples: ['Applying', 'Separating', 'Exact', 'Retrograde'],
    
    // Body text - Sentence case
    body: 'sentenceCase',
    
    // List items - Sentence case (capitalize first word only)
    listItems: 'sentenceCase',
    listItemExamples: ['Emotional processing', 'Physical energy', 'Creative expression'],
    
    // Themes - Title Case (each word capitalized)
    themes: 'titleCase',
    themeExamples: ['Inner Reflection', 'Outward Expression', 'Structured Growth', 'Fluid Adaptation'],
    
    // Supportive responses - Sentence case
    supportiveResponses: 'sentenceCase',
    supportiveExamples: ['Rest and recuperation', 'Physical activity', 'Creative projects'],
    
    // Hashtags - lowercase
    hashtags: 'lowercase',
    hashtagExamples: ['#onescentdial', '#cyclesinharmony'],
    
    // ===== PROPER NOUNS - ALWAYS CAPITALIZED =====
    // These are celestial bodies and must always be treated as proper nouns
    properNouns: {
      planets: ['Sun', 'Moon', 'Mercury', 'Venus', 'Mars', 'Jupiter', 'Saturn', 'Uranus', 'Neptune', 'Pluto'],
      luminaries: ['Sun', 'Moon'],
      nodes: ['North Node', 'South Node'],
      points: ['Ascendant', 'Midheaven', 'IC', 'Descendant'],
      asteroids: ['Chiron', 'Ceres', 'Pallas', 'Juno', 'Vesta'],
      // Astronomical events - Title Case
      eclipseTypes: ['Total Solar Eclipse', 'Partial Solar Eclipse', 'Annular Solar Eclipse', 'Total Lunar Eclipse', 'Partial Lunar Eclipse', 'Penumbral Lunar Eclipse'],
      moonPhases: ['New Moon', 'Waxing Crescent', 'First Quarter', 'Waxing Gibbous', 'Full Moon', 'Waning Gibbous', 'Last Quarter', 'Waning Crescent'],
      sabbats: ['Imbolc', 'Ostara', 'Beltane', 'Litha', 'Lughnasadh', 'Mabon', 'Samhain', 'Yule'],
      seasons: ['Spring', 'Summer', 'Autumn', 'Winter'],
      zodiacSigns: ['Aries', 'Taurus', 'Gemini', 'Cancer', 'Leo', 'Virgo', 'Libra', 'Scorpio', 'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces']
    },
    
    // First word capitalization rule
    firstWordCapitalization: {
      enabled: true,
      applies: ['sentences', 'listItems', 'bulletPoints', 'synthesisText']
    }
  },
  
  // =============================================================================
  // UI CONTENT PRIORITY RULES - Meta guidance for all features/updates
  // =============================================================================
  contentPriority: {
    // REAL TIME/PLACE DATA EVERYWHERE
    // This is a TOP-LEVEL PRIORITY: Always show current context at top-level
    realTimePlaceData: {
      priority: 'P0',
      rule: 'Always display current time and location context prominently',
      placement: 'Header or top of any panel/overlay',
      format: {
        time: 'Short format (e.g., "Mon, Jan 22, 2026 • 10:30 AM")',
        place: 'City name (e.g., "Los Angeles")'
      },
      icons: ['Clock', 'MapPin']
    },
    
    // CONDENSE UNUSED SPACE - Maximize valuable content
    spaceOptimization: {
      priority: 'P0',
      rule: 'Maximize valuable content, minimize padding and whitespace',
      guidelines: [
        'Use compact spacing (space-y-2 instead of space-y-4) for dense content',
        'Reduce margins between related elements',
        'Never sacrifice real data for decorative spacing',
        'Use responsive text sizes (text-[10px] for dense info)',
        'Prefer inline layouts over stacked when space is limited'
      ]
    },
    
    // GLOSSARY INTEGRATION - All technical terms should link to glossary
    glossaryIntegration: {
      priority: 'P1',
      rule: 'Wrap all technical/astrological terms with GlossaryTerm component',
      terms: [
        'retrograde', 'phase', 'synthesis', 'aspect', 'conjunction', 'opposition',
        'trine', 'square', 'sextile', 'modulation', 'threshold', 'confluence',
        'station_retrograde', 'station_direct', 'natal', 'transit'
      ]
    }
  },
  
  // Font assignments by context
  fontAssignments: {
    // PDF font assignments
    pdf: {
      title: { font: 'CinzelDecorative', style: 'bold', fallback: 'helvetica' },
      subtitle: { font: 'Rajdhani', style: 'normal', fallback: 'helvetica' },
      sectionHeader: { font: 'Rajdhani', style: 'bold', fallback: 'helvetica' },
      body: { font: 'Rajdhani', style: 'normal', fallback: 'helvetica' },
      data: { font: 'Rajdhani', style: 'normal', fallback: 'helvetica' },
      footer: { font: 'Rajdhani', style: 'normal', fallback: 'helvetica' }
    },
    // CSS font assignments (already loaded via Google Fonts)
    css: {
      title: "'Cinzel Decorative', serif",
      body: "'Rajdhani', sans-serif",
      mono: "'Space Mono', monospace"
    }
  }
};

// =============================================================================
// TILE COMPONENTS - Reusable display tiles for context data
// =============================================================================

/**
 * PLACE TILE - Displays location information in a consistent format
 * Used in: LocationController, DateTimePicker, ProfileManager, UniversalInfoPanel
 */
export const PLACE_TILE_CONFIG = {
  // Visual styling
  styles: {
    container: 'p-2 rounded border transition-colors',
    containerActive: 'bg-tod-brass/10 border-tod-brass',
    containerInactive: 'bg-tod-bg-secondary/30 border-tod-brass/20',
    label: 'text-[10px] uppercase tracking-wider mb-1',
    labelActive: 'text-tod-brass',
    labelInactive: 'text-tod-text-secondary',
    cityName: 'text-sm font-medium text-tod-text-primary truncate',
    coordinates: 'text-[10px] text-tod-text-secondary font-mono',
    icon: 'text-tod-brass'
  },
  // Display format
  format: {
    cityDisplay: (city, state, country) => [city, state, country].filter(Boolean).join(', ') || 'Custom Location',
    coordsDisplay: (lat, lon) => `${lat?.toFixed(2)}°${lat >= 0 ? 'N' : 'S'}, ${Math.abs(lon)?.toFixed(2)}°${lon >= 0 ? 'E' : 'W'}`
  },
  // Labels by context
  labels: {
    selected: 'Selected Location',
    current: 'Current Location',
    natal: 'Birth Location',
    custom: 'Custom Coordinates'
  }
};

/**
 * TIME TILE - Displays date/time information in a consistent format
 * Used in: DateTimePicker, LocationController, ProfileManager, UniversalInfoPanel
 */
export const TIME_TILE_CONFIG = {
  // Visual styling
  styles: {
    container: 'p-2 rounded border transition-colors',
    containerActive: 'bg-tod-brass/10 border-tod-brass',
    containerInactive: 'bg-tod-bg-secondary/30 border-tod-brass/20',
    label: 'text-[10px] uppercase tracking-wider mb-1',
    labelActive: 'text-tod-brass',
    labelInactive: 'text-tod-text-secondary',
    dateTime: 'text-sm font-medium text-tod-text-primary',
    subtext: 'text-[10px] text-tod-text-secondary',
    icon: 'text-tod-brass'
  },
  // Display format - uses safe date parsing to avoid timezone issues
  format: {
    dateDisplay: (dateStr) => {
      if (!dateStr) return 'No date';
      // Use safe parsing for date-only strings (YYYY-MM-DD)
      const d = REAL_TIME_DISPLAY.parseDateString(dateStr);
      if (!d || isNaN(d.getTime())) return 'Invalid Date';
      return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
    },
    timeDisplay: (timeStr) => {
      if (!timeStr) return '';
      const [h, m] = timeStr.split(':');
      const hour = parseInt(h);
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const hour12 = hour % 12 || 12;
      return `${hour12}:${m} ${ampm}`;
    },
    fullDisplay: (dateStr, timeStr) => {
      const date = TIME_TILE_CONFIG.format.dateDisplay(dateStr);
      const time = TIME_TILE_CONFIG.format.timeDisplay(timeStr);
      return time ? `${date} • ${time}` : date;
    }
  },
  // Labels by context
  labels: {
    selected: 'Selected Time',
    current: 'Current Time',
    natal: 'Birth Time',
    custom: 'Custom Time'
  }
};

// =============================================================================
// REAL TIME & PLACE DISPLAY STANDARDS (META)
// Ensures consistent formatting across all components
// =============================================================================

/**
 * REAL_TIME_DISPLAY - Standard formatting for time/date everywhere
 * Use these functions for consistent display across the app
 */
export const REAL_TIME_DISPLAY = {
  /**
   * Format complete datetime with year (for main displays)
   * Example: "Thu, Jan 22, 2026 • 3:30 AM" or "Thu, Jan 22, 2500 BCE • 3:30 AM"
   * @param {Date} date - Date to format
   * @param {Object} bceContext - Optional BCE context { isBCE, astronomicalYear }
   */
  formatFull: (date, bceContext = null) => {
    if (!date) return 'Unknown';
    const d = date instanceof Date ? date : new Date(date);
    if (isNaN(d.getTime())) return 'Invalid Date';
    
    // Check for BCE date
    const isBCE = bceContext?.isBCE || bceContext?.astronomicalYear < 0;
    if (isBCE && bceContext?.astronomicalYear != null) {
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      const weekday = dayNames[d.getDay()];
      const month = monthNames[d.getMonth()];
      const day = d.getDate();
      const astroYear = bceContext.astronomicalYear;
      const displayYear = Math.abs(astroYear) + (astroYear <= 0 ? 1 : 0);
      const timeStr = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
      return `${weekday}, ${month} ${day}, ${displayYear} BCE • ${timeStr}`;
    }
    
    const dateStr = d.toLocaleDateString('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
    const timeStr = d.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit', 
      hour12: true 
    });
    return `${dateStr} • ${timeStr}`;
  },
  
  /**
   * Format date only with year
   * Example: "Jan 22, 2026" or "Jan 22, 2500 BCE"
   * @param {Date} date - Date to format
   * @param {Object} bceContext - Optional BCE context { isBCE, astronomicalYear }
   */
  formatDateWithYear: (date, bceContext = null) => {
    if (!date) return 'Unknown';
    const d = date instanceof Date ? date : new Date(date);
    if (isNaN(d.getTime())) return 'Invalid Date';
    
    // Check for BCE date
    const isBCE = bceContext?.isBCE || bceContext?.astronomicalYear < 0;
    if (isBCE && bceContext?.astronomicalYear != null) {
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const month = monthNames[d.getMonth()];
      const day = d.getDate();
      const astroYear = bceContext.astronomicalYear;
      const displayYear = Math.abs(astroYear) + (astroYear <= 0 ? 1 : 0);
      return `${month} ${day}, ${displayYear} BCE`;
    }
    
    return d.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  },
  
  /**
   * Format time only
   * Example: "3:30 AM"
   */
  formatTime: (date) => {
    if (!date) return '';
    const d = date instanceof Date ? date : new Date(date);
    if (isNaN(d.getTime())) return '';
    
    return d.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit', 
      hour12: true 
    });
  },
  
  /**
   * Format date string (YYYY-MM-DD) with proper timezone handling
   * CRITICAL: Parses date-only strings as local time, not UTC
   */
  parseDateString: (dateStr) => {
    if (!dateStr) return null;
    // For date-only strings (YYYY-MM-DD), parse as local time
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      const [year, month, day] = dateStr.split('-').map(Number);
      return new Date(year, month - 1, day); // month is 0-indexed
    }
    // For full ISO strings, use standard parsing
    return new Date(dateStr);
  },
  
  /**
   * Format date string safely with timezone awareness
   * Example: "Jun 15, 1990"
   */
  formatDateStringSafe: (dateStr) => {
    const d = REAL_TIME_DISPLAY.parseDateString(dateStr);
    if (!d || isNaN(d.getTime())) return 'Unknown';
    
    return d.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  }
};

/**
 * REAL_PLACE_DISPLAY - Standard formatting for location everywhere
 * Always includes: city, state/province, country
 */
export const REAL_PLACE_DISPLAY = {
  /**
   * Format complete location (for main displays)
   * Example: "New York, NY, USA"
   */
  formatFull: (location) => {
    if (!location) return 'Unknown';
    const { city, state, country } = location;
    const parts = [city, state, country].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : 'Unknown';
  },
  
  /**
   * Format short location (city + country)
   * Example: "New York, USA"
   */
  formatShort: (location) => {
    if (!location) return 'Unknown';
    const { city, country } = location;
    const parts = [city, country].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : 'Unknown';
  },
  
  /**
   * Format city only
   * Example: "New York"
   */
  formatCity: (location) => {
    if (!location?.city) return 'Unknown';
    return location.city;
  },
  
  /**
   * Format state/country codes (compact for tiles)
   * Example: "NY, USA" or "England, UK" or "USA"
   */
  formatStateCountryCodes: (location) => {
    if (!location) return '';
    const { state, country } = location;
    
    // Get country code (common mappings)
    const countryCode = REAL_PLACE_DISPLAY.getCountryCode(country);
    
    // Build parts - state code + country code
    const parts = [];
    if (state) parts.push(state.length <= 3 ? state : state); // Keep short state codes, use full for longer
    if (countryCode) parts.push(countryCode);
    
    return parts.join(', ');
  },
  
  /**
   * Get country code from country name
   */
  getCountryCode: (country) => {
    if (!country) return '';
    const countryUpper = country.toUpperCase();
    
    // Common country code mappings
    const codes = {
      'UNITED STATES': 'USA',
      'UNITED STATES OF AMERICA': 'USA',
      'UNITED KINGDOM': 'UK',
      'GREAT BRITAIN': 'UK',
      'AUSTRALIA': 'AUS',
      'CANADA': 'CAN',
      'GERMANY': 'GER',
      'FRANCE': 'FRA',
      'JAPAN': 'JPN',
      'CHINA': 'CHN',
      'INDIA': 'IND',
      'BRAZIL': 'BRA',
      'MEXICO': 'MEX',
      'SPAIN': 'ESP',
      'ITALY': 'ITA',
      'NETHERLANDS': 'NLD',
      'SWEDEN': 'SWE',
      'NORWAY': 'NOR',
      'DENMARK': 'DNK',
      'SWITZERLAND': 'SUI',
      'AUSTRIA': 'AUT',
      'BELGIUM': 'BEL',
      'POLAND': 'POL',
      'RUSSIA': 'RUS',
      'SOUTH KOREA': 'KOR',
      'NEW ZEALAND': 'NZL',
      'IRELAND': 'IRL',
      'PORTUGAL': 'PRT',
      'SOUTH AFRICA': 'RSA',
      'ARGENTINA': 'ARG',
      'EGYPT': 'EGY',
      'ISRAEL': 'ISR',
      'SINGAPORE': 'SGP',
      'THAILAND': 'THA',
      'PHILIPPINES': 'PHL',
      'INDONESIA': 'IDN',
      'MALAYSIA': 'MYS',
      'VIETNAM': 'VNM',
      'TURKEY': 'TUR',
      'GREECE': 'GRC',
      'CZECH REPUBLIC': 'CZE',
      'FINLAND': 'FIN',
      'HONG KONG': 'HKG',
      'TAIWAN': 'TWN',
      'UAE': 'UAE',
      'UNITED ARAB EMIRATES': 'UAE',
      'SAUDI ARABIA': 'KSA'
    };
    
    return codes[countryUpper] || (country.length <= 3 ? country.toUpperCase() : country);
  },
  
  /**
   * Format coordinates
   * Example: "40.71°N, 74.01°W"
   */
  formatCoords: (location) => {
    if (!location?.latitude || !location?.longitude) return '';
    const lat = location.latitude;
    const lon = location.longitude;
    const latDir = lat >= 0 ? 'N' : 'S';
    const lonDir = lon >= 0 ? 'E' : 'W';
    return `${Math.abs(lat).toFixed(2)}°${latDir}, ${Math.abs(lon).toFixed(2)}°${lonDir}`;
  },
  
  /**
   * Format with coordinates as fallback
   * Example: "New York, NY, USA" or "40.71°N, 74.01°W"
   */
  formatWithCoordsFallback: (location) => {
    const full = REAL_PLACE_DISPLAY.formatFull(location);
    if (full !== 'Unknown') return full;
    return REAL_PLACE_DISPLAY.formatCoords(location) || 'Unknown';
  }
};

// =============================================================================
// REAL-TIME DATA INTEGRATION STANDARDS
// Ensures all synthesis/interpretations include associated temporal data
// =============================================================================

export const REALTIME_DATA_STANDARDS = {
  // Every interpretation MUST include these real-time data anchors
  requiredDataAnchors: {
    phase: {
      required: true,
      fields: ['currentPhase', 'daysInPhase', 'daysLeftInPhase', 'phaseProgress'],
      displayFormat: '{phaseName} • Day {daysInPhase} of ~{phaseDuration} ({daysLeftInPhase}d left)'
    },
    sign: {
      required: true,
      fields: ['currentSign', 'daysInSign', 'daysLeftInSign', 'signProgress'],
      displayFormat: '{signName} • {daysLeftInSign}d remaining'
    },
    cycle: {
      required: true,
      fields: ['lunarAge', 'daysUntilNewMoon', 'daysUntilFullMoon', 'cycleProgress'],
      displayFormat: 'Lunar Day {lunarAge} • {daysUntilNextPhase}d to {nextPhaseName}'
    },
    season: {
      required: true,
      fields: ['currentSeason', 'daysInSeason', 'daysLeftInSeason', 'seasonProgress'],
      displayFormat: '{seasonName} • {daysLeftInSeason}d remaining'
    },
    station: {
      required: false,
      fields: ['stationType', 'daysInStation', 'daysUntilDirect', 'daysUntilRetrograde'],
      displayFormat: '{planetName} {stationType} • {daysRemaining}d'
    }
  },
  
  // "Days Until X" display standards
  daysUntilFormats: {
    imminent: { maxDays: 3, badge: 'IMMINENT', color: '#EF4444' },     // Red
    building: { maxDays: 7, badge: 'BUILDING', color: '#F97316' },     // Orange
    forming: { maxDays: 14, badge: 'FORMING', color: '#FBBF24' },      // Yellow
    approaching: { maxDays: 30, badge: 'APPROACHING', color: '#22D3EE' }, // Cyan
    distant: { maxDays: Infinity, badge: null, color: '#94A3B8' }      // Gray
  },
  
  // Energy date ranges - identifies peak/low energy periods
  energyDateRanges: {
    // Phase-based energy peaks
    phaseEnergy: {
      NOURISH: { peakDay: 3, energy: 'restorative', keywords: ['rest', 'incubation'] },
      ACTIVATE: { peakDay: 5, energy: 'initiating', keywords: ['effort', 'action'] },
      FULFILL: { peakDay: 15, energy: 'expressive', keywords: ['visibility', 'harvest'] },
      INTEGRATE: { peakDay: 25, energy: 'discerning', keywords: ['release', 'wisdom'] }
    },
    
    // Calculate highest energy window for an event/goal
    getEnergyWindow: (eventType) => ({
      // These return relative day ranges
      newProject: { optimalPhases: ['ACTIVATE'], peakRange: [6, 12] },
      completion: { optimalPhases: ['FULFILL'], peakRange: [13, 18] },
      planning: { optimalPhases: ['NOURISH'], peakRange: [1, 5] },
      release: { optimalPhases: ['INTEGRATE'], peakRange: [22, 28] },
      socialEvent: { optimalPhases: ['FULFILL', 'ACTIVATE'], peakRange: [10, 18] },
      restRecovery: { optimalPhases: ['NOURISH'], peakRange: [1, 7] },
      creativeWork: { optimalPhases: ['ACTIVATE', 'FULFILL'], peakRange: [8, 17] },
      reflection: { optimalPhases: ['INTEGRATE', 'NOURISH'], peakRange: [20, 29] }
    })
  },
  
  // Containers that MUST display real-time data
  requiredContainers: [
    'aspectsPanel',
    'dateTimePicker', 
    'locationController',
    'universalInfoPanel',
    'comparisonSynthesisPanel',
    'cihIntelligencePanel'
  ],
  
  // Data refresh intervals (in milliseconds)
  refreshIntervals: {
    lunar: 60000,        // 1 minute for lunar data
    planetary: 300000,   // 5 minutes for planetary positions  
    aspects: 600000,     // 10 minutes for aspects
    eclipses: 3600000    // 1 hour for eclipse data
  },
  
  // SYNTHESIS CONTEXT REQUIREMENTS
  // All term descriptions and interpretations MUST include real-time context
  synthesisContextRules: {
    // Every term/element description must answer "why is this relevant NOW?"
    requiredContext: {
      zodiacSign: {
        mustInclude: ['current planet positions in this sign', 'days until sign change'],
        template: '{signDescription} Currently hosting {planetsInSign}. {relevantAspects}'
      },
      planet: {
        mustInclude: ['current sign', 'current phase/station', 'key aspects'],
        template: '{planetDescription} Now in {currentSign}, {aspectContext}.'
      },
      aspect: {
        mustInclude: ['exactness/orb', 'applying/separating', 'days to exact'],
        template: '{aspectDescription} Currently {orbStatus} at {orb}° ({daysToExact}).'
      },
      phase: {
        mustInclude: ['days in current phase', 'days to next phase'],
        template: '{phaseDescription} Day {dayInPhase} of {phaseName} ({daysToNext}d to {nextPhase}).'
      },
      house: {
        mustInclude: ['planets currently in house', 'sign on cusp'],
        template: '{houseDescription} Currently contains: {planetsInHouse}. Cusp in {cuspSign}.'
      }
    },
    
    // CO (Cosmic Orientation) Panel specific rules
    coPanelRules: {
      // Header must show current date/time
      headerMustInclude: ['current date', 'current time', 'location'],
      // Each element card must have real-time data badge
      elementBadge: {
        format: '{status} • {daysUntil}d',
        statusOptions: ['Active', 'Applying', 'Separating', 'Exact', 'Station']
      },
      // Eclipse panel example (already good) - emulate this pattern
      eclipsePanelPattern: {
        topSection: ['type', 'date', 'days_until', 'status'],
        bottomSection: ['interpretation with context']
      }
    },
    
    // Anti-pattern: Textbook definitions without context
    antiPatterns: [
      'Generic descriptions without current planetary positions',
      'Static text that doesn\'t change with time',
      'Missing "days until" for upcoming events',
      'No reference to current phase or season'
    ]
  }
};

// =============================================================================
// CIH COSMOLOGY - SOURCE OF TRUTH FOR PHASE MAPPINGS
// =============================================================================
// This is the authoritative source for all phase-to-element mappings.
// All components MUST use these mappings for consistent phase colors.
// 
// CiH PHASE CYCLE:
// NOURISH (blue)   → Inward, restorative, foundational
// ACTIVATE (green) → Outward, initiating, expanding  
// FULFILL (orange) → Expressive, manifesting, illuminating
// INTEGRATE (purple) → Processing, releasing, consolidating

export const CIH_COSMOLOGY = {
  // Phase colors - SINGLE SOURCE OF TRUTH
  phaseColors: {
    NOURISH: '#4A90E2',    // Blue - inward, restorative
    ACTIVATE: '#7ED321',   // Green - outward, initiating
    FULFILL: '#F5A623',    // Orange - expressive, manifesting
    INTEGRATE: '#9B59B6'   // Purple - processing, releasing
  },
  
  // =============================================================================
  // NEUTRAL UI COLORS - For labels that should NOT signal a phase
  // =============================================================================
  // Use these for standard text labels (planet names, sign names, etc.)
  // These MUST contrast with all phase colors to avoid confusion
  // HIGH CONTRAST VALUES for maximum readability (META/CORE STANDARD)
  neutralColors: {
    // Primary neutral - VERY BRIGHT silver (NOT gold, which looks like FULFILL)
    label: '#E0E8F0',        // HIGH CONTRAST: Very bright silver
    labelHover: '#F0F4F8',   // Near white on hover
    labelMuted: '#D0DCE8',   // HIGH CONTRAST: Still very visible (was muted, now bright)
    
    // Secondary neutral - soft cream (not yellow/gold)
    accent: '#F0ECE4',       // Bright warm off-white
    accentMuted: '#E4E0D8',  // HIGH CONTRAST: Visible warm gray
    
    // Brass/Gold - ONLY for decorative elements, NOT for data labels
    decorative: '#C5A059',   // Use sparingly, only for borders/accents
    decorativeLight: '#E6C288'
  },
  
  // =============================================================================
  // COMMON GLOSSARY TERMS - Auto-link these when detected
  // =============================================================================
  // Terms that should ALWAYS trigger glossary overlays
  autoLinkTerms: [
    'retrograde', 'direct', 'station', 'stationing',
    'conjunction', 'opposition', 'trine', 'square', 'sextile', 'quincunx',
    'new moon', 'full moon', 'first quarter', 'last quarter',
    'waxing', 'waning', 'crescent', 'gibbous',
    'eclipse', 'solar eclipse', 'lunar eclipse',
    'stellium', 'void of course', 'cazimi',
    'nourish', 'activate', 'fulfill', 'integrate'
  ],
  
  // LUNAR PHASES → CiH Phase
  // 8 traditional phases mapped to 4 CiH phases
  lunarPhaseMapping: {
    // NOURISH phase (New Moon cycle - first ~7 days)
    'new_moon': 'NOURISH',
    'new moon': 'NOURISH',
    'waxing_crescent': 'NOURISH',
    'waxing crescent': 'NOURISH',
    
    // ACTIVATE phase (First Quarter cycle - days ~7-14)
    'first_quarter': 'ACTIVATE',
    'first quarter': 'ACTIVATE',
    'waxing_gibbous': 'ACTIVATE',
    'waxing gibbous': 'ACTIVATE',
    
    // FULFILL phase (Full Moon cycle - days ~14-21)
    'full_moon': 'FULFILL',
    'full moon': 'FULFILL',
    'waning_gibbous': 'FULFILL',
    'waning gibbous': 'FULFILL',
    
    // INTEGRATE phase (Last Quarter cycle - days ~21-28)
    'last_quarter': 'INTEGRATE',
    'last quarter': 'INTEGRATE',
    'third_quarter': 'INTEGRATE',
    'third quarter': 'INTEGRATE',
    'waning_crescent': 'INTEGRATE',
    'waning crescent': 'INTEGRATE',
    'balsamic': 'INTEGRATE'
  },
  
  // ZODIAC SIGNS → CiH Phase (Archetype)
  // Each sign has an inherent phase quality
  zodiacPhaseMapping: {
    // NOURISH signs (Water/Earth receptive)
    'capricorn': 'NOURISH',
    'aquarius': 'NOURISH', 
    'pisces': 'NOURISH',
    
    // ACTIVATE signs (Fire/Air initiating)
    'aries': 'ACTIVATE',
    'taurus': 'ACTIVATE',
    'gemini': 'ACTIVATE',
    
    // FULFILL signs (Fire/Water expressive)
    'cancer': 'FULFILL',
    'leo': 'FULFILL',
    'virgo': 'FULFILL',
    
    // INTEGRATE signs (Air/Water processing)
    'libra': 'INTEGRATE',
    'scorpio': 'INTEGRATE',
    'sagittarius': 'INTEGRATE'
  },
  
  // SEASONS → CiH Phase
  seasonPhaseMapping: {
    // Temperate seasons
    'winter': 'NOURISH',
    'spring': 'ACTIVATE',
    'summer': 'FULFILL',
    'autumn': 'INTEGRATE',
    'fall': 'INTEGRATE',
    // Polar seasons (four quadrants on dial)
    'polar night': 'NOURISH',
    'light returns': 'ACTIVATE',
    'polar day': 'FULFILL',
    'light fades': 'INTEGRATE',
    // Polar nuanced states
    'polar night (deep)': 'NOURISH',
    'polar night (approaching)': 'NOURISH',
    'polar twilight': 'ACTIVATE',
    'polar twilight (emerging)': 'ACTIVATE',
    'polar twilight (fading)': 'INTEGRATE',
    'polar day (emerging)': 'ACTIVATE',
    'polar day (rising)': 'ACTIVATE',
    'polar day (peak)': 'FULFILL',
    'polar day (fading)': 'INTEGRATE',
    // Equatorial seasons (four quadrants mapped to CiH phases)
    'wet': 'ACTIVATE',
    'dry': 'INTEGRATE',
    'wet (primary)': 'ACTIVATE',
    'dry (primary)': 'FULFILL',
    'wet (secondary)': 'INTEGRATE',
    'dry (secondary)': 'NOURISH',
    // Australasian/Pacific simple seasons
    'wet (monsoon)': 'ACTIVATE',
    'dry (trade wind)': 'INTEGRATE'
  },
  
  // =============================================================================
  // REGIONAL CIH PHASE CORRESPONDENCES - Source of Truth for Synthesis
  // =============================================================================
  // Use these correspondences to synthesize region-specific interpretations.
  // All regions share cyclical patterns of activation and integration, but:
  // - Polar extremes are stark (extended phases with nuanced transitions)
  // - Equatorial cycles are moisture-driven (wet/dry)
  // - Temperate zones balance both light and temperature (four seasons)
  
  regionalPhaseGuidance: {
    // POLAR REGIONS - Nuanced light/dark cycles with 6 transitional states
    polar: {
      description: 'Polar regions operate on extremes with gradual transitions',
      phases: {
        polarDayPeak: {
          corresponds: ['FULFILL'],
          description: 'Midnight sun at its peak - maximum light, maximum expression',
          qualities: ['endless daylight', 'peak intensity', 'tireless manifestation', 'full visibility']
        },
        polarDayRising: {
          corresponds: ['ACTIVATE', 'FULFILL'],
          description: 'Light builds toward midnight sun - momentum accelerates',
          qualities: ['building intensity', 'rising energy', 'approaching peak', 'anticipation']
        },
        polarDayEmerging: {
          corresponds: ['ACTIVATE'],
          description: 'Light surges after polar night - explosive spring activation',
          qualities: ['rapid expansion', 'urgent growth', 'fierce awakening', 'spring surge']
        },
        polarDayFading: {
          corresponds: ['FULFILL', 'INTEGRATE'],
          description: 'Light begins gentle retreat - harvest while abundance holds',
          qualities: ['softening light', 'harvest time', 'gentle release', 'transition begins']
        },
        polarNightDeep: {
          corresponds: ['NOURISH'],
          description: 'Deep polar night - profound darkness invites deepest rest',
          qualities: ['complete darkness', 'deep rest', 'aurora magic', 'inner vision']
        },
        polarNightApproaching: {
          corresponds: ['INTEGRATE', 'NOURISH'],
          description: 'Darkness gathers - final release before deep winter',
          qualities: ['deepening dark', 'surrender', 'final preparations', 'acceptance']
        },
        polarTwilightEmerging: {
          corresponds: ['NOURISH', 'ACTIVATE'],
          description: 'First light returns - sacred threshold of hope and renewal',
          qualities: ['hope dawning', 'first light', 'gentle stirring', 'emergence']
        },
        polarTwilightFading: {
          corresponds: ['INTEGRATE'],
          description: 'Light retreats toward polar night - poignant integration',
          qualities: ['fading light', 'deepening quiet', 'beautiful release', 'acceptance']
        }
      },
      synthesisNote: 'Polar regions experience the full cycle through nuanced transitions. Six distinct states create a complete journey from deepest dark to endless light and back, each with its own unique energy and invitation.'
    },
    
    // EQUATORIAL REGIONS - Moisture-driven cycles
    equatorial: {
      description: 'Equatorial regions mirror cycles via wet and dry seasons',
      phases: {
        wet: {
          corresponds: ['ACTIVATE', 'FULFILL'],
          description: 'Wet season mirrors growth and activation',
          qualities: ['abundance', 'growth', 'fertility', 'expansion', 'nourishing rains']
        },
        dry: {
          corresponds: ['INTEGRATE', 'NOURISH'],
          description: 'Dry season mirrors reflection and integration',
          qualities: ['conservation', 'reflection', 'consolidation', 'inner work', 'preparation']
        },
        transition: {
          corresponds: ['all phases equally'],
          description: 'Transitions between wet and dry are softer shifts, creating balance between phases',
          qualities: ['gentle change', 'balanced energy', 'adaptive flow']
        }
      },
      synthesisNote: 'Equatorial moisture cycles create a rhythm distinct from light-based temperate seasons. Growth and rest follow water, not sun angle.'
    },
    
    // TEMPERATE REGIONS - Four-phase light/temperature cycle
    temperate: {
      description: 'Temperate regions hold a four-phase cycle that gradually flows',
      phases: {
        spring: {
          corresponds: ['ACTIVATE'],
          description: 'Activation phase - outward initiating energy',
          qualities: ['new beginnings', 'emergence', 'expansion', 'planting seeds', 'fresh starts']
        },
        summer: {
          corresponds: ['FULFILL'],
          description: 'Fulfillment phase - expressive manifesting energy',
          qualities: ['full expression', 'peak activity', 'abundance', 'illumination', 'harvest preparation']
        },
        autumn: {
          corresponds: ['INTEGRATE'],
          description: 'Integration phase - processing and releasing',
          qualities: ['harvest', 'letting go', 'gratitude', 'completion', 'preparing for rest']
        },
        winter: {
          corresponds: ['NOURISH'],
          description: 'Nourishment phase - inward restorative energy',
          qualities: ['rest', 'reflection', 'gestation', 'foundations', 'inner resources']
        }
      },
      synthesisNote: 'Temperate seasons gradually flow into each other, creating a balanced four-phase cycle that mirrors the complete CiH framework.'
    }
  },
  
  // Helper method to get regional phase guidance
  getRegionalGuidance: (region) => {
    const regionKey = region?.toLowerCase() || 'temperate';
    if (regionKey.includes('polar')) return CIH_COSMOLOGY.regionalPhaseGuidance.polar;
    if (regionKey.includes('equat')) return CIH_COSMOLOGY.regionalPhaseGuidance.equatorial;
    return CIH_COSMOLOGY.regionalPhaseGuidance.temperate;
  },
  
  // ELEMENTS → CiH Phase bias
  elementPhaseMapping: {
    'water': 'NOURISH',
    'fire': 'ACTIVATE',
    'earth': 'FULFILL',
    'air': 'INTEGRATE'
  }
};

// Helper to get CiH phase for any lunar phase name
export const getLunarCihPhase = (phaseName) => {
  if (!phaseName) return 'NOURISH';
  const key = phaseName.toLowerCase().replace(/\s+/g, '_');
  const altKey = phaseName.toLowerCase();
  return CIH_COSMOLOGY.lunarPhaseMapping[key] 
      || CIH_COSMOLOGY.lunarPhaseMapping[altKey] 
      || 'NOURISH';
};

// Helper to get CiH phase for any zodiac sign
export const getZodiacCihPhase = (sign) => {
  if (!sign) return 'ACTIVATE';
  return CIH_COSMOLOGY.zodiacPhaseMapping[sign.toLowerCase()] || 'ACTIVATE';
};

// Helper to get CiH phase for any season
export const getSeasonCihPhase = (season) => {
  if (!season) return 'NOURISH';
  return CIH_COSMOLOGY.seasonPhaseMapping[season.toLowerCase()] || 'NOURISH';
};

// Helper to get phase color
export const getCihPhaseColor = (phase) => {
  return CIH_COSMOLOGY.phaseColors[phase] || CIH_COSMOLOGY.phaseColors.NOURISH;
};

// Helper to get regional phase guidance for synthesis interpretations
export const getRegionalCihGuidance = (climateZone) => {
  const zone = climateZone?.toLowerCase() || 'temperate';
  if (zone.includes('polar')) return CIH_COSMOLOGY.regionalPhaseGuidance.polar;
  if (zone.includes('equat')) return CIH_COSMOLOGY.regionalPhaseGuidance.equatorial;
  return CIH_COSMOLOGY.regionalPhaseGuidance.temperate;
};

// Helper to get phase correspondence for a specific season/condition in a region
export const getRegionalPhaseCorrespondence = (climateZone, seasonOrCondition) => {
  const guidance = getRegionalCihGuidance(climateZone);
  if (!guidance?.phases) return ['NOURISH'];
  
  const condition = seasonOrCondition?.toLowerCase() || '';
  
  // Try to match the condition to a phase in the regional guidance
  for (const [key, phaseData] of Object.entries(guidance.phases)) {
    if (condition.includes(key) || key.includes(condition)) {
      return phaseData.corresponds;
    }
  }
  
  // Default fallbacks by climate zone
  if (climateZone?.toLowerCase().includes('polar')) return ['NOURISH', 'INTEGRATE'];
  if (climateZone?.toLowerCase().includes('equat')) return ['ACTIVATE', 'INTEGRATE'];
  return ['ACTIVATE']; // Temperate default
};

// =============================================================================
// DYNAMIC PLANET PHASE CALCULATION
// =============================================================================
// Calculates the effective CiH phase for a planet based on real-time field modulators.
// This creates consistency between tile badges and info panel displays.
//
// Field modulators considered:
// 1. Planet's archetypal base phase (inherent energy)
// 2. Current lunar phase (emotional/cyclical influence) - weighted by Moon's gravity
// 3. Current season (environmental influence) - weighted by Earth's gravity
// 4. Planet's current zodiac sign (archetypal coloring)
// 5. Retrograde status (shifts toward INTEGRATE/NOURISH)
// 6. Aspects to luminaries (Sun/Moon aspects create phase emphasis)
//
// The Moon is special: its phase is determined by the actual lunar cycle, not archetypal.
// =============================================================================

/**
 * Get the dynamic CiH phase for a planet based on current field conditions.
 * 
 * @param {string} planetName - Name of the planet (e.g., 'moon', 'venus', 'mars')
 * @param {Object} cosmicData - Full cosmic data from API containing positions, cih_intelligence, season
 * @param {Object} interpretation - The static interpretation data for this planet
 * @returns {string} The calculated CiH phase (NOURISH, ACTIVATE, FULFILL, or INTEGRATE)
 */
export const getDynamicPlanetPhase = (planetName, cosmicData, interpretation) => {
  if (!cosmicData || !planetName) {
    return interpretation?.phase || 'ACTIVATE';
  }
  
  const planet = planetName.toLowerCase();
  const positions = cosmicData?.positions || {};
  const planetData = positions[planet];
  
  // MOON SPECIAL CASE: Use actual lunar cycle phase, not archetypal
  if (planet === 'moon') {
    const phaseName = planetData?.phase_name;
    if (phaseName) {
      return getLunarCihPhase(phaseName);
    }
    // Fallback to phase_angle calculation if phase_name not available
    const phaseAngle = planetData?.phase_angle;
    if (phaseAngle !== undefined) {
      if (phaseAngle < 90) return 'NOURISH';
      if (phaseAngle < 180) return 'ACTIVATE';
      if (phaseAngle < 270) return 'FULFILL';
      return 'INTEGRATE';
    }
  }
  
  // For other planets: Calculate weighted phase based on field modulators
  const phaseScores = {
    NOURISH: 0,
    ACTIVATE: 0,
    FULFILL: 0,
    INTEGRATE: 0
  };
  
  // 1. Planet's archetypal base phase (weight: 40%)
  const archetypePhase = interpretation?.phase || 'ACTIVATE';
  phaseScores[archetypePhase] += 40;
  
  // 2. Current lunar phase influence (weight: 25% - Moon has high gravity)
  const moonPhaseName = positions?.moon?.phase_name;
  if (moonPhaseName) {
    const lunarPhase = getLunarCihPhase(moonPhaseName);
    phaseScores[lunarPhase] += 25;
  }
  
  // 3. Current season influence (weight: 15% - Earth has highest gravity)
  const currentSeason = cosmicData?.season?.season;
  if (currentSeason) {
    const seasonPhase = getSeasonCihPhase(currentSeason);
    phaseScores[seasonPhase] += 15;
  }
  
  // 4. Planet's current zodiac sign (weight: 10%)
  const planetSign = planetData?.zodiac_sign;
  if (planetSign) {
    const signPhase = getZodiacCihPhase(planetSign);
    phaseScores[signPhase] += 10;
  }
  
  // 5. Retrograde status - shifts toward reflective phases (weight: 5%)
  const isRetrograde = planetData?.is_retrograde;
  if (isRetrograde) {
    phaseScores.INTEGRATE += 5;
    phaseScores.NOURISH += 3;
  } else {
    // Direct motion slightly favors outward phases
    phaseScores.ACTIVATE += 3;
    phaseScores.FULFILL += 2;
  }
  
  // 6. Aspects to luminaries - check if planet has aspects to Sun or Moon
  const aspectFlags = cosmicData?.cih_intelligence?.planetary_flags || [];
  const hasLuminaryAspect = aspectFlags.some(flag => {
    const desc = (flag.description || '').toLowerCase();
    return desc.includes(planet) && (desc.includes('sun') || desc.includes('moon'));
  });
  
  if (hasLuminaryAspect) {
    // Luminary aspects amplify the dominant phase slightly
    const currentMax = Math.max(...Object.values(phaseScores));
    const dominantPhases = Object.entries(phaseScores).filter(([_, score]) => score === currentMax);
    if (dominantPhases.length > 0) {
      phaseScores[dominantPhases[0][0]] += 5;
    }
  }
  
  // Find the phase with highest score
  let maxScore = 0;
  let dominantPhase = archetypePhase;
  
  for (const [phase, score] of Object.entries(phaseScores)) {
    if (score > maxScore) {
      maxScore = score;
      dominantPhase = phase;
    }
  }
  
  return dominantPhase;
};

/**
 * Check if an element type should use dynamic phase calculation.
 * Only planets use dynamic calculation; seasons, sabbats, and zodiac signs use static archetypal phases.
 * 
 * @param {string} elementType - The type of element (planet, zodiac, season, sabbat, etc.)
 * @returns {boolean} Whether to use dynamic phase calculation
 */
export const shouldUseDynamicPhase = (elementType) => {
  return elementType === 'planet' || elementType === 'lunar_phase';
};

// =============================================================================
// PHASE EMPHASIS DISPLAY - Meta Template for Phase Headers
// =============================================================================
// Provides synthesis interpretation and unique convergence visuals for each phase.
// These are signature visual elements that distinguish The Onescent Dial.
//
// CONVERGENCE VISUAL TYPES:
// 1. Orbital Rings - Concentric animated circles (planetary/cosmic)
// 2. Wave Pattern - Flowing sine waves (lunar/emotional)
// 3. Spiral Flow - Golden ratio spiral (seasonal/growth)
// 4. Radial Burst - Emanating rays (sabbat/celebration)
// 5. Mandala - Geometric sacred pattern (zodiac/archetype)
// =============================================================================

export const PHASE_EMPHASIS_CONFIG = {
  // Phase-specific synthesis interpretations (one-liner insights)
  phaseSynthesis: {
    NOURISH: {
      brief: 'Receptive gathering of essential energies',
      extended: 'A time for introspection, rest, and receiving. The field supports quiet absorption and internal processing.',
      action: 'Receive, rest, reflect',
      quality: 'Yin / Inward'
    },
    ACTIVATE: {
      brief: 'Dynamic initiation of outward expression',
      extended: 'A time for action, beginnings, and outward movement. The field supports bold initiatives and new directions.',
      action: 'Initiate, express, begin',
      quality: 'Yang / Outward'
    },
    FULFILL: {
      brief: 'Peak manifestation and abundant realization',
      extended: 'A time for completion, celebration, and full expression. The field supports achievement and recognition.',
      action: 'Complete, celebrate, manifest',
      quality: 'Yang / Peak'
    },
    INTEGRATE: {
      brief: 'Synthesizing wisdom through release and reflection',
      extended: 'A time for processing, releasing, and preparing. The field supports letting go and distilling lessons.',
      action: 'Release, process, prepare',
      quality: 'Yin / Transition'
    }
  },
  
  // Convergence visual configurations by element type
  convergenceVisuals: {
    planet: {
      type: 'orbital',
      description: 'Orbital Rings',
      cssClass: 'convergence-orbital',
      animation: 'orbit-pulse',
      layers: 3
    },
    lunar_phase: {
      type: 'wave',
      description: 'Wave Pattern',
      cssClass: 'convergence-wave',
      animation: 'wave-flow',
      layers: 4
    },
    season: {
      type: 'spiral',
      description: 'Spiral Flow',
      cssClass: 'convergence-spiral',
      animation: 'spiral-grow',
      layers: 2
    },
    sabbat: {
      type: 'radial',
      description: 'Radial Burst',
      cssClass: 'convergence-radial',
      animation: 'radial-pulse',
      layers: 8
    },
    zodiac: {
      type: 'mandala',
      description: 'Mandala Pattern',
      cssClass: 'convergence-mandala',
      animation: 'mandala-rotate',
      layers: 12
    },
    aspect: {
      type: 'geometric',
      description: 'Geometric Lines',
      cssClass: 'convergence-geometric',
      animation: 'geo-pulse',
      layers: 6
    },
    retrograde: {
      type: 'orbital',
      description: 'Reverse Orbital',
      cssClass: 'convergence-orbital-reverse',
      animation: 'orbit-reverse',
      layers: 3
    }
  },
  
  // Get synthesis for a phase
  getSynthesis: (phase, format = 'brief') => {
    const data = PHASE_EMPHASIS_CONFIG.phaseSynthesis[phase];
    if (!data) return null;
    return format === 'extended' ? data.extended : data.brief;
  },
  
  // Get action keyword for a phase
  getAction: (phase) => {
    return PHASE_EMPHASIS_CONFIG.phaseSynthesis[phase]?.action || '';
  },
  
  // Get convergence visual config for element type
  getConvergenceVisual: (elementType) => {
    return PHASE_EMPHASIS_CONFIG.convergenceVisuals[elementType] || 
           PHASE_EMPHASIS_CONFIG.convergenceVisuals.planet;
  },
  
  // Generate SVG path for convergence visual
  generateConvergenceSVG: (elementType, phase, size = 40) => {
    const visual = PHASE_EMPHASIS_CONFIG.getConvergenceVisual(elementType);
    const color = getCihPhaseColor(phase);
    const center = size / 2;
    
    switch (visual.type) {
      case 'orbital':
        // Concentric circles
        return Array.from({ length: visual.layers }, (_, i) => {
          const radius = (size / 2) * (0.3 + (i * 0.25));
          return `<circle cx="${center}" cy="${center}" r="${radius}" fill="none" stroke="${color}" stroke-width="1" opacity="${0.3 + (i * 0.2)}"/>`;
        }).join('');
        
      case 'wave':
        // Sine wave pattern
        const points = Array.from({ length: 20 }, (_, i) => {
          const x = (i / 19) * size;
          const y = center + Math.sin((i / 19) * Math.PI * 4) * (size / 6);
          return `${x},${y}`;
        }).join(' ');
        return `<polyline points="${points}" fill="none" stroke="${color}" stroke-width="1.5" opacity="0.6"/>`;
        
      case 'spiral':
        // Simple spiral approximation
        const spiralPoints = Array.from({ length: 30 }, (_, i) => {
          const angle = (i / 30) * Math.PI * 3;
          const radius = (i / 30) * (size / 2.5);
          const x = center + Math.cos(angle) * radius;
          const y = center + Math.sin(angle) * radius;
          return `${x},${y}`;
        }).join(' ');
        return `<polyline points="${spiralPoints}" fill="none" stroke="${color}" stroke-width="1.5" opacity="0.6"/>`;
        
      case 'radial':
        // Radiating lines
        return Array.from({ length: visual.layers }, (_, i) => {
          const angle = (i / visual.layers) * Math.PI * 2;
          const x2 = center + Math.cos(angle) * (size / 2.2);
          const y2 = center + Math.sin(angle) * (size / 2.2);
          return `<line x1="${center}" y1="${center}" x2="${x2}" y2="${y2}" stroke="${color}" stroke-width="1.5" opacity="0.5"/>`;
        }).join('');
        
      case 'mandala':
        // 12-pointed star pattern
        const starPoints = Array.from({ length: 12 }, (_, i) => {
          const angle = (i / 12) * Math.PI * 2 - Math.PI / 2;
          const r = i % 2 === 0 ? size / 2.2 : size / 4;
          return `${center + Math.cos(angle) * r},${center + Math.sin(angle) * r}`;
        }).join(' ');
        return `<polygon points="${starPoints}" fill="none" stroke="${color}" stroke-width="1" opacity="0.5"/>`;
        
      case 'geometric':
        // Hexagonal pattern
        const hexPoints = Array.from({ length: 6 }, (_, i) => {
          const angle = (i / 6) * Math.PI * 2 - Math.PI / 2;
          return `${center + Math.cos(angle) * (size / 2.5)},${center + Math.sin(angle) * (size / 2.5)}`;
        }).join(' ');
        return `<polygon points="${hexPoints}" fill="none" stroke="${color}" stroke-width="1.5" opacity="0.5"/>`;
        
      default:
        return `<circle cx="${center}" cy="${center}" r="${size/3}" fill="none" stroke="${color}" stroke-width="1" opacity="0.5"/>`;
    }
  }
};

// =============================================================================
// BRANDING META - Single Source of Truth for Naming/Labels
// =============================================================================
// All user-facing labels and internal naming conventions

export const BRANDING_META = {
  // Product naming
  product: {
    full: 'THE ONESCENT DIAL',
    short: 'The Dial',
    internal: 'Dial',
    framework: 'Cycles in Harmony',
    frameworkAbbrev: 'CiH'
  },
  
  // Component naming (code-level congruence)
  components: {
    mainDial: 'Dial',              // Was: CosmicClock
    dialContainer: 'dial-container',
    dialSvg: 'dial-svg',
    dialWrapper: 'dial-wrapper'
  },
  
  // Test ID naming pattern
  testIds: {
    dial: 'the-dial',
    dialSvg: 'the-dial-svg',
    dialCenter: 'dial-center-hub',
    dialApp: 'the-dial-app'
  },
  
  // CSS class naming
  cssClasses: {
    container: 'dial-container',
    svg: 'dial-svg',
    wrapper: 'dial-wrapper'
  }
};

// =============================================================================
// UI SPACING META - Consistent padding/gaps across all interfaces
// =============================================================================
// Use these values for visual continuity across panels, overlays, and screens

export const UI_SPACING = {
  // Screen-level padding (for full-app containers)
  screen: {
    mobile: '12px',      // 0.75rem - tight on mobile
    tablet: '16px',      // 1rem - standard on tablets
    desktop: '20px',     // 1.25rem - comfortable on desktop
    // Recommended: Use 16px (1rem) as universal default
    default: '16px'
  },
  
  // Panel/Card internal padding
  panel: {
    xs: '8px',           // 0.5rem - compact sections
    sm: '12px',          // 0.75rem - tight panels
    md: '16px',          // 1rem - standard panels
    lg: '20px',          // 1.25rem - spacious panels
    xl: '24px',          // 1.5rem - hero sections
    default: '16px'
  },
  
  // Element gaps (space between items)
  gap: {
    xs: '4px',           // 0.25rem - tight inline items
    sm: '8px',           // 0.5rem - compact lists
    md: '12px',          // 0.75rem - standard lists
    lg: '16px',          // 1rem - section spacing
    xl: '24px',          // 1.5rem - major sections
    default: '12px'
  },
  
  // Border radius (for consistency)
  radius: {
    sm: '4px',           // Buttons, badges
    md: '8px',           // Cards, inputs
    lg: '12px',          // Panels, modals
    xl: '16px',          // Hero sections
    full: '9999px',      // Pills, avatars
    default: '8px'
  },
  
  // Tailwind class helpers
  tailwind: {
    screenPadding: 'px-4',           // 16px horizontal
    panelPadding: 'p-4',             // 16px all sides
    sectionGap: 'space-y-4',         // 16px vertical gap
    itemGap: 'gap-3',                // 12px gap
    compactGap: 'gap-2',             // 8px gap
    borderRadius: 'rounded-lg'       // 8px radius
  }
};

// Helper to get neutral label color (NEVER use gold for data labels)
export const getNeutralLabelColor = (variant = 'default') => {
  const colors = CIH_COSMOLOGY.neutralColors;
  switch (variant) {
    case 'hover': return colors.labelHover;
    case 'muted': return colors.labelMuted;
    case 'accent': return colors.accent;
    default: return colors.label;
  }
};

// =============================================================================
// DUAL COUNTDOWN DISPLAY TEMPLATE - Official Meta Standard
// =============================================================================
// This is the single source of truth for displaying time-based cycle information
// across the entire application. All countdown/countup displays MUST use this template.
//
// APPLIES TO:
// - Seasons (astronomical/climate)
// - Sabbats (wheel of year)
// - Zodiac signs (sun transit)
// - Lunar phases
// - Planetary stations (retrograde periods)
// - Any other cyclical cosmic elements
//
// DISPLAY FORMAT:
// ┌─────────────────────────────────────────┐
// │  [Name]                      [±X days]  │
// │  ▓▓▓▓▓▓▓▓▓▓▓░░░░░░░░░░░░░  (progress)  │
// │  [since last / until next] [Type]       │
// └─────────────────────────────────────────┘
// =============================================================================

export const DUAL_COUNTDOWN_TEMPLATE = {
  // Element type definitions for consistent labeling
  elementTypes: {
    season: {
      singularLabel: 'Season',
      pluralLabel: 'Seasons',
      pastPrefix: '',
      futurePrefix: '',
      subtypes: {
        astronomical: 'Season (astronomical)',
        climate: 'Season (climate)'
      }
    },
    sabbat: {
      singularLabel: 'Sabbat',
      pluralLabel: 'Sabbats',
      pastPrefix: '',
      futurePrefix: ''
    },
    zodiac: {
      singularLabel: 'Sign',
      pluralLabel: 'Signs',
      pastPrefix: 'since Sun left',
      futurePrefix: 'until Sun enters'
    },
    lunar_phase: {
      singularLabel: 'Phase',
      pluralLabel: 'Phases',
      pastPrefix: 'since last',
      futurePrefix: 'until next'
    },
    retrograde: {
      singularLabel: 'Station',
      pluralLabel: 'Stations',
      pastPrefix: 'since',
      futurePrefix: 'until'
    }
  },
  
  // Display configuration
  display: {
    // Progress bar styles
    progressBar: {
      height: '6px',        // h-1.5 equivalent
      borderRadius: 'full',
      backgroundColor: 'tod-bg-tertiary',
      activeOpacity: 1,
      inactiveOpacity: 0.4
    },
    // Text sizes
    textSizes: {
      name: 'text-xs',           // Element name
      days: 'text-xs font-bold', // Day count
      label: 'text-[10px]',      // "since/until" label
      header: 'text-[10px]'      // Section header
    },
    // Day count formatting
    formatDays: (days, isPast) => {
      const absdays = Math.abs(days);
      const sign = isPast ? '+' : '-';
      return `${sign}${absdays}d`;
    },
    // Alternative format without sign
    formatDaysSimple: (days) => `${Math.abs(days)}d`
  },
  
  /**
   * Generate display data for a cycle element (past or future)
   * @param {Object} params - Display parameters
   * @param {string} params.name - Element name (e.g., "Winter", "Yule", "Capricorn")
   * @param {string} params.type - Element type (season, sabbat, zodiac, lunar_phase, retrograde)
   * @param {number} params.days - Days since (positive) or until (negative)
   * @param {boolean} params.isPast - Whether this element is in the past
   * @param {number} params.progress - Progress percentage (0-100)
   * @param {string} params.color - CiH phase color for this element
   * @param {string} params.subtype - Optional subtype (e.g., 'astronomical', 'climate')
   * @returns {Object} Formatted display data
   */
  formatDisplay: ({ name, type, days, isPast, progress, color, subtype = null }) => {
    const typeConfig = DUAL_COUNTDOWN_TEMPLATE.elementTypes[type] || DUAL_COUNTDOWN_TEMPLATE.elementTypes.season;
    const prefix = isPast ? typeConfig.pastPrefix : typeConfig.futurePrefix;
    // Use simple singular label without subtype suffix
    const typeLabel = typeConfig.singularLabel.toLowerCase();
    // Only include prefix if it's non-empty
    const contextLabel = prefix ? `${prefix} ${typeLabel}` : '';
    
    return {
      name,
      daysDisplay: DUAL_COUNTDOWN_TEMPLATE.display.formatDays(days, isPast),
      daysSimple: DUAL_COUNTDOWN_TEMPLATE.display.formatDaysSimple(days),
      daysValue: Math.abs(days),
      contextLabel,
      progress: Math.min(100, Math.max(0, progress)),
      color,
      isPast,
      isFuture: !isPast
    };
  },
  
  /**
   * Get cycle elements ordered by proximity (past and future)
   * @param {Array} elements - Array of cycle elements with dates
   * @param {Date} currentDate - Reference date
   * @returns {Object} { past: [...], future: [...], current: element|null }
   */
  categorizeCycleElements: (elements, currentDate = new Date()) => {
    const now = currentDate.getTime();
    const msPerDay = 1000 * 60 * 60 * 24;
    
    const categorized = elements.map(el => {
      const startTime = new Date(el.startDate).getTime();
      const endTime = new Date(el.endDate).getTime();
      
      const isCurrent = now >= startTime && now <= endTime;
      const isPast = now > endTime;
      const isFuture = now < startTime;
      
      const daysSinceEnd = isPast ? Math.floor((now - endTime) / msPerDay) : 0;
      const daysUntilStart = isFuture ? Math.ceil((startTime - now) / msPerDay) : 0;
      
      return {
        ...el,
        isCurrent,
        isPast,
        isFuture,
        daysSinceEnd,
        daysUntilStart,
        proximity: isCurrent ? 0 : (isPast ? daysSinceEnd : daysUntilStart)
      };
    });
    
    return {
      past: categorized.filter(el => el.isPast).sort((a, b) => a.daysSinceEnd - b.daysSinceEnd),
      future: categorized.filter(el => el.isFuture).sort((a, b) => a.daysUntilStart - b.daysUntilStart),
      current: categorized.find(el => el.isCurrent) || null
    };
  }
};

// =============================================================================
// DUAL COUNTDOWN TIMING SYSTEM - Meta-Level Utility
// =============================================================================
// Provides "time since last / time until next" for all cyclical elements
// Applies to: seasons, sabbats, lunar phases, zodiac signs, planetary stations

export const TIMING_UTILITIES = {
  // Synodic month (Moon's orbital period relative to Sun) - 29.53 days
  LUNAR_CYCLE_DAYS: 29.53,
  
  // Zodiac sign duration (approximately 30 days, varies by sign)
  ZODIAC_SIGN_DAYS: {
    aries: 31, taurus: 31, gemini: 31, cancer: 31,
    leo: 31, virgo: 31, libra: 30, scorpio: 30,
    sagittarius: 29, capricorn: 29, aquarius: 30, pisces: 29
  },
  
  // Season duration (approximately 91 days)
  SEASON_DAYS: 91,
  
  // Sabbat spacing (approximately 45-46 days between each)
  SABBAT_SPACING: 45.6,
  
  // ==========================================================================
  // SEASON MARKERS - Astronomical Season Start Dates (Meta Standard)
  // ==========================================================================
  // Single source of truth for season boundaries across the entire application.
  // Used in: season panels, dial rings, Reference, OC, countdown calculations.
  // Dates are approximate - actual astronomical events vary by ~1-2 days per year.
  //
  // Northern Hemisphere:
  //   Winter: Winter Solstice (Dec 21) → Spring Equinox (Mar 21)
  //   Spring: Spring Equinox (Mar 21) → Summer Solstice (Jun 21)
  //   Summer: Summer Solstice (Jun 21) → Autumn Equinox (Sep 21)
  //   Autumn: Autumn Equinox (Sep 21) → Winter Solstice (Dec 21)
  //
  // Southern Hemisphere (inverted):
  //   Winter: Winter Solstice (Jun 21) → Spring Equinox (Sep 21)
  //   Spring: Spring Equinox (Sep 21) → Summer Solstice (Dec 21)
  //   Summer: Summer Solstice (Dec 21) → Autumn Equinox (Mar 21)
  //   Autumn: Autumn Equinox (Mar 21) → Winter Solstice (Jun 21)
  // ==========================================================================
  SEASON_MARKERS: {
    // Astronomical events with day-of-year (1-365) for Northern Hemisphere
    astronomicalEvents: {
      winterSolstice: { month: 12, day: 21, dayOfYear: 355, name: 'Winter Solstice' },
      springEquinox: { month: 3, day: 21, dayOfYear: 80, name: 'Spring Equinox' },
      summerSolstice: { month: 6, day: 21, dayOfYear: 172, name: 'Summer Solstice' },
      autumnEquinox: { month: 9, day: 21, dayOfYear: 264, name: 'Autumn Equinox' }
    },
    
    // Season definitions with start/end markers
    northern: {
      Winter: { 
        startEvent: 'winterSolstice', 
        endEvent: 'springEquinox',
        startMonth: 12, startDay: 21,
        endMonth: 3, endDay: 20,
        durationDays: 89, // Dec 21 - Mar 20
        cihPhase: 'NOURISH',
        description: 'Begins at Winter Solstice (shortest day)'
      },
      Spring: { 
        startEvent: 'springEquinox', 
        endEvent: 'summerSolstice',
        startMonth: 3, startDay: 21,
        endMonth: 6, endDay: 20,
        durationDays: 92, // Mar 21 - Jun 20
        cihPhase: 'ACTIVATE',
        description: 'Begins at Spring Equinox (equal day/night)'
      },
      Summer: { 
        startEvent: 'summerSolstice', 
        endEvent: 'autumnEquinox',
        startMonth: 6, startDay: 21,
        endMonth: 9, endDay: 20,
        durationDays: 93, // Jun 21 - Sep 20
        cihPhase: 'FULFILL',
        description: 'Begins at Summer Solstice (longest day)'
      },
      Autumn: { 
        startEvent: 'autumnEquinox', 
        endEvent: 'winterSolstice',
        startMonth: 9, startDay: 21,
        endMonth: 12, endDay: 20,
        durationDays: 90, // Sep 21 - Dec 20
        cihPhase: 'INTEGRATE',
        description: 'Begins at Autumn Equinox (equal day/night)'
      }
    },
    
    // Southern hemisphere (inverted seasons)
    southern: {
      Winter: { 
        startEvent: 'summerSolstice', // June 21 in south = winter solstice
        endEvent: 'autumnEquinox',    // Sep 21 in south = spring equinox
        startMonth: 6, startDay: 21,
        endMonth: 9, endDay: 20,
        durationDays: 93,
        cihPhase: 'NOURISH',
        description: 'Begins at Winter Solstice (June 21st)'
      },
      Spring: { 
        startEvent: 'autumnEquinox',  // Sep 21 in south = spring equinox
        endEvent: 'winterSolstice',   // Dec 21 in south = summer solstice
        startMonth: 9, startDay: 21,
        endMonth: 12, endDay: 20,
        durationDays: 90,
        cihPhase: 'ACTIVATE',
        description: 'Begins at Spring Equinox (September 21st)'
      },
      Summer: { 
        startEvent: 'winterSolstice', // Dec 21 in south = summer solstice
        endEvent: 'springEquinox',    // Mar 21 in south = autumn equinox
        startMonth: 12, startDay: 21,
        endMonth: 3, endDay: 20,
        durationDays: 89,
        cihPhase: 'FULFILL',
        description: 'Begins at Summer Solstice (December 21st)'
      },
      Autumn: { 
        startEvent: 'springEquinox',  // Mar 21 in south = autumn equinox
        endEvent: 'summerSolstice',   // Jun 21 in south = winter solstice
        startMonth: 3, startDay: 21,
        endMonth: 6, endDay: 20,
        durationDays: 92,
        cihPhase: 'INTEGRATE',
        description: 'Begins at Autumn Equinox (March 21st)'
      }
    },
    
    // Polar seasons (Northern Hemisphere Arctic)
    polar_northern: {
      'Polar Night': {
        startMonth: 12, startDay: 1,
        endMonth: 2, endDay: 28,
        durationDays: 90,
        cihPhase: 'NOURISH',
        description: 'Deep darkness - continuous polar night'
      },
      'Light Returns': {
        startMonth: 3, startDay: 1,
        endMonth: 5, endDay: 31,
        durationDays: 92,
        cihPhase: 'ACTIVATE',
        description: 'Twilight emerges - light returns after polar night'
      },
      'Polar Day': {
        startMonth: 6, startDay: 1,
        endMonth: 8, endDay: 31,
        durationDays: 92,
        cihPhase: 'FULFILL',
        description: 'Midnight sun - continuous polar day'
      },
      'Light Fades': {
        startMonth: 9, startDay: 1,
        endMonth: 11, endDay: 30,
        durationDays: 91,
        cihPhase: 'INTEGRATE',
        description: 'Twilight fades - light retreats toward polar night'
      }
    },
    
    // Polar seasons (Southern Hemisphere Antarctic)
    polar_southern: {
      'Polar Night': {
        startMonth: 6, startDay: 1,
        endMonth: 8, endDay: 31,
        durationDays: 92,
        cihPhase: 'NOURISH',
        description: 'Deep darkness - continuous polar night'
      },
      'Light Returns': {
        startMonth: 9, startDay: 1,
        endMonth: 11, endDay: 30,
        durationDays: 91,
        cihPhase: 'ACTIVATE',
        description: 'Twilight emerges - light returns after polar night'
      },
      'Polar Day': {
        startMonth: 12, startDay: 1,
        endMonth: 2, endDay: 28,
        durationDays: 90,
        cihPhase: 'FULFILL',
        description: 'Midnight sun - continuous polar day'
      },
      'Light Fades': {
        startMonth: 3, startDay: 1,
        endMonth: 5, endDay: 31,
        durationDays: 92,
        cihPhase: 'INTEGRATE',
        description: 'Twilight fades - light retreats toward polar night'
      }
    },
    
    // Equatorial seasons - 4-quadrant ITCZ-driven wet/dry cycle
    // These dates are approximate and vary by specific location
    equatorial: {
      'Wet (Primary)': {
        startMonth: 3, startDay: 1,
        endMonth: 5, endDay: 31,
        durationDays: 92,
        cihPhase: 'ACTIVATE',
        description: 'Primary wet season - main monsoon, peak rainfall'
      },
      'Dry (Primary)': {
        startMonth: 6, startDay: 1,
        endMonth: 8, endDay: 31,
        durationDays: 92,
        cihPhase: 'FULFILL',
        description: 'Primary dry season - clear skies, harvest time'
      },
      'Wet (Secondary)': {
        startMonth: 9, startDay: 1,
        endMonth: 11, endDay: 30,
        durationDays: 91,
        cihPhase: 'INTEGRATE',
        description: 'Secondary wet season - shorter rains, transition'
      },
      'Dry (Secondary)': {
        startMonth: 12, startDay: 1,
        endMonth: 2, endDay: 28,
        durationDays: 90,
        cihPhase: 'NOURISH',
        description: 'Secondary dry season - rest period, conservation'
      }
    },
    
    // Canonical season orders for each climate type
    order: ['Winter', 'Spring', 'Summer', 'Autumn'],
    polarOrder: ['Polar Night', 'Light Returns', 'Polar Day', 'Light Fades'],
    equatorialOrder: ['Dry (Secondary)', 'Wet (Primary)', 'Dry (Primary)', 'Wet (Secondary)'],
    
    // Get next season in cycle (handles all climate types)
    getNextSeason: (currentSeason) => {
      if (!currentSeason) return null;
      const lowerSeason = currentSeason.toLowerCase();
      
      // Check polar seasons
      const polarOrder = ['polar night', 'light returns', 'polar day', 'light fades'];
      let idx = polarOrder.findIndex(s => lowerSeason.includes(s) || s.includes(lowerSeason));
      if (idx !== -1) return ['Polar Night', 'Light Returns', 'Polar Day', 'Light Fades'][(idx + 1) % 4];
      
      // Check equatorial seasons
      if (lowerSeason.includes('wet') || lowerSeason.includes('dry')) {
        const eqOrder = ['dry (secondary)', 'wet (primary)', 'dry (primary)', 'wet (secondary)'];
        idx = eqOrder.findIndex(s => lowerSeason === s);
        if (idx !== -1) return ['Wet (Primary)', 'Dry (Primary)', 'Wet (Secondary)', 'Dry (Secondary)'][(idx + 1) % 4];
        // Simple wet/dry
        return lowerSeason.includes('wet') ? 'Dry' : 'Wet';
      }
      
      // Default to temperate
      const order = ['Winter', 'Spring', 'Summer', 'Autumn'];
      idx = order.findIndex(s => s.toLowerCase() === lowerSeason);
      return order[(idx + 1) % 4];
    },
    
    // Get previous season in cycle (handles all climate types)
    getPreviousSeason: (currentSeason) => {
      if (!currentSeason) return null;
      const lowerSeason = currentSeason.toLowerCase();
      
      // Check polar seasons
      const polarOrder = ['polar night', 'light returns', 'polar day', 'light fades'];
      let idx = polarOrder.findIndex(s => lowerSeason.includes(s) || s.includes(lowerSeason));
      if (idx !== -1) return ['Polar Night', 'Light Returns', 'Polar Day', 'Light Fades'][(idx - 1 + 4) % 4];
      
      // Check equatorial seasons
      if (lowerSeason.includes('wet') || lowerSeason.includes('dry')) {
        const eqOrder = ['dry (secondary)', 'wet (primary)', 'dry (primary)', 'wet (secondary)'];
        idx = eqOrder.findIndex(s => lowerSeason === s);
        if (idx !== -1) return ['Dry (Secondary)', 'Wet (Primary)', 'Dry (Primary)', 'Wet (Secondary)'][(idx - 1 + 4) % 4];
        // Simple wet/dry
        return lowerSeason.includes('wet') ? 'Dry' : 'Wet';
      }
      
      // Default to temperate
      const order = ['Winter', 'Spring', 'Summer', 'Autumn'];
      idx = order.findIndex(s => s.toLowerCase() === lowerSeason);
      return order[(idx - 1 + 4) % 4];
    },
    
    // Get season data for a given hemisphere (handles polar, equatorial, and temperate)
    getSeasonData: (seasonName, hemisphere = 'northern', climateZone = null) => {
      if (!seasonName) return null;
      const lowerSeason = seasonName.toLowerCase();
      const isSouthern = hemisphere?.toLowerCase() === 'south' || hemisphere?.toLowerCase() === 'southern';
      
      // Check if it's a polar season
      const polarSeasons = ['polar night', 'light returns', 'polar day', 'light fades'];
      if (polarSeasons.some(ps => lowerSeason.includes(ps) || ps.includes(lowerSeason)) || climateZone === 'polar') {
        const polarHemi = isSouthern ? 'polar_southern' : 'polar_northern';
        // Try exact match first, then partial match
        const polarData = TIMING_UTILITIES.SEASON_MARKERS[polarHemi]?.[seasonName];
        if (polarData) return polarData;
        // Try finding by partial match (e.g., "Polar Twilight (Emerging)" -> "Light Returns")
        for (const [key, data] of Object.entries(TIMING_UTILITIES.SEASON_MARKERS[polarHemi] || {})) {
          if (lowerSeason.includes(key.toLowerCase())) return data;
        }
      }
      
      // Check if it's an equatorial season
      const equatorialSeasons = ['wet', 'dry'];
      if (equatorialSeasons.some(es => lowerSeason.includes(es)) || climateZone === 'equatorial') {
        const eqData = TIMING_UTILITIES.SEASON_MARKERS.equatorial?.[seasonName];
        if (eqData) return eqData;
        // Try partial match for qualified seasons
        for (const [key, data] of Object.entries(TIMING_UTILITIES.SEASON_MARKERS.equatorial || {})) {
          if (lowerSeason === key.toLowerCase()) return data;
        }
      }
      
      // Default to temperate seasons
      const hemi = isSouthern ? 'southern' : 'northern';
      return TIMING_UTILITIES.SEASON_MARKERS[hemi]?.[seasonName] || null;
    },
    
    // Calculate season start date for a given year (handles polar, equatorial, and temperate)
    getSeasonStartDate: (seasonName, year, hemisphere = 'northern') => {
      if (!seasonName) return null;
      const lowerSeason = seasonName.toLowerCase();
      const isSouthern = hemisphere?.toLowerCase() === 'south' || hemisphere?.toLowerCase() === 'southern';
      
      // Check if it's a polar season
      const polarSeasons = ['polar night', 'light returns', 'polar day', 'light fades'];
      if (polarSeasons.some(ps => lowerSeason.includes(ps))) {
        const polarHemi = isSouthern ? 'polar_southern' : 'polar_northern';
        const seasonData = TIMING_UTILITIES.SEASON_MARKERS[polarHemi]?.[seasonName];
        if (seasonData) {
          return new Date(year, seasonData.startMonth - 1, seasonData.startDay);
        }
      }
      
      // Check if it's an equatorial season
      if (lowerSeason.includes('wet') || lowerSeason.includes('dry')) {
        const seasonData = TIMING_UTILITIES.SEASON_MARKERS.equatorial?.[seasonName];
        if (seasonData) {
          return new Date(year, seasonData.startMonth - 1, seasonData.startDay);
        }
      }
      
      // Default to temperate lookup
      const seasonData = TIMING_UTILITIES.SEASON_MARKERS.getSeasonData(seasonName, hemisphere);
      if (!seasonData) return null;
      return new Date(year, seasonData.startMonth - 1, seasonData.startDay);
    },
    
    // Calculate season end date for a given year (handles polar, equatorial, and temperate)
    getSeasonEndDate: (seasonName, year, hemisphere = 'northern') => {
      if (!seasonName) return null;
      const lowerSeason = seasonName.toLowerCase();
      const isSouthern = hemisphere?.toLowerCase() === 'south' || hemisphere?.toLowerCase() === 'southern';
      
      // Check if it's a polar season
      const polarSeasons = ['polar night', 'light returns', 'polar day', 'light fades'];
      if (polarSeasons.some(ps => lowerSeason.includes(ps))) {
        const polarHemi = isSouthern ? 'polar_southern' : 'polar_northern';
        const seasonData = TIMING_UTILITIES.SEASON_MARKERS[polarHemi]?.[seasonName];
        if (seasonData) {
          // Handle year wrap
          const endYear = seasonData.endMonth < seasonData.startMonth ? year + 1 : year;
          return new Date(endYear, seasonData.endMonth - 1, seasonData.endDay);
        }
      }
      
      // Check if it's an equatorial season
      if (lowerSeason.includes('wet') || lowerSeason.includes('dry')) {
        const seasonData = TIMING_UTILITIES.SEASON_MARKERS.equatorial?.[seasonName];
        if (seasonData) {
          // Handle year wrap (Dry Secondary crosses year boundary)
          const endYear = seasonData.endMonth < seasonData.startMonth ? year + 1 : year;
          return new Date(endYear, seasonData.endMonth - 1, seasonData.endDay);
        }
      }
      
      // Default to temperate lookup
      const seasonData = TIMING_UTILITIES.SEASON_MARKERS.getSeasonData(seasonName, hemisphere);
      if (!seasonData) return null;
      // Handle year wrap (Winter ends in next year)
      const endYear = seasonData.endMonth < seasonData.startMonth ? year + 1 : year;
      return new Date(endYear, seasonData.endMonth - 1, seasonData.endDay);
    }
  },
  
  /**
   * Calculate dual countdown for any cyclical element
   * @param {Date} currentDate - Current date
   * @param {Date} previousEventDate - Date of last occurrence
   * @param {Date} nextEventDate - Date of next occurrence
   * @returns {Object} { daysSince, daysUntil, progress, totalCycleDays }
   */
  getDualCountdown: (currentDate, previousEventDate, nextEventDate) => {
    const current = new Date(currentDate).getTime();
    const previous = new Date(previousEventDate).getTime();
    const next = new Date(nextEventDate).getTime();
    
    const msPerDay = 1000 * 60 * 60 * 24;
    const daysSince = Math.floor((current - previous) / msPerDay);
    const daysUntil = Math.ceil((next - current) / msPerDay);
    const totalCycleDays = daysSince + daysUntil;
    const progress = totalCycleDays > 0 ? (daysSince / totalCycleDays) * 100 : 0;
    
    return { daysSince, daysUntil, progress, totalCycleDays };
  },
  
  /**
   * Format dual countdown for display
   * @param {Object} countdown - Result from getDualCountdown
   * @param {string} previousLabel - Label for previous event (e.g., "Yule")
   * @param {string} nextLabel - Label for next event (e.g., "Imbolc")
   * @returns {Object} Formatted strings for display
   */
  formatDualCountdown: (countdown, previousLabel, nextLabel) => {
    const { daysSince, daysUntil, progress } = countdown;
    return {
      sincePrevious: `${daysSince}d since ${previousLabel}`,
      untilNext: `${daysUntil}d until ${nextLabel}`,
      combined: `${daysSince}d ← ${previousLabel} | ${nextLabel} → ${daysUntil}d`,
      progress: Math.round(progress),
      progressBar: {
        value: progress,
        label: `${Math.round(progress)}% through cycle`
      }
    };
  },
  
  /**
   * Get lunar phase countdown (uses 29.53 day cycle)
   * @param {number} lunarAge - Current lunar age in days (0-29.53)
   * @param {string} currentPhase - Current phase name
   * @param {string} nextPhase - Next phase name
   */
  getLunarCountdown: (lunarAge, currentPhase, nextPhase) => {
    const cycleDays = TIMING_UTILITIES.LUNAR_CYCLE_DAYS;
    // Each of the 8 phases is ~3.69 days
    const phaseLength = cycleDays / 8;
    const daysSincePhaseStart = lunarAge % phaseLength;
    const daysUntilNextPhase = phaseLength - daysSincePhaseStart;
    
    return {
      daysSince: Math.floor(daysSincePhaseStart),
      daysUntil: Math.ceil(daysUntilNextPhase),
      progress: (daysSincePhaseStart / phaseLength) * 100,
      cycleDays,
      phaseLength: Math.round(phaseLength * 10) / 10,
      currentPhase,
      nextPhase
    };
  },
  
  /**
   * Get zodiac sign countdown
   * @param {number} longitude - Current longitude (0-360)
   * @param {string} currentSign - Current zodiac sign
   * @param {string} nextSign - Next zodiac sign
   */
  getZodiacCountdown: (longitude, currentSign, nextSign) => {
    const degreeInSign = longitude % 30;
    const degreesRemaining = 30 - degreeInSign;
    // Approximate days: ~1 degree per day for Sun, varies for other bodies
    const approxDaysInSign = degreeInSign; // Rough estimate
    const approxDaysRemaining = degreesRemaining;
    
    return {
      daysSince: Math.floor(approxDaysInSign),
      daysUntil: Math.ceil(approxDaysRemaining),
      degreeInSign: Math.round(degreeInSign * 10) / 10,
      degreesRemaining: Math.round(degreesRemaining * 10) / 10,
      progress: (degreeInSign / 30) * 100,
      currentSign,
      nextSign
    };
  },
  
  /**
   * Get season countdown - uses SEASON_MARKERS as source of truth
   * @param {Object} seasonData - Season data from backend
   * @param {Date} currentDate - Optional current date (defaults to now)
   */
  getSeasonCountdown: (seasonData, currentDate = new Date()) => {
    if (!seasonData) return null;
    
    const { days_into_season, days_remaining, season, next_season, location_info } = seasonData;
    const hemisphere = location_info?.hemisphere || 'northern';
    
    // Use backend data if available
    let daysSince = days_into_season;
    let daysUntil = days_remaining;
    
    // If backend data is incomplete, calculate from SEASON_MARKERS
    if (daysSince === undefined || daysUntil === undefined || daysSince === null || daysUntil === null) {
      const calculated = TIMING_UTILITIES.calculateSeasonProgress(season, currentDate, hemisphere);
      if (calculated) {
        daysSince = calculated.daysSince;
        daysUntil = calculated.daysUntil;
      }
    }
    
    // Ensure we have valid numbers
    daysSince = daysSince || 0;
    daysUntil = daysUntil || 0;
    const totalDays = daysSince + daysUntil;
    
    return {
      daysSince,
      daysUntil,
      progress: totalDays > 0 ? (daysSince / totalDays) * 100 : 0,
      totalCycleDays: totalDays,
      currentSeason: season,
      nextSeason: next_season || TIMING_UTILITIES.SEASON_MARKERS.getNextSeason(season),
      previousSeason: TIMING_UTILITIES.SEASON_MARKERS.getPreviousSeason(season),
      hemisphere
    };
  },
  
  /**
   * Calculate season progress from SEASON_MARKERS (client-side calculation)
   * @param {string} seasonName - Name of the season (Winter, Spring, Summer, Autumn)
   * @param {Date} currentDate - Current date
   * @param {string} hemisphere - 'northern' or 'southern'
   */
  calculateSeasonProgress: (seasonName, currentDate = new Date(), hemisphere = 'northern') => {
    if (!seasonName) return null;
    
    const markers = TIMING_UTILITIES.SEASON_MARKERS;
    const seasonData = markers.getSeasonData(seasonName, hemisphere);
    if (!seasonData) return null;
    
    const year = currentDate.getFullYear();
    const currentTime = currentDate.getTime();
    
    // Get season start date
    let startDate = markers.getSeasonStartDate(seasonName, year, hemisphere);
    
    // Handle Winter which might have started in previous year
    if (seasonName === 'Winter' && currentDate.getMonth() < 3) {
      startDate = markers.getSeasonStartDate(seasonName, year - 1, hemisphere);
    }
    
    // Get season end date
    let endDate = markers.getSeasonEndDate(seasonName, year, hemisphere);
    if (seasonName === 'Winter' && currentDate.getMonth() >= 9) {
      endDate = markers.getSeasonEndDate(seasonName, year + 1, hemisphere);
    }
    
    const msPerDay = 1000 * 60 * 60 * 24;
    const daysSince = Math.floor((currentTime - startDate.getTime()) / msPerDay);
    const daysUntil = Math.ceil((endDate.getTime() - currentTime) / msPerDay);
    const totalDays = daysSince + daysUntil;
    
    return {
      daysSince: Math.max(0, daysSince),
      daysUntil: Math.max(0, daysUntil),
      progress: totalDays > 0 ? (Math.max(0, daysSince) / totalDays) * 100 : 0,
      totalDays,
      startDate,
      endDate,
      startEvent: seasonData.description
    };
  },
  
  /**
   * Get sabbat countdown
   * @param {Array} sabbats - Array of sabbat data
   * @param {number} currentDayOfYear - Current day of year (1-365)
   */
  getSabbatCountdown: (sabbats, currentDayOfYear) => {
    if (!sabbats || sabbats.length === 0) return null;
    
    // Find previous and next sabbats
    const sortedByProximity = sabbats.map(s => ({
      ...s,
      daysUntil: (s.day_of_year - currentDayOfYear + 365) % 365,
      daysSince: (currentDayOfYear - s.day_of_year + 365) % 365
    })).sort((a, b) => a.daysUntil - b.daysUntil);
    
    const nextSabbat = sortedByProximity[0];
    const previousSabbat = sortedByProximity[sortedByProximity.length - 1];
    
    return {
      daysSince: previousSabbat?.daysSince || 0,
      daysUntil: nextSabbat?.daysUntil || 0,
      previousSabbat: previousSabbat?.name,
      nextSabbat: nextSabbat?.name,
      progress: previousSabbat && nextSabbat 
        ? (previousSabbat.daysSince / (previousSabbat.daysSince + nextSabbat.daysUntil)) * 100 
        : 0
    };
  }
};

// =============================================================================
// QUICK STATUS OVERLAY - Center Hub Click Response
// =============================================================================
// Configuration for the compact status overlay shown when clicking dial center

export const QUICK_STATUS_CONFIG = {
  // What to show in quick status
  displaySections: [
    {
      id: 'dominant_phase',
      label: 'Dominant Phase',
      priority: 1,
      showProgress: true
    },
    {
      id: 'lunar_status',
      label: 'Lunar',
      priority: 2,
      showDualCountdown: true,
      format: '{phase} in {sign}'
    },
    {
      id: 'solar_status', 
      label: 'Solar',
      priority: 3,
      showDualCountdown: true,
      format: '{season} • {sabbat}'
    },
    {
      id: 'active_aspects',
      label: 'Active Aspects',
      priority: 4,
      maxItems: 3
    },
    {
      id: 'retrogrades',
      label: 'Stations',
      priority: 5,
      showIfAny: true
    }
  ],
  
  // Styling
  style: {
    maxWidth: '320px',
    position: 'center',
    animation: 'scale-fade-in',
    backdropBlur: true
  },
  
  // Actions
  actions: {
    expandToFull: {
      label: 'Full Orientation',
      icon: 'expand',
      opensPanel: 'contextualOrientation'
    },
    close: {
      label: 'Close',
      icon: 'x'
    }
  }
};

// =============================================================================
// PHASE-AWARE UI STYLING HELPERS
// =============================================================================
// These helpers ensure all UI elements derive colors from CiH cosmology

/**
 * Get styled properties for a zodiac sign link/text
 * Returns inline style object for consistent archetype coloring
 */
export const getZodiacLinkStyle = (sign) => {
  const phase = getZodiacCihPhase(sign);
  const color = getCihPhaseColor(phase);
  return {
    color,
    cursor: 'pointer',
    textDecoration: 'underline',
    textDecorationColor: `${color}50`,
    textUnderlineOffset: '2px'
  };
};

/**
 * Get styled properties for a lunar phase link/text
 */
export const getLunarPhaseLinkStyle = (phaseName) => {
  const phase = getLunarCihPhase(phaseName);
  const color = getCihPhaseColor(phase);
  return {
    color,
    cursor: 'pointer',
    textDecoration: 'underline',
    textDecorationColor: `${color}50`,
    textUnderlineOffset: '2px'
  };
};

/**
 * Get styled properties for activity tags based on current dominant phase
 * Supported activities use the phase color
 * Less supported activities use the "opposite" phase (NOURISH↔FULFILL, ACTIVATE↔INTEGRATE)
 */
export const getActivityTagStyle = (isSupported, dominantPhase = 'ACTIVATE') => {
  // Supported activities align with the dominant phase
  // Less supported = what the phase doesn't favor
  const phaseOpposites = {
    NOURISH: 'FULFILL',
    ACTIVATE: 'INTEGRATE',
    FULFILL: 'NOURISH',
    INTEGRATE: 'ACTIVATE'
  };
  
  const phase = isSupported ? dominantPhase : phaseOpposites[dominantPhase] || 'INTEGRATE';
  const color = getCihPhaseColor(phase);
  
  return {
    color: isSupported ? color : `${color}99`, // Slightly muted for "less supported"
    backgroundColor: `${color}15`,
    borderColor: `${color}30`,
    borderWidth: '1px',
    borderStyle: 'solid',
    borderRadius: '4px',
    padding: '4px 8px',
    fontSize: '0.75rem'
  };
};

/**
 * Get section header style for activity sections
 */
export const getActivitySectionStyle = (isSupported, dominantPhase = 'ACTIVATE') => {
  const phaseOpposites = {
    NOURISH: 'FULFILL',
    ACTIVATE: 'INTEGRATE',
    FULFILL: 'NOURISH',
    INTEGRATE: 'ACTIVATE'
  };
  
  const phase = isSupported ? dominantPhase : phaseOpposites[dominantPhase] || 'INTEGRATE';
  const color = getCihPhaseColor(phase);
  
  return {
    color,
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginBottom: '0.5rem'
  };
};

/**
 * Get phase badge style (background + text color)
 */
export const getPhaseBadgeStyle = (phase) => {
  const color = getCihPhaseColor(phase);
  return {
    backgroundColor: `${color}30`,
    color: color,
    padding: '2px 12px',
    borderRadius: '4px',
    fontWeight: 'bold',
    fontSize: '0.75rem'
  };
};

// =============================================================================
// META-LEVEL STICKY HEADER PATTERN
// =============================================================================
// All overlay/panel sticky headers MUST use this pattern for legibility during scroll

/**
 * Get sticky header style - OPAQUE background for legibility during scroll
 * @param {string} accentPhase - Optional phase for accent border color
 * @param {boolean} isDark - Whether dark mode is active
 * @returns {object} Style object for sticky header
 */
export const getStickyHeaderStyle = (accentPhase = null, isDark = true) => {
  const accentColor = accentPhase ? getCihPhaseColor(accentPhase) : '#C5A059'; // Default to brass
  
  return {
    position: 'sticky',
    top: 0,
    zIndex: 20,
    padding: '1rem',
    // CRITICAL: Use OPAQUE background, never translucent
    backgroundColor: isDark ? '#0B0F19' : '#FFFEF9', // tod-bg-primary equivalent
    borderBottom: `1px solid ${accentColor}30`,
    // Backup for any transparency issues
    backdropFilter: 'blur(8px)',
    WebkitBackdropFilter: 'blur(8px)'
  };
};

/**
 * Get sticky header className string for Tailwind usage
 * @returns {string} Tailwind classes for sticky header
 */
export const getStickyHeaderClasses = () => {
  return 'sticky top-0 z-20 p-4 bg-tod-bg-primary border-b';
};

/**
 * Meta-level overlay container patterns
 * Use these for consistent overlay styling across all panels
 */
export const OVERLAY_PATTERNS = {
  // Standard overlay with phase-colored header
  standard: {
    container: 'absolute inset-0 z-10 overflow-y-auto',
    header: getStickyHeaderClasses(),
    content: 'p-4 space-y-4'
  },
  // Eclipse/threshold overlay with enhanced styling
  threshold: {
    container: 'absolute inset-0 z-10 overflow-y-auto',
    header: getStickyHeaderClasses() + ' border-tod-accent-purple/30',
    content: 'p-4 space-y-4'
  },
  // Glossary/reference overlay
  reference: {
    container: 'absolute inset-0 z-10 overflow-y-auto',
    header: getStickyHeaderClasses() + ' border-tod-brass/30',
    content: 'p-4 space-y-4'
  }
};

// =============================================================================
// META-LEVEL ELEMENT BUNDLES
// Group related elements for consistent co-display throughout the system
// =============================================================================

export const ELEMENT_BUNDLES = {
  // LUNAR BUNDLE: Moon phase + Moon sign always displayed together
  lunar: {
    name: 'Lunar Bundle',
    elements: ['moon_phase', 'moon_sign'],
    displayRule: 'Always show moon phase and moon sign together when either is referenced',
    format: {
      primary: 'phase',  // Show phase name first
      secondary: 'sign', // Then sign
      separator: ' in ', // "Waxing Crescent in Aquarius"
      includeTraditional: true, // Include traditional phase names
      includeCiH: true,  // Include CiH phase mapping
    },
    traditionalPhaseNames: {
      'new_moon': 'New Moon',
      'waxing_crescent': 'Waxing Crescent',
      'first_quarter': 'First Quarter',
      'waxing_gibbous': 'Waxing Gibbous',
      'full_moon': 'Full Moon',
      'waning_gibbous': 'Waning Gibbous',
      'last_quarter': 'Last Quarter (Third Quarter)',
      'waning_crescent': 'Waning Crescent (Balsamic)'
    },
    // DEPRECATED: Use CIH_COSMOLOGY.lunarPhaseMapping instead
    cihPhaseMapping: CIH_COSMOLOGY.lunarPhaseMapping
  },
  
  // SOLAR BUNDLE: Season + Sabbat + Sun's zodiac sign displayed together
  solar: {
    name: 'Solar Bundle',
    elements: ['season', 'sabbat', 'sun_sign'],
    displayRule: 'Show season, current/next sabbat, and sun sign together for solar context',
    format: {
      primary: 'season',    // "Winter"
      secondary: 'sabbat',  // "approaching Imbolc"
      tertiary: 'sun_sign', // "Sun in Aquarius"
      separator: ' • '
    },
    seasonSabbatPairs: {
      // Each season has a start sabbat and midpoint sabbat
      'Winter': { start: 'Yule', midpoint: 'Imbolc' },
      'Spring': { start: 'Ostara', midpoint: 'Beltane' },
      'Summer': { start: 'Litha', midpoint: 'Lughnasadh' },
      'Fall': { start: 'Mabon', midpoint: 'Samhain' },
      'Autumn': { start: 'Mabon', midpoint: 'Samhain' }
    },
    zodiacSeasonMapping: {
      // Sun signs that define each season
      'Capricorn': 'Winter',
      'Aquarius': 'Winter',
      'Pisces': 'Winter/Spring',
      'Aries': 'Spring',
      'Taurus': 'Spring',
      'Gemini': 'Spring/Summer',
      'Cancer': 'Summer',
      'Leo': 'Summer',
      'Virgo': 'Summer/Fall',
      'Libra': 'Fall',
      'Scorpio': 'Fall',
      'Sagittarius': 'Fall/Winter'
    },
    // DEPRECATED: Use CIH_COSMOLOGY.zodiacPhaseMapping instead
    zodiacPhaseMapping: CIH_COSMOLOGY.zodiacPhaseMapping
  }
};

// Helper function to format lunar bundle display
export const formatLunarBundle = (moonPhase, moonSign, illumination) => {
  const bundle = ELEMENT_BUNDLES.lunar;
  const traditionalName = bundle.traditionalPhaseNames[moonPhase?.toLowerCase().replace(' ', '_')] || moonPhase;
  const cihPhase = bundle.cihPhaseMapping[moonPhase?.toLowerCase().replace(' ', '_')] || 'NOURISH';
  
  return {
    display: `${traditionalName} in ${moonSign}`,
    traditional: traditionalName,
    cihPhase: cihPhase,
    sign: moonSign,
    illumination: illumination,
    fullContext: `${traditionalName} (${cihPhase}) in ${moonSign} at ${Math.round(illumination || 0)}%`
  };
};

// Helper function to format solar bundle display
export const formatSolarBundle = (season, sabbats, sunSign, daysToSabbat) => {
  const bundle = ELEMENT_BUNDLES.solar;
  const seasonData = bundle.seasonSabbatPairs[season] || {};
  
  // Find next sabbat
  const nextSabbat = sabbats?.find(s => s.daysUntil > 0 && s.daysUntil < 50);
  
  return {
    display: `${season} • ${sunSign}`,
    season: season,
    sunSign: sunSign,
    sabbat: nextSabbat?.name,
    sabbatStatus: nextSabbat ? `${nextSabbat.name} in ${nextSabbat.daysUntil}d` : null,
    seasonalContext: `${season} season with Sun in ${sunSign}`,
    fullContext: nextSabbat 
      ? `${season} • ${nextSabbat.name} (${nextSabbat.daysUntil}d) • Sun in ${sunSign}`
      : `${season} • Sun in ${sunSign}`
  };
};

// =============================================================================
// AMERICAN ENGLISH GRAMMAR & STYLE RULES
// For consistent, professional synthesis output
// =============================================================================

export const GRAMMAR_RULES = {
  // Serial comma (Oxford comma) - ALWAYS use
  serialComma: true,
  
  // Punctuation
  punctuation: {
    // Single space after periods
    singleSpaceAfterPeriod: true,
    // Em dash (—) with no spaces around it
    emDash: '—',
    // En dash (–) for ranges
    enDash: '–',
    // Ellipsis character, not three periods
    ellipsis: '…'
  },
  
  // Number formatting
  numbers: {
    // Spell out one through nine, use digits for 10+
    spellOutUnderTen: true,
    // Always use digits for measurements and coordinates
    digitsForMeasurements: true,
    // Degree symbol with no space: "29.8°"
    degreeFormat: '{value}°',
    // Decimal precision for positions
    positionPrecision: 1,
    // Decimal precision for orbs
    orbPrecision: 1
  },
  
  // Time and date formatting (American English)
  dateTime: {
    // Month Day, Year (January 20, 2026)
    fullDateFormat: { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' },
    // Mon DD, YYYY for compact
    shortDateFormat: { month: 'short', day: 'numeric', year: 'numeric' },
    // 12-hour time with AM/PM
    timeFormat: { hour: 'numeric', minute: '2-digit', hour12: true },
    // Include time zone when relevant
    includeTimezone: true
  },
  
  // Sentence structure rules for synthesis
  sentenceRules: {
    // Start sentences with capital letter
    capitalizeFirstWord: true,
    // End sentences with proper punctuation
    endWithPunctuation: true,
    // Avoid sentence fragments in body text
    avoidFragments: true,
    // Use active voice when possible
    preferActiveVoice: true
  },
  
  // CiH Voice rules (non-prescriptive tone)
  cihVoice: {
    // Words to USE (supportive, observational)
    preferred: [
      'tends to support',
      'may feel',
      'supports',
      'availability for',
      'conditions favor',
      'this period emphasizes',
      'the climate suggests',
      'there is potential for'
    ],
    // Words to AVOID (prescriptive, judgmental)
    avoid: [
      'you should',
      'you must',
      'do this',
      'don\'t do',
      'good',
      'bad',
      'right',
      'wrong',
      'optimize',
      'best',
      'worst'
    ],
    // Tone descriptors
    tone: ['calm', 'precise', 'grounded', 'non-prescriptive', 'observational']
  },
  
  // List formatting
  lists: {
    // Use serial comma before "and" in lists
    serialComma: true,
    // Capitalize first word of each list item
    capitalizeItems: true,
    // Period at end of complete sentences, no period for fragments
    punctuateCompleteSentences: true
  }
};

// =============================================================================
// OPERATIONAL PREFERENCES - System Behavior & Storage
// =============================================================================

export const OPERATIONAL_PREFERENCES = {
  // Sidebar Button Styling - Standardized across all buttons
  sidebarButtonStyle: {
    // Base classes for ALL sidebar buttons (ensures consistency)
    base: 'bg-[#050814]/90 backdrop-blur-xl border-2 px-2 sm:px-4 py-1.5 sm:py-2 font-bold uppercase tracking-widest transition-all duration-300 flex items-center justify-start gap-2 text-xs sm:text-sm w-full',
    // Icon size classes
    iconSize: 'w-4 h-4 sm:w-5 sm:h-5',
    // Label visibility (md breakpoint = 768px+)
    labelClass: 'hidden md:inline',
  },
  
  // Sidebar Button Labels - Single source of truth for all sidebar buttons
  // Desktop shows labels, mobile shows icons only
  sidebarButtons: {
    // Primary navigation buttons (always visible)
    selectDate: {
      label: 'Select Date',
      shortLabel: 'Date',
      icon: 'calendar',
      color: '#C5A059'
    },
    location: {
      label: 'Location',
      shortLabel: 'Location',
      icon: 'map-pin',
      color: '#C5A059'
    },
    orientation: {
      label: 'Orientation',
      shortLabel: 'Orient',
      icon: 'info',
      color: '#C5A059'
    },
    compare: {
      label: 'Compare',
      shortLabel: 'Compare',
      labelActive: 'Exit',
      icon: 'spiral',
      color: '#9370DB'
    },
    synthesis: {
      label: 'Synthesis',
      shortLabel: 'Synth',
      icon: 'layers',
      color: '#9370DB'
    },
    
    // Secondary action buttons
    save: {
      label: 'Save',
      shortLabel: 'Save',
      icon: 'save',
      color: '#7ED321',
      requiresAuth: true
    },
    history: {
      label: 'Storage',
      shortLabel: 'Store',
      icon: 'history',
      color: '#4A90D9',
      requiresAuth: true
    },
    share: {
      label: 'Share',
      shortLabel: 'Share',
      icon: 'share-2',
      color: '#9370DB'
    },
    tutorial: {
      label: 'Tutorial',
      shortLabel: 'Help',
      icon: 'help-circle',
      color: '#C5A059'
    },
    profile: {
      label: 'Profile',
      shortLabel: 'Me',
      icon: 'user',
      color: '#7ED321',
      requiresAuth: true
    }
  },
  
  // User menu items
  userMenu: {
    viewOriginalOrientation: {
      label: 'View My Original Orientation',
      icon: 'star',
      color: '#FFD700'
    },
    manageProfiles: {
      label: 'Manage Profiles',
      icon: 'users',
      color: '#C5A059'
    },
    addOns: {
      label: 'Add-Ons & Storage',
      icon: 'package',
      color: '#FFD700'
    },
    interfaceSettings: {
      label: 'Interface Settings',
      icon: 'settings',
      color: '#9370DB'
    },
    changePassword: {
      label: 'Change Password',
      icon: 'lock',
      color: '#94A3B8'
    },
    signOut: {
      label: 'Sign Out',
      icon: 'log-out',
      color: '#EF4444'
    }
  },
  
  // Profile management
  // IMPORTANT: Natal profile is ALWAYS FREE and separate from profile slots
  // The 3 free slots are for ADDITIONAL profiles (family, friends, events)
  profileCredits: {
    natalProfileFree: true,   // User's own birth chart - never counts against slots
    freeSlots: 1,             // Natal profile slot (always free, separate)
    defaultPaidSlots: 3,      // ADDITIONAL profile slots included with registration
    maxSlots: 10,             // Maximum additional slots with credits
    purchaseUrl: 'https://onescepress.com/shop/dial/',
    creditBundles: [
      { slots: 3, price: '$4.99', description: '3 additional profile slots' },
      { slots: 5, price: '$7.99', description: '5 additional profile slots' },
      { slots: 10, price: '$12.99', description: '10 additional profile slots' }
    ],
    slotCountingRules: {
      natalProfile: 'Does NOT count against slots - always free',
      additionalProfiles: 'Count against the 3 free slots (or purchased slots)',
      displayFormat: 'Show "X of 3 additional slots" + "(+1 Natal free)"'
    }
  },

  // Mobile Responsive Panel Rules
  // These rules govern how panels behave on different screen sizes
  mobileResponsive: {
    breakpoints: {
      mobile: 640,      // < 640px = mobile
      tablet: 768,      // 640-768px = tablet
      desktop: 1024     // > 1024px = desktop
    },
    
    // Z-index layering for proper panel stacking
    zIndex: {
      base: 10,              // Base UI elements
      panel: 40,             // Date/Time, Location panels
      contextualOrientation: 45,  // Contextual orientation panel
      modal: 50,             // Auth modal, confirmation dialogs
      toast: 60,             // Toast notifications
      tooltip: 70            // Tooltips
    },
    
    // Panel behavior rules
    panels: {
      // Date/Time picker panel
      dateTimePicker: {
        mobileFullScreen: true,
        maxHeightMobile: '90vh',
        maxHeightDesktop: 'auto',
        scrollBehavior: 'smooth',
        touchFriendly: true,
        zIndex: 40,
        // Close panel before opening auth modal (for ProfilePrompt)
        closeOnAuthOpen: true
      },
      
      // Location controller panel  
      locationController: {
        mobileFullScreen: true,
        maxHeightMobile: '90vh',
        maxHeightDesktop: 'auto',
        scrollBehavior: 'smooth',
        touchFriendly: true,
        zIndex: 40,
        closeOnAuthOpen: true
      },
      
      // Export/Share panel
      exportShare: {
        mobileFullScreen: false,
        maxHeightMobile: '85vh',
        maxHeightDesktop: 'auto',
        zIndex: 40
      },
      
      // Contextual Orientation panel (slide-in from right)
      contextualOrientation: {
        mobileFullScreen: true,
        maxHeightMobile: '100vh',
        maxHeightDesktop: 'auto',
        scrollBehavior: 'smooth',
        touchFriendly: true,
        zIndex: 45,
        // Panel width on desktop
        desktopWidth: 'w-96'
      },
      
      // Profile Manager modal
      profileManager: {
        mobileFullScreen: true,
        maxHeightMobile: '100vh',
        maxHeightDesktop: '90vh',
        scrollBehavior: 'smooth',
        touchFriendly: true,
        zIndex: 50
      },
      
      // Auth modal - highest priority
      authModal: {
        mobileFullScreen: true,
        maxHeightMobile: '100vh',
        maxHeightDesktop: 'auto',
        zIndex: 50,
        backdropBlur: true
      },
      
      // Glossary panel (slide-in)
      glossaryPanel: {
        mobileFullScreen: true,
        maxHeightMobile: '100vh',
        maxHeightDesktop: 'auto',
        scrollBehavior: 'smooth',
        touchFriendly: true,
        zIndex: 60  // Higher than other panels to appear on top when opened
      },
      
      // Search History panel
      searchHistory: {
        mobileFullScreen: true,
        maxHeightMobile: '90vh',
        maxHeightDesktop: '80vh',
        scrollBehavior: 'smooth',
        touchFriendly: true,
        zIndex: 45
      },
      
      // Universal Info Panel (dial click overlays)
      universalInfoPanel: {
        mobileFullScreen: true,
        maxHeightMobile: '90vh',
        maxHeightDesktop: 'auto',
        scrollBehavior: 'smooth',
        touchFriendly: true,
        zIndex: 50
      },
      
      // Comparison panels (CA mode)
      comparisonPanel: {
        mobileFullScreen: true,
        maxHeightMobile: '100vh',
        maxHeightDesktop: 'auto',
        scrollBehavior: 'smooth',
        touchFriendly: true,
        zIndex: 45
      },
      
      // Onboarding/Tutorial
      onboarding: {
        mobileFullScreen: false,
        maxHeightMobile: '85vh',
        maxHeightDesktop: 'auto',
        zIndex: 102  // Above everything
      },
      
      // Profile prompts - must be visible on all sizes
      profilePrompt: {
        alwaysVisible: true,
        minTouchTarget: 44, // 44px minimum for touch
        variant: {
          mobile: 'banner',   // Use banner on mobile
          desktop: 'overlay'  // Use overlay on desktop
        },
        // Action behavior
        onClickBehavior: 'closeParentThenOpenAuth'
      }
    },
    
    // Button sizing for touch targets
    buttons: {
      minHeight: 44,        // iOS minimum touch target
      minWidth: 44,
      paddingMobile: '12px 16px',
      paddingDesktop: '8px 16px'
    },
    
    // Text scaling - user-adjustable accessibility feature
    text: {
      // Headers scale down on mobile
      headerScale: {
        mobile: 0.85,
        tablet: 0.95,
        desktop: 1
      }
    }
  },
  
  // =============================================================================
  // TEXT SCALE ACCESSIBILITY SYSTEM
  // =============================================================================
  // User-adjustable text scaling for accessibility and readability
  // All text sizes should be relative (rem) to allow scaling
  textScaleSystem: {
    // Storage key for user preference
    storageKey: 'tod_text_scale',
    
    // Scale range (multiplier applied to base font size)
    min: 0.75,      // 75% - Compact
    max: 1.5,       // 150% - Large
    default: 1.0,   // 100% - Default
    step: 0.05,     // 5% increments
    
    // Named presets for quick selection
    presets: {
      compact: { value: 0.85, label: 'Compact', description: 'More content visible' },
      default: { value: 1.0, label: 'Default', description: 'Standard text size' },
      comfortable: { value: 1.15, label: 'Comfortable', description: 'Easier to read' },
      large: { value: 1.3, label: 'Large', description: 'Enhanced readability' },
      extraLarge: { value: 1.5, label: 'Extra Large', description: 'Maximum readability' }
    },
    
    // Base font sizes (in rem) - these get multiplied by scale
    baseSizes: {
      // Text hierarchy
      h1: 2.25,        // 36px at 16px base
      h2: 1.5,         // 24px
      h3: 1.25,        // 20px
      h4: 1.125,       // 18px
      body: 1,         // 16px
      small: 0.875,    // 14px
      xs: 0.75,        // 12px
      xxs: 0.625,      // 10px
      
      // Component-specific
      buttonText: 0.875,
      inputText: 1,
      labelText: 0.75,
      tooltipText: 0.75,
      badgeText: 0.625,
      
      // Panel headers
      panelHeader: 1.125,
      sectionHeader: 0.75,
      overlayHeader: 1.25
    },
    
    // Line height adjustments at different scales
    lineHeightScale: {
      compact: 1.4,      // Tighter for small text
      default: 1.5,      // Standard
      large: 1.6         // More spacing for large text
    },
    
    // Container adjustments - padding/margin scale factors
    containerScale: {
      padding: 1.0,      // Padding scales 1:1 with text
      margin: 0.8,       // Margin scales slightly less
      gap: 0.9           // Gap scales moderately
    },
    
    // Accessibility labels
    a11yLabels: {
      slider: 'Adjust text size for better readability',
      presetCompact: 'Compact text - shows more content',
      presetDefault: 'Default text size',
      presetLarge: 'Large text - easier to read',
      currentScale: 'Current text scale'
    }
  },
  
  // =============================================================================
  // CONTAINER BEHAVIOR SYSTEM
  // =============================================================================
  // Standardized container positioning, anchoring, and pinning behavior
  containerSystem: {
    storageKey: 'tod_container_prefs',
    
    // Default positioning - all containers anchor to screen center
    positioning: {
      anchor: 'center',           // 'center' | 'top' | 'bottom'
      alignItems: 'center',
      justifyContent: 'center',
      // Offset from anchor point
      offsetY: 0,
      offsetX: 0
    },
    
    // Animation presets for container transitions
    animations: {
      enter: {
        opacity: { from: 0, to: 1 },
        scale: { from: 0.95, to: 1 },
        y: { from: 20, to: 0 },
        duration: 0.3,
        ease: 'easeOut'
      },
      exit: {
        opacity: { from: 1, to: 0 },
        scale: { from: 1, to: 0.95 },
        y: { from: 0, to: 20 },
        duration: 0.2,
        ease: 'easeIn'
      },
      slideIn: {
        x: { from: '100%', to: 0 },
        duration: 0.4,
        ease: 'spring',
        springConfig: { stiffness: 100, damping: 20 }
      }
    },
    
    // Overlay stacking behavior
    overlayBehavior: {
      // When a new overlay opens over an existing one
      stackMode: 'push',          // 'push' (stack) | 'replace' (close previous)
      maxStackDepth: 3,           // Maximum nested overlays
      dimPreviousOverlay: true,   // Dim background overlays
      dimOpacity: 0.3
    },
    
    // Pinnable/Sticky container settings
    pinning: {
      enabled: true,
      storageKey: 'tod_pinned_containers',
      
      // Containers that support pinning
      pinnableContainers: [
        'contextualOrientation',    // CO panel
        'glossaryPanel',            // Reference panel
        'synthesisStudy',           // Future: Study mode
        'personalNotes',            // Future: My Notes
        'attunementCalculator'      // Future: Personal calculator
      ],
      
      // Pin position options
      positions: {
        left: { side: 'left', width: '400px', offset: 0 },
        right: { side: 'right', width: '400px', offset: 0 },
        bottomLeft: { side: 'bottom-left', width: '350px', height: '50vh' },
        bottomRight: { side: 'bottom-right', width: '350px', height: '50vh' }
      },
      
      // Pin button styling
      pinButton: {
        icon: 'Pin',              // Lucide icon
        iconPinned: 'PinOff',     // When pinned
        tooltip: 'Pin this panel',
        tooltipPinned: 'Unpin this panel'
      }
    },
    
    // Container-level tutorial access (CyKL)
    tutorialAccess: {
      enabled: true,
      symbol: '🐉',               // Dragon symbol
      position: 'header-right',   // Where to show the dragon
      tooltip: 'Ask CyKL for help',
      // Containers that show CyKL access - ALL containers have access per user requirement
      showIn: [
        'dateTimePicker',
        'locationController', 
        'contextualOrientation',
        'profileManager',
        'glossaryPanel',
        'comparativeAnalysis',
        'comparativeAnalysisSetup',
        'comparisonSynthesisPanel',
        'exportShare',
        'searchHistory',
        'universalInfoPanel',
        'onboardingTutorial',
        'phaseInfoPanel',
        'quickStatus',
        'deepDive',
        'modulationDetailPanel',
        'multiwaySynthesis',
        'timeLapseSynthesis'
      ]
    }
  },
  
  // =============================================================================
  // CyKL TUTORIAL AGENT SYSTEM
  // =============================================================================
  // CyKL = Cy (CiH) Knowledgebase Legend
  // Interactive tutorial guide and knowledge assistant
  // 
  // ARCHITECTURE NOTE: CyKL follows the same meta -> flow pattern as:
  //   - GLOSSARY system (glossaryFAQ.js -> GlossaryTerm component -> UI)
  //   - SYNTHESIS system (cihSynthesis.js -> SynthesisText component -> UI)
  // Pattern: Meta-bundle config -> Hook/Component layer -> UI rendering
  //
  // BRAND MASCOT NOTE: CyKL functions as the brand mascot for The Onescent Dial,
  // similar to:
  //   - Duo (Duolingo) - Friendly owl that guides language learning
  //   - Finch (Finch app) - Bird companion for self-care
  //   - Clippy (Microsoft) - Assistant character (historical reference)
  // CyKL will have comprehensive character integration with Onescent Dial branding
  // during the dedicated design/configuration phase.
  //
  cyklSystem: {
    // ==========================================================================
    // IDENTITY & BRAND PRESENCE
    // ==========================================================================
    identity: {
      name: 'CyKL',
      fullName: 'Cy Knowledgebase Legend',
      pronunciation: 'sai-kul',
      symbol: '🐉',
      
      // Brand mascot classification
      mascotType: 'guide_companion',
      brandRole: 'primary_mascot',
      
      // Personality traits for consistent voice (mirrors SYNTHESIS voice rules)
      personality: {
        tone: 'warm, wise, encouraging',
        style: 'patient, metaphor-rich, grounded',
        approach: 'guides discovery rather than dictates answers',
        // Voice constraints (parallel to SYNTHESIS voice rules)
        voiceRules: {
          use: ['tends to', 'may', 'supports', 'invites'],
          avoid: ['must', 'should', 'will', 'always'],
          principle: 'descriptive, never prescriptive'
        }
      },
      
      // Character background (for consistent storytelling)
      lore: {
        origin: 'Ancient keeper of cyclical wisdom',
        role: 'Guide through the harmonies of time and space',
        traits: ['patient', 'curious', 'playful', 'wise']
      }
    },
    
    // ==========================================================================
    // IDENTITY RENDERING STANDARDS
    // ==========================================================================
    // Defines how CyKL appears across all states and contexts
    renderingStandards: {
      // Size variants (matches responsive text scale)
      sizes: {
        micro: { width: 16, height: 16, use: 'inline hints' },
        small: { width: 24, height: 24, use: 'button icons' },
        medium: { width: 48, height: 48, use: 'panel headers' },
        large: { width: 96, height: 96, use: 'tutorial screens' },
        hero: { width: 200, height: 200, use: 'welcome/onboarding' }
      },
      
      // Appearance states
      states: {
        idle: {
          animation: 'float',
          expression: 'neutral_friendly',
          glowIntensity: 0.3
        },
        speaking: {
          animation: 'gentle_bob',
          expression: 'engaged',
          glowIntensity: 0.5,
          mouthAnimation: true
        },
        thinking: {
          animation: 'slow_orbit',
          expression: 'contemplative',
          glowIntensity: 0.4,
          sparkles: true
        },
        celebrating: {
          animation: 'spiral_dance',
          expression: 'joyful',
          glowIntensity: 0.8,
          particles: 'confetti'
        },
        guiding: {
          animation: 'point_gesture',
          expression: 'encouraging',
          glowIntensity: 0.6,
          trailEffect: true
        },
        sleeping: {
          animation: 'gentle_breathe',
          expression: 'peaceful',
          glowIntensity: 0.1,
          zzzParticles: true
        },
        error: {
          animation: 'concerned_shake',
          expression: 'sympathetic',
          glowIntensity: 0.4
        }
      },
      
      // Color palette (integrates with theme system)
      // VISUAL IDENTITY: "Western dragon meets Quetzalcoatl"
      // - Horns/spikes, scale details, four main proportionate limbs, two large wings, long tail
      // - "Jeweled"/etheric divine quality with feather-like plumages around head/neck, ankles, tail tip
      colors: {
        primary: '#FFD700',       // Gold - matches CiH gold (base accent)
        secondary: '#9370DB',     // Purple - mystical accent
        glow: 'rgba(255, 215, 0, 0.5)',
        
        // Theme-responsive color scheme
        // Dark theme: Purple base with fuchsia/magenta highlights, teal/aqua luminescence
        darkTheme: {
          body: '#9370DB',              // Purple base
          bodyGradient: ['#9370DB', '#8B5CF6'], // Purple to violet gradient
          highlights: '#FF00FF',         // Fuchsia highlights
          highlightSecondary: '#C71585', // Magenta shadows
          luminescence: '#00F0FF',       // Teal/aqua scale reflection
          luminescenceAlt: '#40E0D0',    // Turquoise for movement
          scaleReflection: 'rgba(0, 240, 255, 0.3)', // Subtle teal glow on scales
          plumage: '#DDA0DD',            // Lavender for feather-like plumage
          shadow: 'rgba(139, 92, 246, 0.5)', // Purple shadow
          glow: 'rgba(0, 240, 255, 0.4)'    // Teal glow effect
        },
        
        // Light theme: INVERTED - Teal base with emerald/aqua highlights, purple/fuchsia luminescence
        lightTheme: {
          body: '#20B2AA',              // Light sea green/teal base
          bodyGradient: ['#20B2AA', '#3CB371'], // Teal to medium sea green gradient
          highlights: '#00CED1',         // Dark turquoise highlights
          highlightSecondary: '#2E8B57', // Sea green shadows (emerald family)
          luminescence: '#9370DB',       // Purple scale reflection
          luminescenceAlt: '#FF00FF',    // Fuchsia for movement
          scaleReflection: 'rgba(147, 112, 219, 0.3)', // Subtle purple glow on scales
          plumage: '#98FB98',            // Pale green for feather-like plumage
          shadow: 'rgba(32, 178, 170, 0.4)', // Teal shadow
          glow: 'rgba(147, 112, 219, 0.4)'  // Purple glow effect
        }
      },
      
      // Physical character design
      characterDesign: {
        style: 'Western dragon meets Quetzalcoatl',
        anatomical: {
          head: 'Horns/spikes, jeweled/etheric quality',
          neck: 'Feather-like plumage around neck',
          body: 'Scale details with divine shimmering quality',
          limbs: 'Four main proportionate limbs with plumage at ankles',
          wings: 'Two large wings',
          tail: 'Long tail with feather-like plumage at tip'
        },
        aesthetic: {
          quality: 'Jeweled/etheric divine',
          texture: 'Scales with iridescent reflection',
          ornamentation: 'Feather-like plumages (head/neck, ankles, tail tip)',
          expression: 'Wise yet approachable'
        }
      },
      
      // Accessibility
      accessibility: {
        altText: 'CyKL, the dragon guide',
        ariaRole: 'img',
        reduceMotion: {
          animation: 'none',
          glowIntensity: 0.5
        }
      }
    },
    
    // Storage for tutorial state
    storageKeys: {
      tutorialProgress: 'tod_cykl_progress',
      conversationHistory: 'tod_cykl_history',
      userPreferences: 'tod_cykl_prefs',
      completedLessons: 'tod_cykl_completed'
    },
    
    // Animation system for CyKL
    animations: {
      // Dragon entrance animations
      entrance: {
        fly: { type: 'fly', duration: 1.5, path: 'arc-in' },
        glide: { type: 'glide', duration: 2, easing: 'smooth' },
        appear: { type: 'fade-scale', duration: 0.8 },
        spiral: { type: 'spiral-in', duration: 1.2 }
      },
      // Idle animations
      idle: {
        hover: { type: 'float', amplitude: 10, period: 3 },
        breathe: { type: 'scale-pulse', amplitude: 0.05, period: 4 }
      },
      // Transition animations
      transitions: {
        dissolve: { type: 'particle-dissolve', duration: 1 },
        circle: { type: 'orbit-circle', duration: 2 },
        transform: { type: 'morph', duration: 1.5 }
      },
      // Audio cues (placeholder paths)
      sounds: {
        entrance: '/sounds/cykl-entrance.mp3',
        speak: '/sounds/cykl-speak.mp3',
        success: '/sounds/cykl-success.mp3',
        exit: '/sounds/cykl-exit.mp3'
      }
    },
    
    // Tutorial modules CyKL can guide
    tutorialModules: {
      onboarding: {
        id: 'onboarding',
        name: 'Welcome Journey',
        description: 'First-time user introduction to The Onescent Dial',
        steps: 8,
        estimatedMinutes: 5
      },
      dialNavigation: {
        id: 'dial-navigation',
        name: 'Reading the Dial',
        description: 'Understanding the visual elements and how to interact',
        steps: 6,
        estimatedMinutes: 4
      },
      synthesisUnderstanding: {
        id: 'synthesis',
        name: 'Understanding Synthesis',
        description: 'How CiH interprets and synthesizes cosmic data',
        steps: 10,
        estimatedMinutes: 8
      },
      profileSetup: {
        id: 'profiles',
        name: 'Personal Profiles',
        description: 'Setting up natal charts and managing profiles',
        steps: 5,
        estimatedMinutes: 3
      },
      comparativeAnalysis: {
        id: 'comparison',
        name: 'Comparative Analysis',
        description: 'Comparing charts and understanding interactions',
        steps: 7,
        estimatedMinutes: 6
      },
      advancedFeatures: {
        id: 'advanced',
        name: 'Advanced Features',
        description: 'Space weather, exports, and deep customization',
        steps: 8,
        estimatedMinutes: 7
      }
    },
    
    // CyKL capabilities beyond tutorial
    capabilities: {
      // Answer user questions
      questionAnswering: {
        enabled: true,
        domains: ['synthesis', 'astrology', 'cih-philosophy', 'app-usage']
      },
      // Explain synthesis interpretations
      synthesisExplanation: {
        enabled: true,
        depth: ['summary', 'detailed', 'philosophical']
      },
      // Guide calculator usage (future)
      calculatorGuidance: {
        enabled: false,
        futureFeature: true
      },
      // Synthesis Study collaboration (future)
      studyMode: {
        enabled: false,
        futureFeature: true
      }
    },
    
    // Context-aware help triggers
    contextualHelp: {
      // What triggers CyKL to offer help
      triggers: [
        { context: 'first_time_panel', action: 'offer_intro' },
        { context: 'complex_synthesis', action: 'offer_explanation' },
        { context: 'user_idle_30s', action: 'gentle_prompt' },
        { context: 'error_state', action: 'offer_assistance' }
      ]
    }
  },
  
  // ==========================================================================
  // UPCOMING FEATURES LIST
  // Features planned for future releases
  // ==========================================================================
  upcomingFeatures: {
    orientationCalculator: {
      name: 'Orientation Calculator',
      description: 'Calculate and explore personal attunement patterns based on CiH principles.',
      status: 'planned',
      priority: 'high'
    },
    synthesisStudy: {
      name: 'Synthesis Study Mode',
      description: 'Deep-dive collaborative exploration of CiH synthesis interpretations.',
      status: 'planned',
      priority: 'medium'
    },
    personalNotes: {
      name: 'My Notes',
      description: 'Add personal annotations and reflections to your synthesis saves.',
      status: 'planned',
      priority: 'medium'
    },
    attunementCalculator: {
      name: 'Attunement Calculator',
      description: 'Personal phase timing calculator based on your natal orientation.',
      status: 'planned',
      priority: 'low'
    }
  },
  
  // Natal/Original Orientation Visual Indicators
  // Use star icon (⭐/★) consistently across all natal-related UI
  natalIndicators: {
    icon: '★',                          // Star symbol for consistency
    lucideIcon: 'Star',                 // Lucide React icon name
    color: '#FFD700',                   // Gold color for natal
    gradientFrom: '#C5A059',            // Gradient start
    gradientTo: '#9370DB',              // Gradient end (purple accent)
    
    // Where to apply natal indicators
    applyTo: [
      'originalOrientationTime',        // Date/Time panel preset
      'originalOrientationPlace',       // Location panel preset
      'natalProfileBadge',              // Profile list badge
      'comparisonChartLabel',           // CA panel chart labels
      'exportHeaderIfNatal'             // Export reports when viewing natal
    ],
    
    // Label patterns for natal references
    labels: {
      time: 'Original Orientation Time',
      place: 'Original Orientation Place',
      chart: 'Natal Chart',
      profile: 'My Profile'
    }
  },
  
  // Data Storage Strategy - Dual storage for online/offline access
  storage: {
    // MongoDB for persistent cloud storage (synced across devices)
    cloud: {
      provider: 'mongodb',
      syncEnabled: true,
      // What to store in cloud
      cloudData: [
        'userProfile',        // Account info, natal chart
        'savedProfiles',      // Other people's profiles (partner, child, etc.)
        'locationPresets',    // Saved location presets
        'timePresets',        // Saved time/date presets
        'comparisonPresets',  // Saved comparison configurations
        'synthesisStudies',   // Saved synthesis analysis
        'notes'               // User notes
      ]
    },
    // localStorage for offline access and quick retrieval
    local: {
      enabled: true,
      // What to cache locally
      localData: [
        'currentViewState',   // Current dial configuration
        'recentLocations',    // Recent location history
        'recentDates',        // Recent date selections
        'cachedCosmicData',   // Cached API responses
        'tutorialState',      // Tutorial completion state
        'uiPreferences'       // Theme, zoom, panel states
      ],
      // Sync strategy
      syncOnLoad: true,       // Pull from cloud on app load
      syncOnChange: true,     // Push to cloud on significant changes
      offlineFirst: true      // Use local cache when offline
    }
  },
  
  // Comparison Analysis Modes
  comparisonModes: {
    // Person × Time × Place framework
    personTimePlaceMatrix: true,
    
    // Available comparison types
    types: {
      // Single person, different times (same place)
      timeLapse: {
        enabled: true,
        label: 'Time Lapse',
        description: 'Compare same location across 3-8 different times',
        minSlots: 3,
        maxSlots: 8,
        defaultSlots: 4
      },
      // Single time, different places (same person)
      globalView: {
        enabled: true,
        label: 'Global View',
        description: 'Compare 3-8 global locations for a single timing event',
        minSlots: 3,
        maxSlots: 8,
        defaultSlots: 4
      },
      // Different persons, same time/place
      multiProfile: {
        enabled: true,
        label: 'Multi-Profile',
        description: 'Compare multiple profiles at same time and place',
        minSlots: 2,
        maxSlots: 4,
        defaultSlots: 2
      },
      // Natal vs Current
      natalComparison: {
        enabled: true,
        label: 'Original Orientation',
        description: 'Compare natal chart with current or selected time',
        legacyLabel: 'Natal × Current'  // Old label for reference
      }
    },
    
    // Cross-synthesis analysis
    crossSynthesis: {
      enabled: true,
      label: 'Cross-Synthesis',
      description: 'Analyze patterns across any combination of loaded charts',
      maxCombinations: 28  // C(8,2) = 28 possible pairs from 8 charts
    }
  },
  
  // User Presets Configuration
  presets: {
    // Location presets
    locations: {
      maxUserPresets: 10,
      includeCommonCities: true,
      commonCities: ['New York', 'Los Angeles', 'London', 'Tokyo', 'Sydney', 'Paris'],
      allowCustomLabels: true  // "Home", "Work", "Partner's Place"
    },
    // Time presets
    times: {
      maxUserPresets: 10,
      includeCommonTimes: true,
      commonTimes: ['Sunrise', 'Noon', 'Sunset', 'Midnight'],
      allowRecurring: true  // Weekly, monthly presets
    },
    // Profile presets
    profiles: {
      maxSavedProfiles: 20,
      categories: ['Self', 'Partner', 'Family', 'Friend', 'Client', 'Other'],
      requiredFields: ['name', 'birthDate', 'birthTime', 'birthLocation'],
      optionalFields: ['timezone', 'notes', 'category']
    }
  },
  
  // Panel Configuration
  panels: {
    // Which panels can be opened simultaneously
    simultaneousMax: 3,
    // Panel persistence
    rememberState: true,
    // Default panel widths
    defaultWidths: {
      compact: 280,
      standard: 360,
      expanded: 480,
      split: '50%'
    },
    // Panel navigation configuration (breadcrumbs + real-time context)
    navigation: {
      // Enable breadcrumb navigation system-wide
      enableBreadcrumbs: true,
      // Enable real-time time×place context in subheadings
      enableContextSubheading: true,
      // Panels that should show breadcrumbs when nested
      breadcrumbPanels: [
        'co',           // Contextual Orientation
        'ca',           // Comparative Analysis
        'glossary',     // Glossary/FAQ
        'storage',      // History & Storage
        'discInfo',     // Disc Information
        'profiles'      // Profile Manager
      ],
      // Panels that should show real-time time×place context
      contextPanels: [
        'co',           // Shows current time×place orientation
        'ca',           // Shows comparison time×place
        'discInfo',     // Shows dial state time×place
        'eclipse',      // Shows eclipse time×place×synthesis
        'synthesis'     // Shows synthesis time×place
      ],
      // Context subheading format pattern
      contextFormat: {
        separator: ' • ',
        includeCity: true,
        includeHemisphere: true,
        hemisphereFormat: 'short' // 'short' = (N/S), 'full' = (Northern/Southern)
      },
      // Link behavior preferences (meta-level source of truth)
      linkBehavior: {
        // Container-level links → open full synthesis panel
        containerLinks: {
          description: 'Complete synthesis of all mentioned aspects/elements',
          targets: ['temporalLayer', 'planet', 'zodiac', 'season', 'lunar_phase', 'sabbat', 'aspect', 'modulation'],
          action: 'openSynthesisPanel'
        },
        // Button/text-level links → open detail/explanation panels
        inlineLinks: {
          description: 'Phase/element/label explanation details and glossary/FAQ expansion',
          targets: ['phase', 'element', 'glossaryTerm', 'faqItem'],
          action: 'openDetailPanel'
        },
        // Badge links → contextual info only (no panel change)
        badgeLinks: {
          description: 'Visual indicators showing current state',
          targets: ['phaseBadge', 'retrograde', 'contextBadge'],
          action: 'showTooltip'
        }
      },
      // Phase Energy Labeling (Archetype vs Modified)
      phaseEnergyLabels: {
        archetype: {
          label: 'Archetype',
          description: 'True phase energy for solar/seasonal/lunar expressions pre-synthesis',
          applies: ['lunar', 'seasonal', 'solar_zodiac', 'lunar_zodiac'],
          usageNote: 'Display when showing the raw/unmodified phase from backend'
        },
        modified: {
          label: 'Modified Conditions',
          description: 'Phase energy that has been modified by synthesis logic',
          applies: ['dominant_phase', 'spatial_influence', 'converging_layers'],
          usageNote: 'Display when showing synthesized/combined phase output'
        }
      }
    }
  },
  
  // Tutorial Configuration
  tutorial: {
    // Show tutorial on first visit
    showOnFirstVisit: true,
    // Allow skipping
    allowSkip: true,
    // Remember completion
    rememberCompletion: true,
    // Re-show option in settings
    canRewatch: true
  }
};

// =============================================================================
// LOADING & STATUS STATES - Branded UI Messages
// =============================================================================
// Consistent, on-brand messaging for all loading, processing, and status states

export const LOADING_STATES = {
  // General loading states
  loading: {
    message: 'Calibrating Orientation...',
    subtext: 'Aligning celestial coordinates',
    icon: 'loader'
  },
  calculating: {
    message: 'Calculating Positions...',
    subtext: 'Processing astronomical data',
    icon: 'compass'
  },
  
  // Save/sync states
  saving: {
    message: 'Synchronizing Cycles...',
    subtext: 'Preserving your configuration',
    icon: 'save'
  },
  syncing: {
    message: 'Harmonizing Data...',
    subtext: 'Syncing with celestial database',
    icon: 'refresh-cw'
  },
  
  // Profile operations
  loadingProfiles: {
    message: 'Gathering Profiles...',
    subtext: 'Loading saved orientations',
    icon: 'users'
  },
  savingProfile: {
    message: 'Recording Natal Data...',
    subtext: 'Storing birth coordinates',
    icon: 'user-plus'
  },
  
  // Export states
  exporting: {
    message: 'Rendering Synthesis...',
    subtext: 'Creating your cosmic snapshot',
    icon: 'download'
  },
  generatingPdf: {
    message: 'Composing Report...',
    subtext: 'Formatting celestial analysis',
    icon: 'file-text'
  },
  
  // Error states (maintains "Temporal Disruption" branding)
  error: {
    message: 'Temporal Disruption',
    subtext: 'An unexpected alignment occurred',
    icon: 'alert-triangle',
    recoverable: true
  },
  networkError: {
    message: 'Signal Lost',
    subtext: 'Unable to reach celestial servers',
    icon: 'wifi-off',
    recoverable: true
  },
  
  // Success states
  success: {
    message: 'Celestial Alignment Complete',
    subtext: 'Operation successful',
    icon: 'check-circle'
  },
  saved: {
    message: 'Orientation Preserved',
    subtext: 'Your data has been stored',
    icon: 'check'
  },
  exported: {
    message: 'Synthesis Captured',
    subtext: 'File ready for download',
    icon: 'download-cloud'
  }
};

// Helper to get loading state with fallback
export const getLoadingState = (stateKey) => {
  return LOADING_STATES[stateKey] || LOADING_STATES.loading;
};

// =============================================================================
// TOOLTIPS - Branded Help & Information System
// =============================================================================
// Centralized tooltip content for consistent UX and on-brand messaging
// All tooltips follow CiH terminology and mystical-scientific tone

export const TOOLTIPS = {
  // -------------------------------------------------------------------------
  // DIAL & ORIENTATION
  // -------------------------------------------------------------------------
  dial: {
    title: 'The Dial',
    description: 'Your window into the celestial dance. The dial displays real-time planetary positions relative to your selected time and location.',
    learnMore: 'The outer ring shows zodiac houses, while inner elements track planetary bodies through their eternal cycles.'
  },
  orientation: {
    title: 'Orientation',
    description: 'Your cosmic perspective point—the specific moment and place from which you observe the celestial sphere.',
    learnMore: 'Every orientation is unique, shaped by time, location, and the ever-moving heavens.'
  },
  natalProfile: {
    title: 'Natal Profile',
    description: 'Your Original Orientation—the exact celestial configuration at your moment of birth.',
    learnMore: 'This serves as your personal baseline for all comparative analyses and interpretations.'
  },
  
  // -------------------------------------------------------------------------
  // COMPARATIVE ANALYSIS
  // -------------------------------------------------------------------------
  comparativeAnalysis: {
    title: 'Comparative Analysis',
    description: 'Compare two orientations side-by-side to discover harmonic patterns and dynamic tensions.',
    learnMore: 'Use this for synastry (comparing two people), transits (current sky vs. natal), or event planning.'
  },
  globalView: {
    title: 'Global View',
    description: 'Observe how the same moment appears from multiple locations around the world.',
    learnMore: 'Compare 3-8 locations simultaneously to see how geography shifts celestial perspectives.'
  },
  timeLapse: {
    title: 'Time Lapse',
    description: 'Watch how a single location transforms through time—hours, days, or years.',
    learnMore: 'Track planetary movements and discover significant transits across your chosen timeline.'
  },
  
  // -------------------------------------------------------------------------
  // CELESTIAL BODIES
  // -------------------------------------------------------------------------
  planets: {
    sun: {
      title: 'The Sun ☉',
      description: 'Core identity, vitality, and conscious will. The central luminary around which all else orbits.',
      keywords: 'Self, ego, life force, purpose'
    },
    moon: {
      title: 'The Moon ☽',
      description: 'Emotional nature, instincts, and the subconscious realm. Reflects inner needs and nurturing patterns.',
      keywords: 'Emotions, habits, intuition, comfort'
    },
    mercury: {
      title: 'Mercury ☿',
      description: 'Communication, intellect, and mental agility. Governs how we process and share information.',
      keywords: 'Mind, speech, learning, connections'
    },
    venus: {
      title: 'Venus ♀',
      description: 'Love, beauty, and values. Represents attraction, aesthetics, and what we cherish.',
      keywords: 'Love, art, pleasure, worth'
    },
    mars: {
      title: 'Mars ♂',
      description: 'Action, drive, and assertion. The warrior energy that propels us forward.',
      keywords: 'Energy, courage, desire, conflict'
    },
    jupiter: {
      title: 'Jupiter ♃',
      description: 'Expansion, wisdom, and fortune. The great benefic that brings growth and opportunity.',
      keywords: 'Growth, luck, philosophy, abundance'
    },
    saturn: {
      title: 'Saturn ♄',
      description: 'Structure, discipline, and mastery. The teacher that brings lessons through limitation.',
      keywords: 'Boundaries, time, authority, karma'
    },
    uranus: {
      title: 'Uranus ♅',
      description: 'Revolution, innovation, and awakening. The cosmic disruptor that shatters stagnation.',
      keywords: 'Change, freedom, genius, rebellion'
    },
    neptune: {
      title: 'Neptune ♆',
      description: 'Dreams, spirituality, and transcendence. The mystic waters of imagination and dissolution.',
      keywords: 'Intuition, illusion, compassion, art'
    },
    pluto: {
      title: 'Pluto ♇',
      description: 'Transformation, power, and regeneration. The phoenix force of death and rebirth.',
      keywords: 'Power, depth, renewal, secrets'
    }
  },
  
  // -------------------------------------------------------------------------
  // CIH PHASES
  // -------------------------------------------------------------------------
  phases: {
    NOURISH: {
      title: 'Nourish Phase',
      color: '#4A90E2',
      description: 'Time for rest, reflection, and inner cultivation. Energy flows inward.',
      guidance: 'Ideal for planning, meditation, and self-care practices.'
    },
    ACTIVATE: {
      title: 'Activate Phase',
      color: '#7ED321',
      description: 'Rising energy calls for initiative and new beginnings. Seeds planted now take root.',
      guidance: 'Perfect for starting projects, making connections, and building momentum.'
    },
    FULFILL: {
      title: 'Fulfill Phase',
      color: '#F5A623',
      description: 'Peak expression and manifestation. Energy reaches its zenith.',
      guidance: 'Optimal for completion, celebration, and sharing your work with the world.'
    },
    INTEGRATE: {
      title: 'Integrate Phase',
      color: '#9B59B6',
      description: 'Waning energy invites release and wisdom-gathering. Time to harvest lessons.',
      guidance: 'Best for editing, refining, and preparing for the next cycle.'
    }
  },
  
  // -------------------------------------------------------------------------
  // ASPECTS
  // -------------------------------------------------------------------------
  aspects: {
    conjunction: {
      title: 'Conjunction (0°)',
      symbol: '☌',
      description: 'Fusion of energies—planets merge their influences into a powerful, unified force.',
      nature: 'Variable (depends on planets involved)'
    },
    opposition: {
      title: 'Opposition (180°)',
      symbol: '☍',
      description: 'Polarity and awareness—planets face each other, creating dynamic tension and perspective.',
      nature: 'Challenging but illuminating'
    },
    trine: {
      title: 'Trine (120°)',
      symbol: '△',
      description: 'Harmony and flow—energies support each other effortlessly, creating natural talents.',
      nature: 'Benefic and supportive'
    },
    square: {
      title: 'Square (90°)',
      symbol: '□',
      description: 'Friction and growth—tension that demands action and creates transformation.',
      nature: 'Challenging but productive'
    },
    sextile: {
      title: 'Sextile (60°)',
      symbol: '⚹',
      description: 'Opportunity and cooperation—gentle support that requires some effort to activate.',
      nature: 'Mildly benefic'
    }
  },
  
  // -------------------------------------------------------------------------
  // FEATURES & UI ELEMENTS
  // -------------------------------------------------------------------------
  features: {
    quickCast: {
      title: 'Quick Cast',
      description: 'Instantly view any profile\'s natal orientation on the dial.',
      shortcut: 'Click the eye icon'
    },
    export: {
      title: 'Export & Share',
      description: 'Generate high-fidelity reports as PNG images or detailed PDFs.',
      formats: 'Mobile PNG, Print PDF, Study PDF'
    },
    history: {
      title: 'Download History',
      description: 'Access your recently generated exports for quick retrieval.',
      retention: 'Stored locally in your browser'
    },
    presets: {
      title: 'Quick Presets',
      description: 'One-click access to your natal time and place settings.',
      requires: 'Signed-in account with natal profile'
    },
    save: {
      title: 'Save Configuration',
      description: 'Store your current dial settings for later retrieval.',
      storage: 'Synced to your account when signed in'
    }
  },
  
  // -------------------------------------------------------------------------
  // PROFILE MANAGEMENT
  // -------------------------------------------------------------------------
  profiles: {
    manage: {
      title: 'Manage Profiles',
      description: 'View, add, edit, and organize profiles for yourself and others.',
      tip: 'Your Natal profile is free and permanent. Additional profiles are for comparative analysis.'
    },
    slots: {
      title: 'Profile Slots',
      description: 'Additional profiles let you store birth data for people you want to compare.',
      free: 'Your natal profile is always free and doesn\'t count against slots.'
    },
    credits: {
      title: 'Profile Credits',
      description: 'Purchase additional slots to store more profiles for comparative analysis.',
      bundles: 'Available in 3, 5, or 10 slot bundles'
    },
    relationship: {
      title: 'Relationship Type',
      description: 'Categorize profiles for easier organization and reference.',
      types: 'Partner, Family, Friend, Child, Colleague, Other'
    }
  },
  
  // -------------------------------------------------------------------------
  // ADD-ONS & CREDITS
  // -------------------------------------------------------------------------
  addOns: {
    overview: {
      title: 'Add-Ons & Storage',
      description: 'Purchase additional storage for profiles, dial states, and saved analyses.',
      policy: 'All add-ons extend your free allocation—they never replace it.'
    },
    starSpaces: {
      title: 'Star Spaces',
      description: 'Storage for saved dial views and synthesis snapshots.',
      free: 'Free tier: 10 dial views + 10 synthesis saves.',
      use: 'Save cosmic moments to revisit anytime'
    },
    presetTokens: {
      title: 'Preset Tokens',
      description: 'Storage for saved location and time presets.',
      free: 'Free tier: 1 location + 1 time preset.',
      use: 'Quick-access your favorite places and moments'
    },
    saveTokens: {
      title: 'Extra Save Tokens',
      description: 'Permanent storage for dial states, analyses, and synthesis reports.',
      free: '20 free save tokens included. Add-ons extend this.',
      use: 'Keep your favorite insights forever'
    },
    comboBundles: {
      title: 'Add-On Bundles',
      description: 'Get profiles, star spaces, and preset tokens together at a discount.',
      tip: 'Best value for active users'
    },
    insiderAccess: {
      title: 'The Onescent Dial: Insider Access',
      description: 'Unlimited storage for all classes and types within THE ONESCENT DIAL.',
      includes: 'Unlimited profiles, star spaces, preset tokens, plus priority support and early feature access.',
      note: 'Dial-specific add-on. Full Onesce membership available at the Onesce Store.'
    }
  },
  
  // -------------------------------------------------------------------------
  // PREFERENCES & SETTINGS
  // -------------------------------------------------------------------------
  preferences: {
    overview: {
      title: 'Interface Settings',
      description: 'Customize your dial experience with theme, highlighting, and data preferences.',
      tip: 'Settings sync across devices when signed in.'
    },
    theme: {
      title: 'Theme',
      description: 'Switch between Celestial Dark and Parchment Light visual modes.',
      dark: 'Celestial Dark—rich, immersive night-sky aesthetic',
      light: 'Parchment Light—high-contrast readability for day use'
    },
    synthesisHighlight: {
      title: 'Synthesis Highlighting',
      description: 'Automatically highlight CiH glossary terms in synthesis text.',
      tip: 'Hover highlighted terms to see definitions'
    },
    autoDelete: {
      title: 'Auto-Delete History',
      description: 'Automatically remove unsaved search history after a set period.',
      range: '24-48 hours',
      tip: 'Star important searches to keep them permanently'
    },
    dataManagement: {
      title: 'Data Management',
      description: 'Clear search history or reset preferences to defaults.',
      tip: 'Clearing history cannot be undone'
    }
  },
  
  // -------------------------------------------------------------------------
  // SEARCH HISTORY
  // -------------------------------------------------------------------------
  searchHistory: {
    overview: {
      title: 'Search History',
      description: 'Access your recent dial configurations and searches.',
      requires: 'Sign in to save history across sessions'
    },
    tiers: {
      title: 'Storage Tiers',
      description: 'Organize searches by retention period.',
      session: 'Session—cleared when browser closes',
      week: 'Week—7-day retention',
      month: 'Month—30-day retention',
      year: 'Year—365-day retention',
      starred: 'Starred—permanent until deleted'
    },
    starred: {
      title: 'Starred Items',
      description: 'Important searches saved permanently.',
      tip: 'Star a search to prevent auto-deletion'
    }
  },
  
  // -------------------------------------------------------------------------
  // SPACE WEATHER & ECLIPSES
  // -------------------------------------------------------------------------
  spaceWeather: {
    solarCycle: {
      title: 'Solar Cycle',
      description: 'The ~11-year cycle of solar activity affecting Earth\'s magnetic field and aurora.',
      current: 'Currently in Solar Cycle 25'
    },
    geomagneticActivity: {
      title: 'Geomagnetic Activity',
      description: 'Disturbances in Earth\'s magnetic field caused by solar wind and CMEs.',
      scale: 'Measured on Kp index (0-9)'
    },
    auroraForecast: {
      title: 'Aurora Forecast',
      description: 'Predicted visibility of northern/southern lights based on solar activity.',
      factors: 'Depends on location, Kp level, and cloud cover'
    }
  },
  eclipses: {
    solar: {
      title: 'Solar Eclipse',
      description: 'The Moon passes between Earth and Sun, creating a powerful new beginning portal.',
      significance: 'Major reset points—events set in motion can unfold over 6 months'
    },
    lunar: {
      title: 'Lunar Eclipse',
      description: 'Earth\'s shadow falls on the Moon, illuminating hidden truths and endings.',
      significance: 'Culmination points—what\'s been building reaches a climax'
    }
  },
  
  // -------------------------------------------------------------------------
  // GENERAL UI
  // -------------------------------------------------------------------------
  ui: {
    dateTime: {
      title: 'Date & Time Selection',
      description: 'Set the temporal coordinates for your orientation.',
      tip: 'Use presets for quick access to your natal time'
    },
    location: {
      title: 'Location Selection',
      description: 'Set the geographic coordinates for your orientation.',
      tip: 'Search any city or use current location'
    },
    layers: {
      title: 'Display Layers',
      description: 'Toggle visibility of different dial elements.',
      options: 'Planets, aspects, houses, and more'
    },
    zoom: {
      title: 'Zoom Controls',
      description: 'Adjust the dial view for detailed examination.',
      tip: 'Use + and - or scroll wheel'
    }
  }
};

// Helper to get tooltip with fallback
export const getTooltip = (path) => {
  const parts = path.split('.');
  let current = TOOLTIPS;
  
  for (const part of parts) {
    if (current && current[part]) {
      current = current[part];
    } else {
      return {
        title: 'Information',
        description: 'Additional details for this feature.'
      };
    }
  }
  
  return current;
};

// Tooltip positioning helper
export const TOOLTIP_POSITIONS = {
  top: 'bottom-full left-1/2 -translate-x-1/2 mb-2',
  bottom: 'top-full left-1/2 -translate-x-1/2 mt-2',
  left: 'right-full top-1/2 -translate-y-1/2 mr-2',
  right: 'left-full top-1/2 -translate-y-1/2 ml-2',
  topLeft: 'bottom-full right-0 mb-2',
  topRight: 'bottom-full left-0 mb-2',
  bottomLeft: 'top-full right-0 mt-2',
  bottomRight: 'top-full left-0 mt-2'
};

/**
 * Format a date/time for synthesis outputs
 * Uses actual retrieval time, never "Live"
 * @param {Date|string|null} date - Date to format, null means current time
 * @param {string} format - 'full', 'short', 'datetime', 'time'
 * @returns {string} Formatted date string
 */
export const formatDateTime = (date, format = 'full') => {
  const d = date ? new Date(date) : new Date();
  
  switch (format) {
    case 'full':
      return d.toLocaleDateString('en-US', GRAMMAR_RULES.dateTime.fullDateFormat);
    case 'short':
      return d.toLocaleDateString('en-US', GRAMMAR_RULES.dateTime.shortDateFormat);
    case 'datetime':
      return `${d.toLocaleDateString('en-US', GRAMMAR_RULES.dateTime.shortDateFormat)} at ${d.toLocaleTimeString('en-US', GRAMMAR_RULES.dateTime.timeFormat)}`;
    case 'time':
      return d.toLocaleTimeString('en-US', GRAMMAR_RULES.dateTime.timeFormat);
    case 'iso':
      return d.toISOString();
    default:
      return d.toLocaleDateString('en-US', GRAMMAR_RULES.dateTime.fullDateFormat);
  }
};

/**
 * Format a number according to American English rules
 * @param {number} num - Number to format
 * @param {string} context - 'prose', 'measurement', 'degree', 'orb'
 * @returns {string} Formatted number
 */
export const formatNumber = (num, context = 'prose') => {
  if (num === null || num === undefined) return '—';
  
  switch (context) {
    case 'prose':
      // Spell out 1-9 in prose
      if (Number.isInteger(num) && num >= 1 && num <= 9) {
        const words = ['one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine'];
        return words[num - 1];
      }
      return num.toString();
    case 'degree':
      return `${num.toFixed(GRAMMAR_RULES.numbers.positionPrecision)}°`;
    case 'orb':
      return `${num.toFixed(GRAMMAR_RULES.numbers.orbPrecision)}°`;
    case 'measurement':
    default:
      return num.toString();
  }
};

/**
 * Format a list with proper serial comma
 * @param {string[]} items - List items
 * @param {string} conjunction - 'and', 'or', etc.
 * @returns {string} Formatted list
 */
export const formatList = (items, conjunction = 'and') => {
  if (!items || items.length === 0) return '';
  if (items.length === 1) return items[0];
  if (items.length === 2) return `${items[0]} ${conjunction} ${items[1]}`;
  
  // Use serial comma (Oxford comma)
  const allButLast = items.slice(0, -1).join(', ');
  return `${allButLast}, ${conjunction} ${items[items.length - 1]}`;
};

/**
 * Apply capitalization rule to text
 * @param {string} text - Input text
 * @param {string} rule - 'uppercase', 'lowercase', 'titleCase', 'sentenceCase'
 * @returns {string} - Formatted text
 */
export const applyCapitalization = (text, rule) => {
  if (!text) return '';
  
  switch (rule) {
    case 'uppercase':
      return text.toUpperCase();
    case 'lowercase':
      return text.toLowerCase();
    case 'titleCase':
      return text.replace(/\w\S*/g, txt => 
        txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
      );
    case 'sentenceCase':
      return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
    default:
      return text;
  }
};

/**
 * Format a header according to brand rules (ALL CAPS)
 */
export const formatHeader = (text) => applyCapitalization(text, 'uppercase');

/**
 * Format a planet name according to brand rules (Title Case)
 */
export const formatPlanetName = (text) => applyCapitalization(text, 'titleCase');

/**
 * Format a zodiac sign name according to brand rules (Title Case).
 * Backend already emits signs in Title Case, but this ensures any lowercase
 * legacy or user input is normalized to the meta-standard everywhere.
 */
export const formatSignName = (text) => applyCapitalization(text, 'titleCase');

/**
 * Format an aspect name according to brand rules (Title Case).
 * Backend emits aspect keys lowercase ('conjunction', 'trine', …) for stable
 * lookups. Any user-facing surface MUST route through this formatter.
 */
export const formatAspectName = (text) => applyCapitalization(text, 'titleCase');

/**
 * Universal astrological-name formatter — routes to the correct Title-Case
 * formatter based on caller context. Safe for planets, signs, aspects,
 * modality names, station types, eclipse types, etc.
 *
 * All fields must be Title Case in every user-facing surface. Backend keys
 * remain lowercase for stable lookups; wrap them here at the JSX boundary.
 */
export const formatBodyName = (text) => applyCapitalization(text, 'titleCase');

/**
 * Format a phase name according to brand rules (ALL CAPS)
 */
export const formatPhaseName = (text) => applyCapitalization(text, 'uppercase');

/**
 * Format a theme according to brand rules (Title Case)
 * Used for language themes in CiH synthesis
 */
export const formatTheme = (text) => applyCapitalization(text, 'titleCase');

/**
 * Format a list item according to brand rules (Sentence case)
 * Used for supportive responses and other list items
 */
export const formatListItem = (text) => applyCapitalization(text, 'sentenceCase');

/**
 * Format an array of themes with proper capitalization
 * @param {string[]} themes - Array of theme strings
 * @returns {string[]} - Formatted themes
 */
export const formatThemes = (themes) => {
  if (!themes || !Array.isArray(themes)) return [];
  return themes.map(formatTheme);
};

/**
 * Format an array of list items with proper capitalization
 * @param {string[]} items - Array of list item strings
 * @returns {string[]} - Formatted list items
 */
export const formatListItems = (items) => {
  if (!items || !Array.isArray(items)) return [];
  return items.map(formatListItem);
};

// =============================================================================
// BORDERS & EFFECTS
// =============================================================================

export const BORDERS = {
  // Border widths
  thin: 1,
  medium: 2,
  thick: 3,
  
  // Border colors (with opacity)
  colors: {
    default: 'rgba(197, 160, 89, 0.3)',
    hover: 'rgba(197, 160, 89, 0.5)',
    active: 'rgba(197, 160, 89, 1)',
    purple: 'rgba(147, 112, 219, 0.5)',
    cyan: 'rgba(0, 240, 255, 0.5)'
  },
  
  // Border radius
  radius: {
    none: 0,
    sm: 4,
    md: 8,
    lg: 12,
    full: 9999
  }
};

export const EFFECTS = {
  // Backdrop blur
  blur: {
    sm: '4px',
    md: '8px',
    lg: '12px',
    xl: '24px'
  },
  
  // Shadows
  shadows: {
    glow: {
      brass: '0 0 10px rgba(197, 160, 89, 0.3)',
      gold: '0 0 10px rgba(255, 215, 0, 0.5)',
      purple: '0 0 10px rgba(147, 112, 219, 0.5)'
    }
  },
  
  // Transitions
  transitions: {
    fast: '150ms ease',
    normal: '300ms ease',
    slow: '500ms ease'
  }
};

// =============================================================================
// PDF-SPECIFIC STYLES
// =============================================================================

export const PDF_STYLES = {
  // Page setup
  page: {
    format: 'letter',
    orientation: 'portrait',
    unit: 'mm'
  },
  
  // Margins
  margins: {
    top: 15,
    right: 15,
    bottom: 15,
    left: 15
  },
  
  // Font sizes (pt for PDF)
  fontSizes: {
    title: 18,
    subtitle: 10,
    sectionHeader: 11,
    body: 8,
    small: 7,
    tiny: 6
  },
  
  // Line heights
  lineHeights: {
    title: 8,
    section: 6,
    body: 3.5,
    small: 3
  },
  
  // Colors (RGB arrays for jsPDF)
  colors: COLORS_RGB,
  
  // Section styling
  sections: {
    dividerWidth: 0.4,
    boxPadding: 2
  }
};

// =============================================================================
// COMPONENT PRESETS
// =============================================================================

export const PRESETS = {
  // Panel styles
  panel: {
    background: 'bg-[#0B0F19]/95',
    border: 'border border-[#C5A059]/30',
    backdrop: 'backdrop-blur-xl'
  },
  
  // Button variants
  button: {
    primary: {
      bg: 'bg-[#C5A059]',
      text: 'text-[#050814]',
      hover: 'hover:bg-[#E6C288]'
    },
    secondary: {
      bg: 'bg-transparent',
      border: 'border-2 border-[#C5A059]',
      text: 'text-[#C5A059]',
      hover: 'hover:bg-[#C5A059] hover:text-black'
    },
    ghost: {
      bg: 'bg-transparent',
      text: 'text-[#C5A059]/70',
      hover: 'hover:text-[#C5A059] hover:bg-[#C5A059]/10'
    }
  },
  
  // Phase badge styles
  phaseBadge: (phase) => ({
    backgroundColor: (COLORS.phases[phase] || COLORS.brass) + '30',
    color: COLORS.phases[phase] || COLORS.brass
  }),
  
  // Status indicator styles
  statusIndicator: (status) => ({
    color: COLORS.status[status] || COLORS.muted
  })
};

// =============================================================================
// BRAND IDENTITY (from Brand Identity Packet)
// All brand and CiH cosmology is META - single source of truth
// =============================================================================

export const BRAND_IDENTITY = {
  // Official naming conventions
  names: {
    product: 'THE ONESCENT DIAL',
    productShort: 'Onescent Dial',
    framework: 'Cycles in Harmony',
    frameworkAbbrev: 'CiH',
    frameworkTrademark: 'Cycles in Harmony™',
    company: 'Optimal Mastery LLC',
    family: 'Onesce',
    familyFull: 'Onesce family of applied wellness brands',
    spatioTemporal: 'Spacio-Temporal Analysis™'
  },
  
  // Ownership and proprietary notices
  ownership: {
    owner: 'Optimal Mastery LLC',
    ownerFull: 'Optimal Mastery LLC and the Onesce family of applied wellness brands',
    proprietaryNotice: 'PROPRIETARY NOTICE: THE ONESCENT DIAL',
    frameworkOwnership: 'Cycles in Harmony™ (CiH) is a proprietary framework',
    allRightsReserved: 'All rights reserved'
  },
  
  // CiH Phase Logic (proprietary framework definitions)
  cihPhases: {
    phases: ['NOURISH', 'ACTIVATE', 'FULFILL', 'INTEGRATE'],
    phaseDescriptions: {
      NOURISH: 'Inward, receptive, incubation',
      ACTIVATE: 'Outward, initiative, momentum',
      FULFILL: 'Peak expression, visibility, embodiment',
      INTEGRATE: 'Refinement, release, composting'
    },
    proprietaryNote: 'Phase logic is proprietary to Cycles in Harmony™'
  },
  
  // Section headers (use these exact names in PDFs/exports)
  headers: {
    cosmicClimate: 'COSMIC CLIMATE',
    dominantPhase: 'DOMINANT PHASE',
    phaseEmphasis: 'PHASE EMPHASIS',
    celestialPositions: 'CELESTIAL POSITIONS',
    activeAspects: 'ACTIVE ASPECTS',
    synthesis: 'CYCLES IN HARMONY™ SYNTHESIS',
    retrogrades: 'RETROGRADE STATUS',
    aspectDynamics: 'ASPECT DYNAMICS',
    orientationNote: 'ORIENTATION NOTE',
    fieldFlags: 'FIELD FLAGS',
    seasonalContext: 'SEASONAL CONTEXT',
    lunarContext: 'LUNAR CONTEXT',
    themes: 'THEMES',
    supportiveResponses: 'SUPPORTIVE RESPONSES'
  },
  
  // Copyright and legal
  legal: {
    copyright: '© Optimal Mastery LLC',
    fullCopyright: '© Optimal Mastery LLC • THE ONESCENT DIAL',
    trademark: '™',
    allRightsReserved: 'All rights reserved',
    proprietaryStatement: '© Optimal Mastery LLC and the Onesce family of applied wellness brands. Cycles in Harmony™ is a proprietary framework. All rights reserved.'
  },
  
  // Social and marketing
  social: {
    hashtags: '#onescentdial #cyclesinharmony',
    tagline: 'Cycles in Harmony™ • Orientation, Not Prediction',
    website: 'onescence.com'
  }
};

// =============================================================================
// UI COMPONENT STYLES (Boxes, Badges, Buttons)
// =============================================================================

export const UI_COMPONENTS = {
  // Info boxes (used in panels)
  infoBox: {
    base: 'p-3 rounded border',
    variants: {
      default: 'bg-[#0B0F19]/50 border-[#C5A059]/20',
      highlighted: 'bg-[#9370DB]/10 border-[#9370DB]/30',
      warning: 'bg-[#F5A623]/10 border-[#F5A623]/30',
      success: 'bg-[#7ED321]/10 border-[#7ED321]/30',
      phase: (phase) => `bg-[${COLORS.phases[phase]}]/10 border-[${COLORS.phases[phase]}]/30`
    }
  },
  
  // Badge styles
  badge: {
    base: 'px-2 py-1 rounded text-xs font-medium',
    variants: {
      phase: (phase) => ({
        backgroundColor: (COLORS.phases[phase] || COLORS.brass) + '30',
        color: COLORS.phases[phase] || COLORS.brass
      }),
      retrograde: {
        backgroundColor: COLORS.status.retrograde + '20',
        color: COLORS.status.retrograde
      },
      applying: {
        backgroundColor: COLORS.status.applying + '20',
        color: COLORS.status.applying
      },
      separating: {
        backgroundColor: COLORS.muted + '20',
        color: COLORS.muted
      }
    }
  },
  
  // Icon containers
  iconContainer: {
    base: 'p-2 rounded-lg flex items-center justify-center',
    sizes: {
      sm: 'w-8 h-8',
      md: 'w-10 h-10',
      lg: 'w-12 h-12'
    },
    getStyle: (color) => ({
      backgroundColor: color + '20'
    })
  },
  
  // Dividers
  divider: {
    horizontal: 'border-t border-[#C5A059]/30',
    vertical: 'border-l border-[#C5A059]/30',
    thick: 'border-t-2 border-[#C5A059]'
  },
  
  // Section headers
  sectionHeader: {
    base: 'text-xs uppercase tracking-wider',
    colors: {
      brass: 'text-[#C5A059]',
      gold: 'text-[#FFD700]',
      muted: 'text-[#94A3B8]',
      purple: 'text-[#9370DB]'
    }
  },
  
  // Synthesis/content boxes
  synthesisBox: {
    base: 'p-4 rounded border-l-4',
    getStyle: (phaseColor) => ({
      backgroundColor: '#0B0F19',
      borderLeftColor: phaseColor
    })
  },
  
  // Guidance/safeguard boxes
  safeguardBox: {
    base: 'p-3 rounded border',
    style: {
      backgroundColor: 'rgba(245, 166, 35, 0.1)',
      borderColor: 'rgba(245, 166, 35, 0.3)'
    }
  }
};

// =============================================================================
// PDF-SPECIFIC UI COMPONENTS
// =============================================================================

export const PDF_UI = {
  // Section box for PDF
  sectionBox: (pdf, x, y, width, height, borderColor = COLORS_RGB.brass) => {
    pdf.setDrawColor(...borderColor);
    pdf.setLineWidth(0.3);
    pdf.rect(x, y, width, height);
  },
  
  // Phase badge for PDF
  phaseBadge: (pdf, phase, x, y) => {
    const color = COLORS_RGB.phases[phase] || COLORS_RGB.brass;
    pdf.setFillColor(color[0], color[1], color[2], 0.3);
    pdf.roundedRect(x, y - 3, 25, 5, 1, 1, 'F');
    pdf.setTextColor(...color);
    pdf.setFontSize(7);
    pdf.text(phase, x + 2, y);
  },
  
  // Status indicator for PDF
  statusIndicator: (status) => {
    const indicators = {
      applying: { symbol: '→', color: COLORS_RGB.status.applying },
      separating: { symbol: '←', color: COLORS_RGB.grayText },
      exact: { symbol: '●', color: COLORS_RGB.gold },
      retrograde: { symbol: '℞', color: COLORS_RGB.status.retrograde }
    };
    return indicators[status] || indicators.separating;
  }
};

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Get phase color (hex)
 */
export const getPhaseColor = (phase) => COLORS.phases[phase] || COLORS.brass;

/**
 * Get phase color (RGB array for PDF)
 */
export const getPhaseColorRGB = (phase) => COLORS_RGB.phases[phase] || COLORS_RGB.brass;

/**
 * Get status color (hex)
 */
export const getStatusColor = (status) => COLORS.status[status] || COLORS.muted;

/**
 * Convert hex to RGB array
 */
export const hexToRGB = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? [
    parseInt(result[1], 16),
    parseInt(result[2], 16),
    parseInt(result[3], 16)
  ] : [0, 0, 0];
};

/**
 * Get contrasting text color for a background
 */
export const getContrastText = (bgHex) => {
  const rgb = hexToRGB(bgHex);
  const luminance = (0.299 * rgb[0] + 0.587 * rgb[1] + 0.114 * rgb[2]) / 255;
  return luminance > 0.5 ? COLORS.dark.darker : COLORS.text;
};

// =============================================================================
// GLOSSARY UI STANDARDS - When to Use ? vs Underline
// =============================================================================
// Standardized rules for glossary/FAQ integration across the application

export const GLOSSARY_UI_STANDARDS = {
  // The "?" icon (GlossaryIcon) - Opens tooltip with definition + FAQ links
  // USE: When you want to OFFER help without disrupting reading flow
  questionIcon: {
    usage: 'offerHelp',
    description: 'Provides contextual help access without breaking text flow',
    appearance: {
      icon: '?',
      size: 14,
      color: '#C5A059',
      hoverColor: '#FFD700',
      position: 'inline or floating',
      style: 'circled, subtle opacity until hover'
    },
    whenToUse: [
      'Next to section headers (Cosmic Climate, Phase Emphasis)',
      'Next to complex configuration options',
      'Near form labels that may need explanation',
      'At the start of new feature areas',
      'In tutorial steps for additional context'
    ],
    behavior: {
      onClick: 'opens tooltip',
      onHover: 'shows preview',
      tooltipContains: ['definition', 'aliases', 'related FAQs', 'link to full glossary']
    }
  },
  
  // Faint underlining (GlossaryTerm) - Inline clickable term
  // USE: When the term itself IS the content being read
  underlinedTerm: {
    usage: 'enrichContent',
    description: 'Enriches existing content without adding visual clutter',
    appearance: {
      style: 'dotted underline',
      color: '#C5A059',
      opacity: 0.4,
      hoverOpacity: 0.8,
      cursor: 'help'
    },
    whenToUse: [
      'Within body text where a CiH term appears naturally',
      'In synthesis paragraphs with key terms',
      'In panel headers where the term is the subject (e.g., "Phase Emphasis")',
      'In tutorial content explaining concepts',
      'In FAQ answers referencing other terms'
    ],
    behavior: {
      onClick: 'opens tooltip with definition',
      onHover: 'highlights term, shows preview',
      tooltipContains: ['definition', 'category badge', 'related terms', 'view in glossary link']
    }
  },
  
  // Quick Glossary (Auto-highlight) - For synthesis text
  // USE: In generated synthesis text where multiple terms may appear
  autoHighlight: {
    usage: 'synthesisEnrichment',
    description: 'Automatically highlights known CiH terms in generated content',
    appearance: {
      defaultState: 'normal text (no visual change)',
      onHover: 'subtle highlight + underline appears',
      detectedTermStyle: 'cursor changes to help on hover'
    },
    whenToUse: [
      'Synthesis paragraphs in Contextual Orientation panel',
      'Generated interpretations in planet/aspect overlays',
      'PDF export text (as clickable links if interactive)',
      'Comparative analysis synthesis'
    ],
    behavior: {
      mode: 'hover-reveal', // Only shows on hover to avoid visual clutter
      userToggle: true, // Can be disabled in profile settings
      settingLocation: 'Profile > Preferences > Auto-highlight CiH terms'
    }
  },
  
  // Decision matrix for developers
  decisionMatrix: {
    'Is this a section header?': {
      yes: 'Use GlossaryIcon (?) next to header',
      no: 'Continue to next question'
    },
    'Is the term inline within body text?': {
      yes: 'Use GlossaryTerm (underlined)',
      no: 'Continue to next question'
    },
    'Is this generated synthesis text?': {
      yes: 'Use QuickGlossary (auto-highlight on hover)',
      no: 'Continue to next question'
    },
    'Is this a form field label needing explanation?': {
      yes: 'Use GlossaryIcon (?) next to label',
      no: 'May not need glossary integration'
    }
  },
  
  // Z-index layering for tooltips
  zIndex: {
    glossaryIcon: 70,
    glossaryTerm: 70,
    glossaryPanel: 50,
    tooltip: 75
  }
};

// =============================================================================
// STAR SPACE & SEARCH HISTORY CONFIGURATION
// =============================================================================
// Configuration for search history storage and Star Space credits system

export const STAR_SPACE_CONFIG = {
  // Search History Settings
  searchHistory: {
    // Only available to authenticated users
    requiresAuth: true,
    guestAccess: false,
    
    // Free tier limits
    freeTier: {
      maxSearches: 50, // Per category
      categories: ['glossary', 'faq', 'dialState', 'location', 'time'],
      autoDeleteHours: { min: 24, max: 48, default: 36 }, // User configurable via slider
      storageType: 'session' // Session-based, not persisted
    },
    
    // Storage categories (user can organize)
    storageCategories: {
      recent: { label: 'Recent', maxItems: 50, autoDelete: true },
      temp: { label: 'Temporary', maxItems: 100, autoDelete: false },
      saved: { label: 'Saved', maxItems: 'unlimited', requiresStarSpace: true }
    },
    
    // What gets stored with each search
    searchDataShape: {
      query: 'string',
      type: 'glossary|faq|dial|location|time',
      timestamp: 'ISO8601',
      dialLink: 'shareUrl', // Link to reproduce the dial state
      results: 'count',
      category: 'storageCategory'
    }
  },
  
  // =============================================================================
  // UNIFIED CREDITS SYSTEM - Mirrored Pricing Tiers
  // Star Spaces and Save Tokens use identical tier structure for consistency
  // All add-ons EXTEND free-tier allocations (they don't replace them)
  // =============================================================================
  
  // Store branding
  storeBranding: {
    name: 'Onesce Store',
    tagline: 'Official Add-Ons & Memberships',
    url: 'https://onescepress.com/shop/dial/',
    // Full Onesce Insider Membership (handled on web store)
    insiderMembership: {
      name: 'Onesce Insider Membership',
      description: 'Full membership across all Onesce products',
      note: 'Handled on Onesce Store - not purchasable in-app',
      price: 'See Onesce Store for pricing'
    },
    // Dial-specific membership add-on (purchasable in-app)
    dialInsider: {
      name: 'The Onescent Dial: Insider Access',
      description: 'Dial-specific membership add-on; membership details available in the store.',
      clarification: 'Unlimited storage: use as many Profiles, Star Spaces, and Preset Tokens as you dare!',
      includes: ['Unlimited Profile Slots', 'Unlimited Star Spaces', 'Unlimited Preset Tokens', 'Priority Support', 'Early Feature Access'],
      price: '$79.99/year',
      savings: '55%'
    }
  },
  
  // Important: All purchased add-ons EXTEND free tier (accumulative, not replacement)
  addOnPolicy: {
    type: 'accumulative',
    description: 'All add-on purchases extend your existing free allocation. Your free tier is never replaced.',
    example: 'Free: 250 Star Spaces + Add-On: 500 Extra = Total: 750 Star Spaces'
  },
  
  // Unified pricing tiers (both Star Spaces and Save Tokens use these)
  pricingTiers: {
    tier1: { quantity: 100, price: '$2.99', priceValue: 2.99 },
    tier2: { quantity: 500, price: '$9.99', priceValue: 9.99, popular: true },
    tier3: { quantity: 1000, price: '$14.99', priceValue: 14.99 },
    tier4: { quantity: 'unlimited', price: '$24.99/year', priceValue: 24.99, subscription: true }
  },
  
  // Unified code pattern: TOD{type}_{quantity}
  codePattern: {
    prefix: 'TOD',
    types: {
      starSpace: 'space',
      saveToken: 'save',
      profileCredit: 'pro',
      combo: 'combo'
    },
    // Generated codes: TODspace_100, TODsave_500, TODpro_3, TODcombo_explorer
    generate: (type, quantity) => `TOD${type}_${quantity}`
  },
  
  // Star Space Credits System
  starSpace: {
    label: 'Extra Star Spaces',
    labelShort: 'Star Spaces',
    description: 'Additional storage for your Disc Views and Synthesis Saves!',
    icon: 'star',
    color: '#00F0FF',
    
    // Free allocation (always included)
    freeAllocation: {
      discViews: 10,
      synthesisSaves: 10,
      totalFree: 20,
      note: 'Free tier: 10 Disc Views + 10 Synthesis Saves'
    },
    
    // Paid add-on bundles
    paidBundles: [
      { 
        id: 'space_5', 
        name: '+5 Each (10 Total)',
        discViews: 5,
        synthesisSaves: 5,
        total: 10,
        price: '$4.99',
        code: 'TODspace_5',
        description: 'Add 5 extra Disc View slots + 5 extra Synthesis Save slots'
      },
      { 
        id: 'space_10', 
        name: '+10 Each (20 Total)',
        discViews: 10,
        synthesisSaves: 10,
        total: 20,
        price: '$7.99',
        code: 'TODspace_10',
        description: 'Add 10 extra Disc View slots + 10 extra Synthesis Save slots',
        popular: true
      },
      { 
        id: 'space_15', 
        name: '+15 Each (30 Total)',
        discViews: 15,
        synthesisSaves: 15,
        total: 30,
        price: '$12.99',
        code: 'TODspace_15',
        description: 'Add 15 extra Disc View slots + 15 extra Synthesis Save slots'
      }
    ]
  },
  
  // Preset Tokens System (for Times and Places)
  presetTokens: {
    label: 'Preset Tokens',
    labelShort: 'Presets',
    description: 'Additional storage for your own times and places!',
    icon: 'bookmark',
    color: '#9370DB',
    
    // Free allocation (always included)
    freeAllocation: {
      timePresets: 1,
      locationPresets: 1,
      totalFree: 2,
      note: 'Free tier: 1 Time Preset + 1 Location Preset'
    },
    
    // Paid add-on bundles
    paidBundles: [
      {
        id: 'preset_1',
        name: '+1 Each (2 Total)',
        timePresets: 1,
        locationPresets: 1,
        total: 2,
        price: '$4.99',
        code: 'TODpreset_1',
        description: 'Add 1 extra Time Preset + 1 extra Location Preset'
      },
      {
        id: 'preset_2',
        name: '+2 Each (4 Total)',
        timePresets: 2,
        locationPresets: 2,
        total: 4,
        price: '$7.99',
        code: 'TODpreset_2',
        description: 'Add 2 extra Time Presets + 2 extra Location Presets',
        popular: true
      },
      {
        id: 'preset_4',
        name: '+4 Each (8 Total)',
        timePresets: 4,
        locationPresets: 4,
        total: 8,
        price: '$12.99',
        code: 'TODpreset_4',
        description: 'Add 4 extra Time Presets + 4 extra Location Presets'
      }
    ]
  },
  
  // Notes storage configuration
  notes: {
    label: 'Notes',
    labelShort: 'Notes',
    description: 'Personal notes attached to your syntheses and dial views.',
    icon: 'file-text',
    color: '#E6C288',  // Tod-gold
    
    // Free tier allocation
    freeAllocation: {
      starred: 20,      // Base starred notes limit
      session: null,    // Unlimited session notes (auto-delete)
      week: 5,
      month: 5,
      year: 5
    },
    
    // Paid add-on bundles
    paidBundles: [
      {
        id: 'notes_10',
        name: '+10 Notes',
        notes: 10,
        price: '$4.99',
        code: 'TODnotes_10',
        description: 'Add 10 extra starred note slots'
      },
      {
        id: 'notes_20',
        name: '+20 Notes',
        notes: 20,
        price: '$7.99',
        code: 'TODnotes_20',
        description: 'Add 20 extra starred note slots',
        popular: true
      },
      {
        id: 'notes_30',
        name: '+30 Notes',
        notes: 30,
        price: '$9.99',
        code: 'TODnotes_30',
        description: 'Add 30 extra starred note slots'
      }
    ]
  },
  
  // Legacy Save Tokens alias (renamed to Preset Tokens)
  saveTokens: {
    label: 'Preset Tokens',
    labelShort: 'Presets',
    description: 'Additional storage for your own times and places!',
    icon: 'bookmark',
    color: '#9370DB',
    get freeAllocation() { return STAR_SPACE_CONFIG.presetTokens.freeAllocation; },
    get paidBundles() { return STAR_SPACE_CONFIG.presetTokens.paidBundles; }
  },
  
  // Legacy alias for backwards compatibility
  additionalSaves: {
    get description() { return STAR_SPACE_CONFIG.presetTokens.description; },
    get freeAllocation() { return STAR_SPACE_CONFIG.presetTokens.freeAllocation; },
    get paidBundles() { return STAR_SPACE_CONFIG.presetTokens.paidBundles; }
  },
  
  // Legacy Preset Add-Ons - now consolidated into presetTokens
  presetAddOns: {
    // Location Presets (now part of presetTokens)
    locationPresets: {
      label: 'Location Presets',
      description: 'Additional saved location presets (included in Preset Tokens)',
      icon: 'map-pin',
      color: '#9B59B6',
      freeAllocation: 1,
      note: 'See Preset Tokens for bundled pricing'
    },
    // Time Presets (now part of presetTokens)
    timePresets: {
      label: 'Time Presets',
      description: 'Additional saved date/time presets (included in Preset Tokens)',
      icon: 'clock',
      color: '#F5A623',
      freeAllocation: 1,
      note: 'See Preset Tokens for bundled pricing'
    }
  },
  
  // Combo bundles - Get Extra Profiles, Star Spaces, Preset Tokens, and Notes together for discounts!
  comboBundles: [
    {
      id: 'combo_starter',
      name: 'Starter Bundle',
      profileCredits: 3,
      starSpaces: 10,  // +5 disc views, +5 synthesis saves
      presetTokens: 2,  // +1 time, +1 location
      notes: 10,        // +10 starred notes
      price: '$14.99',
      code: 'TODcombo_starter',
      savings: '20%',
      tagline: 'Perfect for getting started'
    },
    {
      id: 'combo_explorer',
      name: 'Explorer Bundle',
      profileCredits: 5,
      starSpaces: 20,  // +10 disc views, +10 synthesis saves
      presetTokens: 4,  // +2 times, +2 locations
      notes: 20,        // +20 starred notes
      price: '$19.99',
      code: 'TODcombo_explorer',
      savings: '30%',
      popular: true,
      tagline: 'Most popular add-on bundle'
    },
    {
      id: 'combo_master',
      name: 'Master Add-On Bundle',
      profileCredits: 10,
      starSpaces: 30,  // +15 disc views, +15 synthesis saves
      presetTokens: 8,  // +4 times, +4 locations
      notes: 30,        // +30 starred notes
      price: '$29.99',
      code: 'TODcombo_master',
      savings: '40%',
      tagline: 'For serious practitioners'
    },
    {
      id: 'combo_ultimate',
      name: 'Insider Membership',
      profileCredits: 'unlimited',
      starSpaces: 'unlimited',
      presetTokens: 'unlimited',
      notes: 'unlimited',
      price: '$79.99/year',
      code: 'TODcombo_ultimate',
      savings: '55%',
      subscription: true,
      bestValue: true,
      insiderMembership: true,
      tagline: 'Unlimited storage + MAX presets for all classes & types',
      includes: ['Unlimited Profile Slots', 'Unlimited Star Spaces', 'Unlimited Notes', 'Unlimited Save Tokens', '+8 Location Presets (MAX)', '+8 Time Presets (MAX)', 'Priority Support', 'Early Feature Access']
    }
  ]
};

// =============================================================================
// PROMOTIONAL ASSET LIBRARY
// Meta-level system for internal ads, promotional content, and branded messaging
// Designed to be replicable for future external branded ad distribution
// =============================================================================

export const PROMOTIONAL_ASSETS = {
  // System Configuration
  config: {
    version: '1.0',
    rotationEnabled: true,
    rotationInterval: 30000, // 30 seconds between ad rotations
    trackImpressions: true,
    trackClicks: true,
    storageKey: 'tod_promo_state',
    maxDailyImpressions: 10 // Per ad slot
  },
  
  // Ad Placement Slots (where promotions can appear)
  slots: {
    profileManagerFooter: {
      id: 'pm_footer',
      name: 'Profile Manager Footer',
      location: 'ProfileManager panel bottom',
      size: 'compact', // compact | standard | featured
      allowedTypes: ['bundle', 'feature', 'upgrade'],
      priority: ['bundle', 'feature'],
      defaultAsset: 'explorer_bundle_promo'
    },
    dashboardBanner: {
      id: 'dash_banner',
      name: 'Dashboard Banner',
      location: 'Main dashboard top',
      size: 'standard',
      allowedTypes: ['announcement', 'feature', 'seasonal'],
      priority: ['announcement', 'seasonal'],
      defaultAsset: null
    },
    panelSidebar: {
      id: 'panel_sidebar',
      name: 'Panel Sidebar',
      location: 'Info panels sidebar',
      size: 'compact',
      allowedTypes: ['tip', 'feature', 'bundle'],
      priority: ['tip'],
      defaultAsset: null
    }
  },
  
  // Promotional Assets (the actual ad content)
  assets: {
    // === BUNDLE PROMOTIONS (Mid-tier focus) ===
    explorer_bundle_promo: {
      id: 'explorer_bundle_promo',
      type: 'bundle',
      tier: 'featured',
      active: true,
      
      // Content
      headline: 'Unlock More Cosmic Insights',
      subheadline: 'Explorer Bundle - Our Most Popular',
      description: '5 profiles, 500 star spaces, 500 save tokens',
      cta: 'Get Explorer Bundle',
      ctaAction: { type: 'navigate', target: 'credits_tab' },
      
      // Pricing Display
      pricing: {
        original: '$35.97',
        sale: '$19.99',
        savings: '30%',
        badge: 'POPULAR'
      },
      
      // Visual Config
      visual: {
        icon: 'sparkles',
        accentColor: '#FFD700',
        bgGradient: 'linear-gradient(135deg, rgba(255, 215, 0, 0.1), rgba(197, 160, 89, 0.05))',
        borderColor: 'rgba(255, 215, 0, 0.4)'
      },
      
      // Targeting
      targeting: {
        showToFreeUsers: true,
        showToStarterUsers: true,
        showToExplorerUsers: false,
        minProfileCount: 0,
        maxProfileCount: 3
      },
      
      // Analytics
      analytics: {
        campaignId: 'explorer_2026_q1',
        trackingCode: 'pm_footer_explorer'
      }
    },
    
    master_bundle_promo: {
      id: 'master_bundle_promo',
      type: 'bundle',
      tier: 'premium',
      active: true,
      
      headline: 'Level Up Your Practice',
      subheadline: 'Master Bundle - For Serious Practitioners',
      description: '10 profiles, 1000 star spaces, 1000 save tokens',
      cta: 'Go Master',
      ctaAction: { type: 'navigate', target: 'credits_tab' },
      
      pricing: {
        original: '$62.97',
        sale: '$34.99',
        savings: '40%',
        badge: 'BEST VALUE'
      },
      
      visual: {
        icon: 'crown',
        accentColor: '#9370DB',
        bgGradient: 'linear-gradient(135deg, rgba(147, 112, 219, 0.1), rgba(197, 160, 89, 0.05))',
        borderColor: 'rgba(147, 112, 219, 0.4)'
      },
      
      targeting: {
        showToFreeUsers: false,
        showToStarterUsers: true,
        showToExplorerUsers: true,
        minProfileCount: 3,
        maxProfileCount: null
      },
      
      analytics: {
        campaignId: 'master_2026_q1',
        trackingCode: 'pm_footer_master'
      }
    },
    
    starter_bundle_promo: {
      id: 'starter_bundle_promo',
      type: 'bundle',
      tier: 'entry',
      active: true,
      
      headline: 'Start Your Cosmic Journey',
      subheadline: 'Starter Bundle - Perfect Beginning',
      description: '3 profiles, 100 star spaces, 100 save tokens',
      cta: 'Get Started',
      ctaAction: { type: 'navigate', target: 'credits_tab' },
      
      pricing: {
        original: '$13.97',
        sale: '$7.99',
        savings: '20%',
        badge: 'STARTER'
      },
      
      visual: {
        icon: 'rocket',
        accentColor: '#7ED321',
        bgGradient: 'linear-gradient(135deg, rgba(126, 211, 33, 0.1), rgba(197, 160, 89, 0.05))',
        borderColor: 'rgba(126, 211, 33, 0.4)'
      },
      
      targeting: {
        showToFreeUsers: true,
        showToStarterUsers: false,
        showToExplorerUsers: false,
        minProfileCount: 0,
        maxProfileCount: 1
      },
      
      analytics: {
        campaignId: 'starter_2026_q1',
        trackingCode: 'pm_footer_starter'
      }
    },
    
    // === FEATURE PROMOTIONS ===
    save_tokens_promo: {
      id: 'save_tokens_promo',
      type: 'feature',
      tier: 'standard',
      active: true,
      
      headline: 'Never Lose an Insight',
      subheadline: 'Save Tokens - Permanent Storage',
      description: 'Keep your favorite dial states, analyses, and reports forever',
      cta: 'Get Save Tokens',
      ctaAction: { type: 'navigate', target: 'credits_tab' },
      
      visual: {
        icon: 'save',
        accentColor: '#9370DB',
        bgGradient: 'linear-gradient(135deg, rgba(147, 112, 219, 0.1), transparent)',
        borderColor: 'rgba(147, 112, 219, 0.3)'
      },
      
      targeting: {
        showToFreeUsers: true,
        showToStarterUsers: true,
        showToExplorerUsers: true,
        triggerCondition: 'lowSaveTokens' // Show when user has < 10 tokens
      },
      
      analytics: {
        campaignId: 'save_tokens_awareness',
        trackingCode: 'pm_footer_save'
      }
    },
    
    star_spaces_promo: {
      id: 'star_spaces_promo',
      type: 'feature',
      tier: 'standard',
      active: true,
      
      headline: 'Expand Your Cosmic Memory',
      subheadline: 'Star Spaces - Quick Access Storage',
      description: 'Save dial states and search history for instant recall',
      cta: 'Get Star Spaces',
      ctaAction: { type: 'navigate', target: 'credits_tab' },
      
      visual: {
        icon: 'star',
        accentColor: '#00F0FF',
        bgGradient: 'linear-gradient(135deg, rgba(0, 240, 255, 0.1), transparent)',
        borderColor: 'rgba(0, 240, 255, 0.3)'
      },
      
      targeting: {
        showToFreeUsers: true,
        showToStarterUsers: true,
        showToExplorerUsers: true,
        triggerCondition: 'lowStarSpaces'
      },
      
      analytics: {
        campaignId: 'star_spaces_awareness',
        trackingCode: 'pm_footer_star'
      }
    },
    
    // === UPGRADE PROMOTIONS ===
    ultimate_upgrade: {
      id: 'ultimate_upgrade',
      type: 'upgrade',
      tier: 'premium',
      active: true,
      
      headline: 'Go Unlimited',
      subheadline: 'Ultimate Annual - Complete Access',
      description: 'Unlimited everything: profiles, spaces, tokens. One price.',
      cta: 'Upgrade to Ultimate',
      ctaAction: { type: 'navigate', target: 'credits_tab' },
      
      pricing: {
        sale: '$79.99/year',
        savings: '55%',
        badge: 'UNLIMITED'
      },
      
      visual: {
        icon: 'infinity',
        accentColor: '#FFD700',
        bgGradient: 'linear-gradient(135deg, rgba(255, 215, 0, 0.15), rgba(147, 112, 219, 0.1))',
        borderColor: 'rgba(255, 215, 0, 0.5)'
      },
      
      targeting: {
        showToFreeUsers: false,
        showToStarterUsers: false,
        showToExplorerUsers: true,
        showToMasterUsers: true
      },
      
      analytics: {
        campaignId: 'ultimate_upgrade_2026',
        trackingCode: 'pm_footer_ultimate'
      }
    }
  },
  
  // Rotation Rules (which ads to show when)
  rotation: {
    // Default rotation for profile manager footer
    profileManagerFooter: {
      strategy: 'user_tier', // 'random' | 'sequential' | 'user_tier' | 'weighted'
      assets: [
        { id: 'explorer_bundle_promo', weight: 50 },
        { id: 'master_bundle_promo', weight: 25 },
        { id: 'starter_bundle_promo', weight: 15 },
        { id: 'ultimate_upgrade', weight: 10 }
      ],
      fallback: 'explorer_bundle_promo'
    }
  },
  
  // Helper function to get appropriate promo for user
  getPromoForUser: (slotId, userTier = 'free', userStats = {}) => {
    const slot = PROMOTIONAL_ASSETS.slots[slotId];
    if (!slot) return null;
    
    // Get rotation config for this slot
    const rotation = PROMOTIONAL_ASSETS.rotation[slotId];
    if (!rotation) return PROMOTIONAL_ASSETS.assets[slot.defaultAsset] || null;
    
    // Filter assets based on user tier and targeting
    const eligibleAssets = rotation.assets.filter(a => {
      const asset = PROMOTIONAL_ASSETS.assets[a.id];
      if (!asset || !asset.active) return false;
      
      const targeting = asset.targeting;
      if (userTier === 'free' && !targeting.showToFreeUsers) return false;
      if (userTier === 'starter' && !targeting.showToStarterUsers) return false;
      if (userTier === 'explorer' && !targeting.showToExplorerUsers) return false;
      if (userTier === 'master' && targeting.showToMasterUsers === false) return false;
      
      // Profile count targeting
      if (targeting.minProfileCount !== undefined && userStats.profileCount < targeting.minProfileCount) return false;
      if (targeting.maxProfileCount !== null && userStats.profileCount > targeting.maxProfileCount) return false;
      
      return true;
    });
    
    if (eligibleAssets.length === 0) {
      return PROMOTIONAL_ASSETS.assets[rotation.fallback] || null;
    }
    
    // Weighted random selection
    const totalWeight = eligibleAssets.reduce((sum, a) => sum + a.weight, 0);
    let random = Math.random() * totalWeight;
    
    for (const asset of eligibleAssets) {
      random -= asset.weight;
      if (random <= 0) {
        return PROMOTIONAL_ASSETS.assets[asset.id];
      }
    }
    
    return PROMOTIONAL_ASSETS.assets[eligibleAssets[0].id];
  }
};

// Continue with search history config
const SEARCH_HISTORY_CONFIG = {
  // Add to profile settings section
  settingsSection: 'Storage & History',
  
  // User-configurable options
  userSettings: {
    autoDeleteScope: {
      type: 'slider',
      min: 24,
      max: 48,
      default: 36,
      unit: 'hours',
      label: 'Auto-delete unsaved searches after'
    },
    autoHighlightTerms: {
      type: 'toggle',
      default: true,
      label: 'Auto-highlight CiH terms in synthesis'
    },
    searchHistoryEnabled: {
      type: 'toggle',
      default: true,
      label: 'Track search history'
    }
  },
  
  // Display format
  display: {
    showUsage: true, // "23/50 Glossary • 12/50 FAQ • 156/300 Star Spaces"
    showPurchasePrompt: true,
    lowSpaceWarning: 10 // Warn when < 10 spaces remaining
  }
};

// =============================================================================
// SEO & GEO META CONFIGURATION
// =============================================================================
// Configuration for SEO optimization and global positioning narrative

export const SEO_GEO_CONFIG = {
  // Core messaging narrative
  brandNarrative: {
    tagline: 'Global-Positioning Meets Celestial Timing',
    mission: 'THE ONESCENT DIAL brings together precise geographic awareness with celestial cycle intelligence to create the world\'s first truly location-aware orientation instrument.',
    uniqueValue: [
      'Real-time celestial positions adjusted for your exact location',
      'Hemisphere-aware seasonal interpretation',
      'Spacio-Temporal Analysis™ for comparing moments across the globe',
      'Non-predictive, agency-preserving cosmology'
    ]
  },
  
  // JSON-LD Structured Data
  structuredData: {
    // Organization schema
    organization: {
      '@type': 'Organization',
      name: 'Optimal Mastery LLC',
      brand: 'The Onescent Dial',
      description: 'Digital orientation instruments for cyclical awareness',
      url: 'https://dial.onescence.com'
    },
    
    // Software Application schema
    application: {
      '@type': 'SoftwareApplication',
      name: 'THE ONESCENT DIAL',
      applicationCategory: 'Lifestyle',
      operatingSystem: 'Web',
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'USD'
      }
    },
    
    // FAQ schema (dynamically generated from glossaryFAQ.js)
    faqPage: {
      '@type': 'FAQPage',
      mainEntity: [] // Populated from FAQ array
    },
    
    // Glossary schema (DefinedTermSet)
    glossary: {
      '@type': 'DefinedTermSet',
      name: 'Cycles in Harmony Glossary',
      description: 'Comprehensive terminology for the CiH orientation framework',
      hasDefinedTerm: [] // Populated from GLOSSARY
    }
  },
  
  // Dynamic meta tags based on dial state
  dynamicMeta: {
    // Base title format
    titleTemplate: '{pageTitle} | THE ONESCENT DIAL',
    
    // Page-specific titles
    pageTitles: {
      home: 'Celestial Orientation Instrument',
      dial: 'Live Celestial Positions for {location}',
      glossary: 'CiH Glossary - {termCount} Terms',
      faq: 'Frequently Asked Questions',
      profile: 'Profile Management'
    },
    
    // Open Graph tags
    openGraph: {
      type: 'website',
      siteName: 'THE ONESCENT DIAL',
      locale: 'en_US',
      image: '/og-image.png',
      imageAlt: 'The Onescent Dial - Celestial Orientation'
    },
    
    // Twitter Card
    twitterCard: {
      card: 'summary_large_image',
      site: '@onescence',
      creator: '@onescence'
    }
  },
  
  // Geo-targeting keywords by region
  geoKeywords: {
    global: [
      'celestial orientation',
      'astrology app',
      'moon phase tracker',
      'zodiac calculator',
      'planetary positions',
      'cosmic weather'
    ],
    regional: {
      northernHemisphere: ['winter solstice', 'summer solstice', 'temperate seasons'],
      southernHemisphere: ['seasonal inversion', 'southern hemisphere astrology'],
      equatorial: ['wet season', 'dry season', 'ITCZ', 'monsoon timing'],
      polar: ['midnight sun', 'polar night', 'arctic astrology']
    }
  },
  
  // Real-time data keywords
  realTimeKeywords: [
    'current moon phase',
    'mercury retrograde now',
    'today\'s planetary positions',
    'zodiac sign dates',
    'celestial events calendar'
  ]
};

// =============================================================================
// PANEL SIZING UTILITY
// Centralized function to get consistent panel sizing classes
// Usage: import { getPanelClasses } from '../utils/styleCard';
//        className={`${getPanelClasses('modal')} other-classes`}
// =============================================================================

export const PANEL_SIZING = {
  // Overlay panels (side panels, glossary, profile, etc.)
  overlay: 'w-[calc(100vw-16px)] sm:w-[420px] lg:w-[440px] max-w-[90vw] lg:max-w-[40vw]',
  
  // Compact panels (quick status, storage preview)
  compact: 'w-[calc(100vw-24px)] sm:w-[340px] lg:w-[360px] max-w-[320px] sm:max-w-[85vw] lg:max-w-[35vw]',
  
  // Full modals (date picker, export, auth, deep dive)
  modal: 'w-[calc(100vw-24px)] sm:w-[520px] lg:w-[560px] max-w-[90vw] lg:max-w-[60vw]',
  
  // Slide panels (CO/Aspects panel - full height)
  slidePanel: 'w-full sm:w-[420px] lg:w-[440px]'
};

/**
 * Get panel sizing classes for consistent responsive sizing
 * @param {string} type - 'overlay' | 'compact' | 'modal' | 'slidePanel'
 * @returns {string} Tailwind classes for panel sizing
 */
export const getPanelClasses = (type = 'modal') => {
  return PANEL_SIZING[type] || PANEL_SIZING.modal;
};

// =============================================================================
// BUTTON STYLING UTILITY
// Centralized button classes for consistent styling
// =============================================================================

export const BUTTON_STYLES = {
  // Base styles shared by all buttons - consistent single-pixel gold borders matching title container
  base: 'backdrop-blur-xl px-2 sm:px-4 py-1.5 sm:py-2 font-bold uppercase tracking-widest transition-all duration-300 flex items-center justify-start gap-2 text-xs sm:text-sm w-full',
  
  // Primary brass button (main CTA)
  primary: 'bg-[var(--tod-bg-secondary)]/90 border border-[var(--tod-brass)]/50 text-[var(--tod-brass)] hover:bg-[var(--tod-brass)] hover:text-black hover:border-[var(--tod-brass)]',
  
  // Secondary brass button (less prominent)
  secondary: 'bg-[var(--tod-bg-secondary)]/90 border border-[var(--tod-brass)]/40 text-[var(--tod-brass)] hover:bg-[var(--tod-brass)]/10 hover:border-[var(--tod-brass)]/60',
  
  // Muted brass (subtle actions)
  muted: 'bg-[var(--tod-bg-secondary)]/90 border border-[var(--tod-brass)]/30 text-[var(--tod-brass)]/70 hover:border-[var(--tod-brass)]/50 hover:text-[var(--tod-brass)]',
  
  // Purple accent (compare mode)
  purple: 'bg-[var(--tod-bg-secondary)]/90 border border-[var(--tod-brass)]/30 text-[var(--tod-lavender)] hover:bg-[var(--tod-purple)]/20 hover:border-[var(--tod-brass)]/50',
  purpleMuted: 'bg-[var(--tod-bg-secondary)]/90 border border-[var(--tod-brass)]/30 text-[var(--tod-purple)]/70 hover:border-[var(--tod-brass)]/50 hover:text-[var(--tod-lavender)]',
  
  // Green accent (save/storage)
  green: 'bg-[var(--tod-bg-secondary)]/90 border border-[var(--tod-brass)]/30 text-[var(--tod-green)] hover:border-[var(--tod-brass)]/50',
  greenMuted: 'bg-[var(--tod-bg-secondary)]/90 border border-[var(--tod-brass)]/30 text-[var(--tod-green)]/70 hover:border-[var(--tod-brass)]/50 hover:text-[var(--tod-green)]',
  greenDisabled: 'bg-[var(--tod-bg-secondary)]/90 border border-[var(--tod-brass)]/20 text-[var(--tod-green)]/40 hover:border-[var(--tod-brass)]/30',
  
  // Blue accent (share)
  blue: 'bg-[var(--tod-bg-secondary)]/90 border border-[var(--tod-brass)]/30 text-[var(--tod-info)]/70 hover:border-[var(--tod-brass)]/50 hover:text-[var(--tod-info)]',
  blueDisabled: 'bg-[var(--tod-bg-secondary)]/90 border border-[var(--tod-brass)]/20 text-[var(--tod-info)]/40 hover:border-[var(--tod-brass)]/30',
  
  // Cyan accent (tutorial/help)
  cyan: 'bg-[var(--tod-bg-secondary)]/90 border border-[var(--tod-brass)]/30 text-[var(--tod-cyan)] hover:border-[var(--tod-brass)]/50 hover:bg-[var(--tod-cyan)]/10'
};

/**
 * Get button classes for consistent styling
 * @param {string} variant - Button variant type
 * @returns {string} Tailwind classes for button
 */
export const getButtonClasses = (variant = 'primary') => {
  const base = BUTTON_STYLES.base;
  const variantClasses = BUTTON_STYLES[variant] || BUTTON_STYLES.primary;
  return `${base} ${variantClasses}`;
};

// =============================================================================
// MENU ITEM STYLING UTILITY
// =============================================================================

export const MENU_ITEM_STYLES = {
  base: 'w-full text-left px-3 py-2.5 text-sm flex items-center gap-3 cursor-pointer',
  default: 'text-[var(--tod-text-secondary)] hover:bg-[var(--tod-brass)]/10 hover:text-[var(--tod-text)]',
  cyan: 'text-[var(--tod-text-secondary)] hover:bg-[var(--tod-cyan)]/10 hover:text-[var(--tod-cyan)]',
  danger: 'text-[var(--tod-text-secondary)] hover:bg-[var(--tod-red)]/10 hover:text-[var(--tod-red)]',
  disabled: 'text-[var(--tod-text-secondary)]/60 hover:bg-[var(--tod-brass)]/5 cursor-not-allowed'
};

export const getMenuItemClasses = (variant = 'default') => {
  const base = MENU_ITEM_STYLES.base;
  const variantClasses = MENU_ITEM_STYLES[variant] || MENU_ITEM_STYLES.default;
  return `${base} ${variantClasses}`;
};

// ============================================
// POPULAR CITIES - Shared location presets
// Used by LocationController and ComparativeAnalysisSetup
// ============================================
export const POPULAR_CITIES = [
  // Northern Hemisphere - temperate
  { name: 'New York', state: 'NY', country: 'USA', lat: 40.7128, lon: -74.0060, isNorthern: true },
  { name: 'London', state: '', country: 'UK', lat: 51.5074, lon: -0.1278, isNorthern: true },
  { name: 'Tokyo', state: '', country: 'Japan', lat: 35.6762, lon: 139.6503, isNorthern: true },
  // Southern Hemisphere
  { name: 'Sydney', state: 'NSW', country: 'Australia', lat: -33.8688, lon: 151.2093, isSouthern: true },
  // Equatorial zone for testing equatorial behavior
  { name: 'Singapore', state: '', country: 'Singapore', lat: 1.3521, lon: 103.8198, isEquatorial: true },
  // Polar location for testing polar day/night cycles
  { name: 'Tromsø', state: '', country: 'Norway', lat: 69.6492, lon: 18.9553, isPolar: true },
];

// Helper to format city display name
export const formatCityName = (city) => {
  if (!city) return '';
  const parts = [city.name];
  if (city.state) parts.push(city.state);
  if (city.country) parts.push(city.country);
  return parts.join(', ');
};

// =============================================================================
// PRESET STANDARDS - Location & Time Preset UI Guidelines
// Defines the standard structure for preset sections across CA modes
// =============================================================================

export const PRESET_STANDARDS = {
  // Location Input Sections (for all CA modes with location selection)
  locationPresets: {
    structure: [
      'search_input',        // Always first - custom location search
      'use_current_location', // Link to use device location
      'user_location_presets', // User's saved location presets (authenticated only)
      'popular_locations'     // POPULAR_CITIES quick select
    ],
    displayRules: {
      userPresetsLabel: 'Your Location Presets',
      popularLocationsLabel: 'Quick Locations',
      maxUserPresets: 4,
      showUserPresetsCondition: 'isAuthenticated && savedLocationPresets.length > 0'
    }
  },
  
  // Time Input Sections (for observation moments, shared moments)
  timePresets: {
    structure: [
      'date_time_selector',   // ComparisonDateSelector component
      'upcoming_events',      // UpcomingEventsSection (moon phases, sabbats, eclipses)
      'user_time_presets'     // User's saved time presets (authenticated only)
    ],
    displayRules: {
      userPresetsLabel: 'Your Time Presets',
      upcomingEventsLabel: 'Upcoming Events',
      maxUserPresets: 4,
      showUserPresetsCondition: 'isAuthenticated && savedTimePresets.length > 0',
      // Note: "Upcoming Aspects" (showPresets in ComparisonDateSelector) is separate 
      // and should be disabled for modes where Upcoming Events section is shown
      disableUpcomingAspectsWhenEventsShown: true
    }
  },
  
  // CA Mode-specific configurations
  modeConfigurations: {
    spacioTemporal: {
      sharedMoment: 'timePresets',      // Uses time preset structure
      locationA: 'locationPresets',      // Uses location preset structure
      locationB: 'locationPresets'       // Uses location preset structure
    },
    globalView: {
      observationMoment: 'timePresets',  // Uses time preset structure
      primaryLocation: 'locationPresets', // Uses location preset structure
      referencePoints: 'locationPresets'  // Uses location preset structure (multi-select)
    },
    temporalView: {
      fixedLocation: 'locationPresets',  // Uses location preset structure
      timeComparisons: 'timePresets'     // Uses time preset structure (multi-select)
    },
    timeLapse: {
      location: 'locationPresets',       // Uses location preset structure
      timeRange: 'timePresets'           // Start/End dates with time preset structure
    },
    transitToNatal: {
      natalChart: null,                  // Profile selection (no presets)
      transitMoment: 'timePresets'       // Uses time preset structure
    },
    eventComparison: {
      eventA: 'timePresets',             // Uses time preset structure
      eventB: 'timePresets'              // Uses time preset structure
    }
  },
  
  // API endpoints for fetching user presets
  endpoints: {
    locationPresets: '/api/auth/location-presets',
    timePresets: '/api/auth/time-presets'  // Via timePresetService.js
  }
};

// =============================================================================
// DEFAULT EXPORT
// =============================================================================

const styleCard = {
  COLORS,
  COLORS_RGB,
  THEME_VARIANTS,
  generateThemeCSS,
  FONTS,
  SPACING,
  TEXT_FORMATTING,
  REALTIME_DATA_STANDARDS,
  GRAMMAR_RULES,
  OPERATIONAL_PREFERENCES,
  BORDERS,
  EFFECTS,
  PDF_STYLES,
  PRESETS,
  BRAND_IDENTITY,
  UI_COMPONENTS,
  PDF_UI,
  SYMBOLS,
  LOADING_STATES,
  TOOLTIPS,
  TOOLTIP_POSITIONS,
  GLOSSARY_UI_STANDARDS,
  STAR_SPACE_CONFIG,
  PROMOTIONAL_ASSETS,
  SEO_GEO_CONFIG,
  PANEL_SIZING,
  BUTTON_STYLES,
  MENU_ITEM_STYLES,
  getLoadingState,
  getTooltip,
  getPhaseColor,
  getPhaseColorRGB,
  getStatusColor,
  hexToRGB,
  getContrastText,
  applyCapitalization,
  formatHeader,
  formatPlanetName,
  formatPhaseName,
  formatTheme,
  formatListItem,
  formatThemes,
  formatListItems,
  formatDateTime,
  formatNumber,
  formatList,
  getPlanetSymbol,
  getPlanetName,
  getAspectSymbol,
  getZodiacSymbol,
  getStatusSymbol,
  getPanelClasses,
  getButtonClasses,
  getMenuItemClasses,
  POPULAR_CITIES,
  formatCityName,
  PRESET_STANDARDS
};

// ============================================
// GLYPH & SYMBOL REGISTRY
// Comprehensive reference for all symbols, glyphs, and emojis used in the app
// Categories are ordered alphabetically for easy navigation
// ============================================
export const GLYPH_REGISTRY = {
  // === ASPECTS (A) ===
  aspects: {
    category: 'Aspects',
    sortOrder: 1,
    description: 'Angular relationships between celestial bodies that create energetic connections',
    items: {
      conjunction: { glyph: '☌', name: 'Conjunction', angle: '0°', meaning: 'Fusion, intensification, new cycle' },
      sextile: { glyph: '⚹', name: 'Sextile', angle: '60°', meaning: 'Opportunity, cooperation, potential' },
      square: { glyph: '□', name: 'Square', angle: '90°', meaning: 'Challenge, friction, growth catalyst' },
      trine: { glyph: '△', name: 'Trine', angle: '120°', meaning: 'Harmony, flow, natural talent' },
      opposition: { glyph: '☍', name: 'Opposition', angle: '180°', meaning: 'Tension, awareness, integration needed' }
    }
  },

  // === CELESTIAL BODIES (C) ===
  celestialBodies: {
    category: 'Celestial Bodies',
    sortOrder: 2,
    description: 'Traditional astrological planet symbols representing archetypal energies',
    items: {
      sun: { glyph: '☉', name: 'Sun', meaning: 'Core self, vitality, conscious will', element: 'Fire' },
      moon: { glyph: '☽', name: 'Moon', meaning: 'Emotions, instincts, the subconscious', element: 'Water' },
      mercury: { glyph: '☿', name: 'Mercury', meaning: 'Communication, thought, adaptability', element: 'Air/Earth' },
      venus: { glyph: '♀', name: 'Venus', meaning: 'Love, beauty, values, attraction', element: 'Earth/Air' },
      mars: { glyph: '♂', name: 'Mars', meaning: 'Action, drive, assertion, courage', element: 'Fire' },
      jupiter: { glyph: '♃', name: 'Jupiter', meaning: 'Expansion, wisdom, abundance, growth', element: 'Fire' },
      saturn: { glyph: '♄', name: 'Saturn', meaning: 'Structure, discipline, responsibility, time', element: 'Earth' },
      uranus: { glyph: '♅', name: 'Uranus', meaning: 'Innovation, rebellion, sudden change', element: 'Air' },
      neptune: { glyph: '♆', name: 'Neptune', meaning: 'Dreams, intuition, spirituality, dissolution', element: 'Water' },
      pluto: { glyph: '♇', name: 'Pluto', meaning: 'Transformation, power, regeneration', element: 'Water' },
      northNode: { glyph: '☊', name: 'North Node', meaning: 'Soul direction, growth path, destiny', element: null },
      southNode: { glyph: '☋', name: 'South Node', meaning: 'Past patterns, comfort zone, release', element: null }
    }
  },

  // === CYCLES IN HARMONY PHASE SYSTEM (C) ===
  cihPhases: {
    category: 'Cycles in Harmony Phase System',
    sortOrder: 3,
    description: 'The Cycles in Harmony four-phase framework. Colors represent energetic qualities: Blue for receptive/cool energy, Green for growth/building energy, Orange for peak/warm energy, Purple for introspective/transformative energy.',
    colorExplanation: 'Phase colors follow natural energy flow: NOURISH (Blue) = cool, receptive, night energy; ACTIVATE (Green) = growth, spring, building; FULFILL (Orange) = warm, peak, harvest; INTEGRATE (Purple) = dusk, reflection, transformation.',
    items: {
      nourish: { 
        glyph: '◐', 
        altGlyph: '💙',
        color: '#3B82F6', 
        name: 'NOURISH', 
        meaning: 'Rest, receive, incubate, restore',
        colorMeaning: 'Blue represents cool, receptive, lunar energy — the quality of night, winter, and deep restoration',
        moonPhases: ['New Moon', 'Waxing Crescent']
      },
      activate: { 
        glyph: '◑', 
        altGlyph: '💚',
        color: '#22C55E', 
        name: 'ACTIVATE', 
        meaning: 'Initiate, build, push forward, create',
        colorMeaning: 'Green represents growth, spring energy, and forward momentum — nature pushing forth',
        moonPhases: ['First Quarter', 'Waxing Gibbous']
      },
      fulfill: { 
        glyph: '○', 
        altGlyph: '🧡',
        color: '#F97316', 
        name: 'FULFILL', 
        meaning: 'Harvest, celebrate, peak expression',
        colorMeaning: 'Orange represents warmth, peak solar energy, and harvest time — full expression',
        moonPhases: ['Full Moon', 'Waning Gibbous']
      },
      integrate: { 
        glyph: '◕', 
        altGlyph: '💜',
        color: '#A855F7', 
        name: 'INTEGRATE', 
        meaning: 'Reflect, release, process, prepare',
        colorMeaning: 'Purple represents twilight, transformation, and introspection — preparing for renewal',
        moonPhases: ['Last Quarter', 'Waning Crescent']
      }
    }
  },

  // === COSMIC EVENTS (C) ===
  cosmicEvents: {
    category: 'Cosmic Events',
    sortOrder: 4,
    description: 'Icons used in the cosmic events ticker to indicate significant celestial happenings',
    items: {
      sabbat: { glyph: '🌞', name: 'Sabbat', meaning: 'Seasonal marker, wheel of the year station' },
      eclipse: { glyph: '🌙', name: 'Eclipse', meaning: 'Solar or lunar eclipse, powerful alignment' },
      stellium: { glyph: '⚡', name: 'Stellium', meaning: '3+ planets concentrated in one sign' },
      retrograde: { glyph: '✨', name: 'Retrograde', meaning: 'Planet in apparent backward motion' },
      conjunction: { glyph: '⭐', name: 'Major Conjunction', meaning: 'Significant planetary alignment' },
      signChange: { glyph: '→', name: 'Ingress', meaning: 'Planet entering a new sign' }
    }
  },

  // === ELEMENTS (E) ===
  elements: {
    category: 'Elements',
    sortOrder: 5,
    description: 'The four classical elements and their astrological associations',
    items: {
      fire: { glyph: '🔥', name: 'Fire', signs: ['Aries', 'Leo', 'Sagittarius'], quality: 'Hot & Dry', meaning: 'Will, inspiration, action, transformation' },
      earth: { glyph: '🌍', name: 'Earth', signs: ['Taurus', 'Virgo', 'Capricorn'], quality: 'Cold & Dry', meaning: 'Matter, stability, practicality, form' },
      air: { glyph: '💨', name: 'Air', signs: ['Gemini', 'Libra', 'Aquarius'], quality: 'Hot & Wet', meaning: 'Thought, communication, connection, ideas' },
      water: { glyph: '💧', name: 'Water', signs: ['Cancer', 'Scorpio', 'Pisces'], quality: 'Cold & Wet', meaning: 'Emotion, intuition, flow, depth' }
    }
  },

  // === GEOGRAPHIC ZONES (G) ===
  geographicZones: {
    category: 'Geographic Zones',
    sortOrder: 6,
    description: 'Location indicators showing latitude zones with distinct seasonal and daylight characteristics',
    items: {
      northern: { 
        glyph: '🏔️', 
        name: 'Northern Hemisphere', 
        color: 'text-tod-accent-blue-light', 
        meaning: 'Temperate and boreal climate zones north of the equator',
        latitudeBounds: '0° to 66.5°N (Equator to Arctic Circle)',
        characteristics: 'Standard seasonal cycle, longest days in June, shortest in December'
      },
      southern: { 
        glyph: '🌊', 
        name: 'Southern Hemisphere', 
        color: 'text-tod-lavender', 
        meaning: 'Temperate zones south of the equator with reversed seasons',
        latitudeBounds: '0° to 66.5°S (Equator to Antarctic Circle)',
        characteristics: 'Reversed seasons from Northern Hemisphere, longest days in December'
      },
      equatorial: { 
        glyph: '🌴', 
        name: 'Equatorial Zone', 
        color: 'text-tod-gold', 
        meaning: 'Tropical zone near the equator with minimal seasonal variation',
        latitudeBounds: '23.5°S to 23.5°N (Tropic of Capricorn to Tropic of Cancer)',
        characteristics: 'Nearly equal day/night year-round, two wet seasons, no traditional winter/summer'
      },
      polar: { 
        glyph: '❄️', 
        name: 'Polar Zone', 
        color: 'text-tod-cyan', 
        meaning: 'Arctic/Antarctic regions with extreme seasonal day/night cycles',
        latitudeBounds: '66.5° to 90° (Arctic/Antarctic Circle to Poles)',
        characteristics: 'Midnight sun in summer, polar night in winter, extreme light variation'
      }
    }
  },

  // === INTERFACE ELEMENTS (I) ===
  interfaceElements: {
    category: 'Interface Elements',
    sortOrder: 7,
    description: 'Symbols used in app navigation and features',
    items: {
      cykl: { glyph: '🐉', name: 'CyKL', meaning: 'Cycles in Harmony Knowledge Legend — your guide' },
      synthesis: { glyph: '⚗️', name: 'Synthesis', meaning: 'Combined interpretation, contextual reading' },
      compare: { glyph: '⚖️', name: 'Compare', meaning: 'Comparative analysis mode' },
      save: { glyph: '📌', name: 'Save/Pin', meaning: 'Save synthesis to storage' },
      starred: { glyph: '⭐', name: 'Starred', meaning: 'Favorited or important item' },
      location: { glyph: '📍', name: 'Location', meaning: 'Geographic position context' },
      time: { glyph: '🕐', name: 'Time', meaning: 'Temporal context or time travel' },
      info: { glyph: 'ℹ️', name: 'Information', meaning: 'Additional details available' },
      glossary: { glyph: '📖', name: 'Glossary', meaning: 'Term definition available' }
    }
  },

  // === LUNAR PHASES (L) ===
  lunarPhases: {
    category: 'Lunar Phases',
    sortOrder: 8,
    description: 'Moon phase symbols mapped to CiH phases. Each lunar phase has a corresponding CiH phase that guides its energetic quality.',
    items: {
      newMoon: { glyph: '🌑', name: 'New Moon', cihPhase: 'NOURISH', cihColor: '#3B82F6', meaning: 'New beginnings, intention setting, rest' },
      waxingCrescent: { glyph: '🌒', name: 'Waxing Crescent', cihPhase: 'NOURISH', cihColor: '#3B82F6', meaning: 'Emergence, intention, first steps' },
      firstQuarter: { glyph: '🌓', name: 'First Quarter', cihPhase: 'ACTIVATE', cihColor: '#22C55E', meaning: 'Action, decisions, challenges' },
      waxingGibbous: { glyph: '🌔', name: 'Waxing Gibbous', cihPhase: 'ACTIVATE', cihColor: '#22C55E', meaning: 'Refinement, adjustment, building' },
      fullMoon: { glyph: '🌕', name: 'Full Moon', cihPhase: 'FULFILL', cihColor: '#F97316', meaning: 'Culmination, illumination, harvest' },
      waningGibbous: { glyph: '🌖', name: 'Waning Gibbous', cihPhase: 'FULFILL', cihColor: '#F97316', meaning: 'Gratitude, sharing, distribution' },
      lastQuarter: { glyph: '🌗', name: 'Last Quarter', cihPhase: 'INTEGRATE', cihColor: '#A855F7', meaning: 'Release, forgiveness, letting go' },
      waningCrescent: { glyph: '🌘', name: 'Waning Crescent', cihPhase: 'INTEGRATE', cihColor: '#A855F7', meaning: 'Surrender, rest, preparation' }
    }
  },

  // === STATUS INDICATORS (S) ===
  statusIndicators: {
    category: 'Status Indicators',
    sortOrder: 9,
    description: 'Symbols indicating planetary motion status and dignity',
    items: {
      retrograde: { glyph: '℞', name: 'Retrograde', meaning: 'Apparent backward motion, review, revision' },
      direct: { glyph: 'D', name: 'Direct', meaning: 'Forward motion, progress, external focus' },
      station: { glyph: 'S', name: 'Station', meaning: 'Planetary pause, intensified influence' },
      exalted: { glyph: '↑', name: 'Exalted', meaning: 'Planet in sign of strength' },
      detriment: { glyph: '↓', name: 'Detriment', meaning: 'Planet in challenging sign' }
    }
  },

  // === ZODIAC SIGNS (Z) ===
  zodiacSigns: {
    category: 'Zodiac Signs',
    sortOrder: 10,
    description: 'The twelve signs of the zodiac wheel, each with element and modality',
    items: {
      aries: { glyph: '♈', name: 'Aries', element: 'Fire', modality: 'Cardinal', meaning: 'Initiative, courage, new beginnings' },
      taurus: { glyph: '♉', name: 'Taurus', element: 'Earth', modality: 'Fixed', meaning: 'Stability, sensuality, persistence' },
      gemini: { glyph: '♊', name: 'Gemini', element: 'Air', modality: 'Mutable', meaning: 'Communication, curiosity, duality' },
      cancer: { glyph: '♋', name: 'Cancer', element: 'Water', modality: 'Cardinal', meaning: 'Nurturing, protection, emotion' },
      leo: { glyph: '♌', name: 'Leo', element: 'Fire', modality: 'Fixed', meaning: 'Creativity, pride, self-expression' },
      virgo: { glyph: '♍', name: 'Virgo', element: 'Earth', modality: 'Mutable', meaning: 'Analysis, service, refinement' },
      libra: { glyph: '♎', name: 'Libra', element: 'Air', modality: 'Cardinal', meaning: 'Balance, partnership, harmony' },
      scorpio: { glyph: '♏', name: 'Scorpio', element: 'Water', modality: 'Fixed', meaning: 'Intensity, transformation, depth' },
      sagittarius: { glyph: '♐', name: 'Sagittarius', element: 'Fire', modality: 'Mutable', meaning: 'Expansion, philosophy, adventure' },
      capricorn: { glyph: '♑', name: 'Capricorn', element: 'Earth', modality: 'Cardinal', meaning: 'Ambition, structure, mastery' },
      aquarius: { glyph: '♒', name: 'Aquarius', element: 'Air', modality: 'Fixed', meaning: 'Innovation, community, individuality' },
      pisces: { glyph: '♓', name: 'Pisces', element: 'Water', modality: 'Mutable', meaning: 'Intuition, compassion, transcendence' }
    }
  }
};

// Helper to get categories sorted alphabetically
export const getSortedCategories = () => {
  return Object.entries(GLYPH_REGISTRY)
    .sort((a, b) => (a[1].sortOrder || 99) - (b[1].sortOrder || 99));
};

// Helper function to get a glyph by category and key
export const getGlyph = (category, key) => {
  return GLYPH_REGISTRY[category]?.items?.[key]?.glyph || '';
};

// Helper function to get full glyph info
export const getGlyphInfo = (category, key) => {
  return GLYPH_REGISTRY[category]?.items?.[key] || null;
};

// Get all glyphs as a flat array for display
export const getAllGlyphs = () => {
  const allGlyphs = [];
  getSortedCategories().forEach(([categoryKey, category]) => {
    Object.entries(category.items).forEach(([itemKey, item]) => {
      allGlyphs.push({
        category: category.category,
        categoryKey,
        itemKey,
        ...item
      });
    });
  });
  return allGlyphs;
};

export default styleCard;
